import { createFileRoute, redirect } from "@tanstack/react-router";
import { WazeAlertsLayer, WazeIcon } from "@/components/waze-alerts-layer";
import { useWazeAlertsToggle } from "@/lib/waze-alerts";
import { Section, Field, TxtInput, InfoRow } from "@/components/vehicle-form-fields";
import { VehicleLogsPanel } from "@/components/vehicle-logs-panel";
import { RouteHistoryCenter } from "@/components/route-history-center";
import { StreetViewPanorama } from "@/components/street-view-panorama";
import { VehicleSendCommandDialog } from "@/components/vehicle-send-command-dialog";
import { GpsDriveSessionBanner } from "@/components/gpsdrive-session-banner";
import { useQuery, useQueryClient, QueryClientProvider, type QueryClient } from "@tanstack/react-query";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchVehicles, type Vehicle, type VehicleStatus } from "@/lib/api";
import { useMovingVehicles } from "@/hooks/use-moving-vehicles";
import { useVehicleClusters, clusterBubbleRadiusPx, type MapBoundsBox } from "@/hooks/use-vehicle-clusters";
import { useReverseGeocode } from "@/hooks/use-reverse-geocode";
import { useBlockingEnabled, usePermission } from "@/hooks/use-permission";
import { useAuth } from "@/contexts/auth-context";
import { SimulateAlertPanel } from "@/components/simulate-alert-panel";
import carIcon3d from "@/assets/vehicle-car-3d.png";
import motoIcon3d from "@/assets/vehicle-moto-3d.png";
import carTopdownUrl from "@/assets/car-topdown.png";
import { isoFrameFor, isoIconMapping, resolveIsoSheet, type IsoSheetMeta } from "@/lib/vehicle-icons/photo-registry";
import {
  resolveVehicleIcon,
  resolveVehicleModel,
  resolveMapVehicleColor,
  vehicleIconDataUri,
  vehicleRealIconUrl,
  DEFAULT_VEHICLE_COLOR,
  AUTO_PREVIEW_COLOR,
  STATUS_TINTS,
  isPolitrackerCompany,
  type VehicleColorKey,
  type VehicleModelKey,
} from "@/lib/vehicle-icons";
import { vehiclesService, readVehicleListCache } from "@/services/vehicles.service";
import { photoIconProps, usePhotoMarkers } from "@/hooks/use-photo-markers";
import { VehicleAnchorDialog } from "@/components/vehicle-anchor-dialog";
import { LocationShareSheet } from "@/components/location-share-sheet";
import { VehicleTrackerInfoDialog } from "@/components/vehicle-tracker-info-dialog";
import { VehicleBlockConfirmDialog } from "@/components/vehicle-block-confirm-dialog";
import { VehicleIconPicker } from "@/components/vehicle-icon-picker";
import { addExtra, setOverride } from "@/lib/vehicle-overrides";
import { mergeIconAttributes } from "@/lib/traccar-device-attributes";
import { isVehicleBlocked, isVehiclePowered, ignitionState, ignitionLabel } from "@/lib/vehicle-state";
import { ALERT_CATALOG } from "@/lib/alert-types";
import { API_URL, ApiError } from "@/lib/api/client";
import { toast } from "sonner";
import { logActivity } from "@/services/gpsdrive-activity-log.service";

import { WaitingBackend } from "@/components/waiting-backend";
import { createRoot } from "react-dom/client";
import { sortVehiclesByStatus } from "@/lib/vehicle-status-sort";
import { DECK_ON_TOP, resolveGoogleMapId } from "@/lib/google-maps-style";
import type { Alert as AlertT, AlertType } from "@/types";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Search,
  Gauge,
  Clock,
  Lock,
  Unlock,
  Crosshair,
  MoreHorizontal,
  Maximize2,
  Minimize2,
  Layers,
  Plus,
  Minus,
  X,
  MapPin,
  Signal,
  Power,
  Truck,
  CircleDot,
  Pencil,
  User,
  Hash,
  Radio,
  ArrowLeft,
  Bell,
  BellOff,
  History as HistoryIcon,
  Info,
  Route as RouteIcon,
  Fuel,
  Wrench,
  Phone,
  Calendar,
  Activity,
  BatteryLow,
  AlertTriangle,
  SignalZero,
  ShieldOff,
  ChevronDown,
  ChevronLeft,
  LocateFixed,
  Star,
  ExternalLink,
  Battery,
  Satellite,
  Moon,
  HelpCircle,
  Terminal,
  Send,
  Radar,
  Image as ImageIcon,
  Camera,
  LandPlot,
  Combine,
  Share2,
  Waypoints,
  Check,
  Zap,
  Eye,
  Tag,
  KeyRound,
  Target,
  Play,
  Pause,
  PowerOff,
  WifiOff,
  Anchor,
  Building2,
  Car,
  ChevronRight,
  Navigation,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
} from "@/components/ui/context-menu";
import { cn } from "@/lib/utils";
import {
  useLinkableUsers,
  useVehicleUsers,
  useSyncVehicleUsers,
  usePlans,
  useDeviceNotifications,
  useToggleDeviceNotification,
  useQuickToggleRules,
  useQuickToggleRule,
  useGeofences,
  useGeofenceDevices,
  useVehicleMotionSince,
  useVehicleMetadata,
  useUpdateVehicleMetadata,
  useVehicleImages,
  useVehicleResellerCompanyMap,
  useAlertRules,
  useCreateAlertRule,
  useUpdateAlertRule,
  useDeleteAlertRule,
  useMoveVehicleToTrash,
  prefetchVehicleAlerts,
  useAlertEventsPage,
  VEHICLE_ALERT_EVENTS_PAGE_SIZE,
  useVehicleAnchor,
  type VehicleAnchor,
  useGeofenceAlertRules,
  useIgnitionAlertSummary,
  ANCHOR_NAME_PREFIX,
  queryKeys,
  useBlockCommandSmsFallback,
  useQuickCreateTrackerProtocol,
  useVehicleSearchIndex,
} from "@/hooks/queries";
import { matchesVehicleSearch, vehicleSearchHaystack } from "@/lib/vehicle-search";
import { sendBlockCommandWithSmsFallback } from "@/lib/send-block-command-with-sms-fallback";
import { usePendingBlockCommand } from "@/lib/pending-block-commands";
import {
  type AlertType as AlertRuleType,
  type AlertRuleConfig,
  ALERT_TYPE_OPTIONS,
} from "@/services/alert-rules.service";
import { AlertLimitsEditor, isLimitEditable, limitSummary } from "@/components/alert-limits-editor";
import { ensurePushSubscription } from "@/lib/push-subscription";
import { vehicleMetadataService } from "@/services/vehicle-metadata.service";
import { resolveGpsDriveUrl, GpsDriveApiError } from "@/lib/api/gpsdrive-client";
import { buildMetadataPayload, toNullableNumber, toNullableString } from "@/lib/vehicle-cadastro-fields";
import { useTrackerProtocols } from "@/hooks/queries/platform";
import { PermissionLinkPicker } from "@/components/permission-link-picker";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { getCachedMaxspeed } from "@/lib/overpass";
import { parseWkt } from "@/lib/wkt";
import type { Geofence, RoutePoint } from "@/types";
import type { LinkableUser } from "@/services/gpsdrive-users.service";
import { Popover, PopoverTrigger, PopoverContent, PopoverAnchor } from "@/components/ui/popover";
import { SalesVehiclePicker, type SalesVehiclePick } from "@/components/sales-vehicle-picker";
import { AgendaVehiclePicker } from "@/components/agenda-vehicle-picker";
import type { AgendaManualVehicle } from "@/services/gpsdrive-agenda.service";
import { vehiclePermissionsService } from "@/services/vehicle-permissions.service";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
} from "@/components/ui/dropdown-menu";
import { VehicleActionsMenu, getVehicleActionItems, type VehicleActionHandlers } from "@/components/vehicle-actions-menu";
import { Slider } from "@/components/ui/slider";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { useMoveTracker } from "@/hooks/queries/stock";
import type { MovementAction } from "@/services/gpsdrive-stock.service";
import { SatelliteZoomGuard } from "@/components/satellite-zoom-guard";
import { MapTileRecovery } from "@/components/map-tile-recovery";
import { isRestrictedAccountType } from "@/lib/account-type";
import { LicensePlate } from "@/components/license-plate";
import { agoLabel, clockLabel, timeWithAgo, useNowTick } from "@/lib/time-ago";

export const Route = createFileRoute("/")({
  component: MapPage,
  // ?vehicle=<id> — deep link real usado pelo botão "Acessar rastreamento"
  // da tela Início (routes/inicio.tsx), pra abrir o Mapa já centralizado e
  // seguindo o veículo escolhido, sem precisar buscar de novo manualmente.
  validateSearch: (search: Record<string, unknown>): { vehicle?: string } => {
    const vehicle = typeof search.vehicle === "string" ? search.vehicle : undefined;
    return vehicle ? { vehicle } : {};
  },
  // Pedido 2026-09-08: "primeira tela que tem que abrir e a tela inicial e
  // não o mapa" — "/" continua sendo o Mapa de verdade (login.tsx,
  // routes/users.tsx e o deep link ?vehicle= acima navegam pra cá de
  // propósito), só que a PRIMEIRA vez que alguém chega aqui nesta aba
  // (sessionStorage ainda sem ENTERED_APP_SESSION_KEY, ver routes/
  // inicio.tsx) vira sinônimo de "acabei de abrir o app" e redireciona pro
  // Início antes de montar o MapPage — evita a query pesada de veículos
  // (com poll de 15s) rodar à toa só pra ser descartada no redirect. Depois
  // que o Início roda uma vez nesta aba (marca a chave), clicar em "Mapa"
  // no menu volta a funcionar normalmente — nunca redireciona de novo na
  // mesma aba.
  beforeLoad: ({ search }) => {
    if (search.vehicle) return;
    if (typeof window === "undefined") return; // SSR decide no cliente
    try {
      if (sessionStorage.getItem("gpsdrive.enteredApp")) return;
    } catch {
      return; // storage indisponível — nunca bloqueia o Mapa por causa disso
    }
    throw redirect({ to: "/inicio" });
  },
  head: () => ({ meta: [{ title: "Mapa — Wp Rastreamento" }] }),
});

/* ---------- helpers ---------- */

const effectiveStatus = (v: Vehicle): VehicleStatus | "blocked" =>
  isVehicleBlocked(v) ? "blocked" : v.status;
const statusLabel = (s: VehicleStatus) =>
  s === "online" ? "Online" : s === "idle" ? "Parado" : "Offline";

// Traduz o código de alarme mais recente reportado pelo rastreador (Traccar
// Position.attributes.alarm) para um rótulo em PT-BR.
const ALARM_LABELS: Record<string, string> = {
  lowBattery: "Bateria violada",
  powerCut: "Corte de energia",
  powerRestored: "Energia restaurada",
  powerOn: "Energia ligada",
  powerOff: "Energia desligada",
  tamper: "Violação detectada",
  removing: "Rastreador removido",
  sos: "Botão de pânico",
  overspeed: "Excesso de velocidade",
  fallDown: "Queda detectada",
  vibration: "Vibração detectada",
  shock: "Impacto detectado",
  geofenceEnter: "Entrada em geocerca",
  geofenceExit: "Saída de geocerca",
  fault: "Falha no dispositivo",
  door: "Porta aberta",
  jamming: "Bloqueio de sinal (jamming)",
};
const alarmLabel = (code?: string) => (code ? ALARM_LABELS[code] ?? code : undefined);

function detectVehicleType(v: Vehicle): "car" | "moto" | "truck" | "van" | "machine" {
  if (v.type) return v.type;
  const n = v.name.toLowerCase();
  if (/moto|scooter|honda cg|xre|biz|pop|factor|fazer|cb ?\d/.test(n)) return "moto";
  if (/caminh(ã|a)o|truck|volvo|scania|iveco|actros|axor|constellation|fh\b|vm\b/.test(n)) return "truck";
  if (/van|sprinter|ducato|master|daily|transit|hr\b|jumper/.test(n)) return "van";
  if (/m(á|a)quina|trator|escavadeira|retro|p(á|a) carreg|bulldozer|colhe|jcb|cat\b/.test(n)) return "machine";
  return "car";
}

function relativeTime(iso: string) {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "agora";
  if (diff < 3600) return `${Math.floor(diff / 60)} min atrás`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h atrás`;
  return `${Math.floor(diff / 86400)} d atrás`;
}

// Texto da etiqueta de status ("Online • há 2 min") — "agora"/plural certo,
// igual ao exemplo pedido ("há 1 dia" / "há 3 dias").
function sinceLabel(iso: string): string {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "agora";
  if (diff < 3600) return `há ${Math.floor(diff / 60)} min`;
  if (diff < 86400) {
    const h = Math.floor(diff / 3600);
    return `há ${h} ${h === 1 ? "hora" : "horas"}`;
  }
  const d = Math.floor(diff / 86400);
  return `há ${d} ${d === 1 ? "dia" : "dias"}`;
}

// Tempo mostrado dentro da etiqueta de status. Online/Offline usam
// lastConnection (dado real já existente). Parado usa o evento real mais
// recente de deviceStopped do Traccar (useVehicleMotionSince) — a última
// posição não serve, porque continua sendo atualizada mesmo parado. Sem
// evento encontrado, não inventa nada: volta null e a etiqueta só mostra o
// status, sem tempo.

function useVehicleStatusSince(v: Vehicle): string | null {
  const { data: motionSince } = useVehicleMotionSince(v.id, v.status === "idle");
  if (v.status !== "idle") return sinceLabel(v.lastConnection);
  if (motionSince?.type === "deviceStopped") return sinceLabel(motionSince.since);
  return null;
}

/* ---------- quick filters ---------- */

type FilterKey =
  | "online"
  | "offline"
  | "idle"
  | "blocked"
  | "unblocked"
  | "ignitionOn"
  | "ignitionOff"
  | "moving"
  | "noGps"
  | "alerts"
  | "lowBattery";

function matchesFilter(v: Vehicle, f: FilterKey): boolean {
  switch (f) {
    case "online": return v.status === "online";
    case "offline": return v.status === "offline";
    case "idle": return v.status === "idle";
    case "blocked": return !!v.blocked;
    case "unblocked": return v.blocked === false;
    case "ignitionOn": return !!v.ignition;
    case "ignitionOff": return v.ignition === false;
    case "moving": return !!v.moving;
    case "noGps": return v.gpsSignal === false;
    case "alerts": return !!v.hasAlerts;
    case "lowBattery": return !!v.batteryLow;
  }
}

const filterDefs: { key: FilterKey; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { key: "online",       label: "Online",           icon: CircleDot },
  { key: "offline",      label: "Offline",          icon: Power },
  { key: "idle",         label: "Parado",           icon: Clock },
  { key: "moving",       label: "Em Movimento",     icon: Activity },
  { key: "blocked",      label: "Bloqueado",        icon: Lock },
  { key: "unblocked",    label: "Desbloqueado",     icon: Unlock },
  { key: "ignitionOn",   label: "Ignição Ligada",   icon: Power },
  { key: "ignitionOff",  label: "Ignição Desl.",    icon: Power },
  { key: "noGps",        label: "Sem Sinal GPS",    icon: SignalZero },
  { key: "alerts",       label: "Com Alertas",      icon: AlertTriangle },
  { key: "lowBattery",   label: "Bateria Baixa",    icon: BatteryLow },
];

/* ---------- page ---------- */

function MapPage() {
  const [newVehicleOpen, setNewVehicleOpen] = useState(false);
  // Bug real 2026-09-01: botão "Novo" (Cadastrar veículo) abria pra
  // qualquer usuário logado, sem checar devices.create — ver mesmo
  // incidente em vehicle-actions-menu.tsx (Editar/Enviar comando).
  const canCreateDevices = usePermission("devices", "create");
  // Pedido 2026-09-01: ao contrário de Usuários/Configurações, o item
  // "Mapa" no menu NUNCA some (ver app-sidebar.tsx) — sem
  // devices.view, a tela continua com a busca/mapa/sidebar normais, só
  // que sem nenhum veículo (nunca a tela de bloqueio "Sem permissão"
  // cheia, como em Geocerca/Relatórios/Usuários/Configurações).
  const canViewDevices = usePermission("devices", "view");
  const {
    data: rawVehicles,
    isLoading,
    error,
    dataUpdatedAt,
  } = useQuery({
    queryKey: ["vehicles"],
    queryFn: fetchVehicles,
    refetchInterval: 15_000,
    retry: false,
    // Mesmo motivo/pedido do useVehicles() em hooks/queries/index.ts (F5 não
    // deve mostrar "carregando"/"0 veículos") — MESMA queryKey (["vehicles"]),
    // então nasce com a mesma frota conhecida salva por listFromTraccar.
    initialData: () => readVehicleListCache(),
    initialDataUpdatedAt: 0,
  });
  const vehicles = canViewDevices ? rawVehicles : [];
  // Bug relatado 2026-09-04: "cor muda sozinha depois de alguns segundos".
  // O snapshot acima pinta a frota instantaneamente com o status salvo no
  // último F5/poll (pode ter minutos), inclusive bloqueado/movendo/offline
  // — cores derivadas desse status (ver colors.ts#resolveAutoColor) então
  // "pulam" pra cor certa assim que o fetch real chega. dataUpdatedAt fica
  // em 0 enquanto os dados exibidos ainda são só o snapshot (initialDataUpdatedAt:
  // 0, acima); vira o timestamp real assim que o primeiro fetch de verdade
  // resolve. Usado só pra decidir a COR do ícone (ver iconUrlFor) — nunca
  // pra esconder posição/nome, que continuam instantâneos do snapshot.
  const vehiclesColorReady = dataUpdatedAt > 0;
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [focusTick, setFocusTick] = useState(0);
  const [editVehicle, setEditVehicle] = useState<Vehicle | null>(null);
  const [detailMode, setDetailMode] = useState(false);
  const [filter, setFilter] = useState<FilterKey | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  // Seta do painel Frota (pedido 2026-10-30): recolhe e deixa só o mapa.
  // Abrir um veículo traz o painel de volta, é onde ficam os detalhes.
  const [fleetPanelClosed, setFleetPanelClosed] = useState(false);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;

  const [historyVehicleId, setHistoryVehicleId] = useState<string | null>(null);
  const [followId, setFollowId] = useState<string | null>(null);
  const [commandVehicle, setCommandVehicle] = useState<Vehicle | null>(null);
  const [trails, setTrails] = useState<Record<string, { points: RoutePoint[] } & TrailStyle>>({});
  // Rastro é SEMPRE em tempo real (pedido 2026-08-28: sem escolha de janela
  // de tempo tipo "30 min/1h" — "a rota é em tempo real") — ativar não busca
  // nenhum histórico, só liga o acúmulo ao vivo a partir da posição atual; o
  // efeito abaixo alimenta o array a cada novo fix que chegar (mesmo poll de
  // 15s do useQuery de vehicles acima), só pra quem tem rastro ativo.
  // MAX_TRAIL_POINTS evita crescimento sem limite numa sessão longa (~2h num
  // poll de 15s).
  const MAX_TRAIL_POINTS = 480;
  const handleShowTrail = (vehicleId: string, style: TrailStyle) => {
    const v = vehicles?.find((x) => x.id === vehicleId);
    const seed: RoutePoint[] = v
      ? [
          {
            lat: v.lat,
            lng: v.lng,
            timestamp: v.positionTimestamp ?? new Date().toISOString(),
            speed: v.speed,
            bearing: v.bearing,
          },
        ]
      : [];
    setTrails((prev) => ({ ...prev, [vehicleId]: { points: seed, ...style } }));
  };
  const handleHideTrail = (vehicleId: string) =>
    setTrails((prev) => {
      const next = { ...prev };
      delete next[vehicleId];
      return next;
    });
  useEffect(() => {
    if (!vehicles) return;
    setTrails((prev) => {
      if (Object.keys(prev).length === 0) return prev;
      let changed = false;
      const next = { ...prev };
      for (const v of vehicles) {
        const trail = prev[v.id];
        if (!trail) continue;
        const last = trail.points[trail.points.length - 1];
        const fixTime = v.positionTimestamp ?? null;
        if (last && fixTime && last.timestamp === fixTime) continue; // mesmo fix, nada novo
        const point: RoutePoint = {
          lat: v.lat,
          lng: v.lng,
          timestamp: fixTime ?? new Date().toISOString(),
          speed: v.speed,
          bearing: v.bearing,
        };
        next[v.id] = { ...trail, points: [...trail.points, point].slice(-MAX_TRAIL_POINTS) };
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [vehicles]);

  // isLoading (não isFetching): fica true só antes do PRIMEIRO sucesso da
  // query — evita mostrar "0 veículos" por 1-2s a cada F5/login antes do
  // fetch inicial responder (bug relatado 2026-09-04: "tela inicial mostra
  // 0 veículo, demora uns segundos e aparece os dados corretos"). Refetch
  // de fundo (poll 15s, foco de aba) nunca reativa isLoading, então os
  // cards não "piscam" de novo depois do primeiro carregamento.
  const stats = useMemo((): {
    total: number | null;
    online: number | null;
    idle: number | null;
    offline: number | null;
    blocked: number | null;
    moving: number | null;
  } => {
    if (isLoading) {
      return { total: null, online: null, idle: null, offline: null, blocked: null, moving: null };
    }
    const list = vehicles ?? [];
    return {
      total: list.length,
      online: list.filter((v) => v.status === "online").length,
      idle: list.filter((v) => v.status === "idle").length,
      offline: list.filter((v) => v.status === "offline").length,
      blocked: list.filter((v) => v.blocked).length,
      moving: list.filter((v) => v.moving).length,
    };
  }, [vehicles, isLoading]);

  // Busca por placa/IMEI/linha/ICCID/chassi/cliente (2026-10-01, ver
  // lib/vehicle-search.ts) — mesma da tela Veículos. Índice de cliente/linha
  // só pra equipe; cliente busca pelo que o Traccar já traz.
  const { gpsDriveUser, gpsDriveStatus } = useAuth();
  const { data: searchIndex } = useVehicleSearchIndex(
    gpsDriveStatus !== "loading" && !!gpsDriveUser && !isRestrictedAccountType(gpsDriveUser.accountType),
  );
  const searching = q.trim() !== "";
  const haystacks = useMemo(
    () =>
      searching
        ? new Map((vehicles ?? []).map((v) => [v.id, vehicleSearchHaystack(v, searchIndex?.[v.id])] as const))
        : null,
    // `vehicles` muda a cada poll de 15s; só recalcula enquanto há busca.
    [vehicles, searchIndex, searching],
  );
  const filtered = useMemo(() => {
    if (!vehicles) return [];
    let list = vehicles;
    if (haystacks) {
      list = list.filter((v) => {
        const h = haystacks.get(v.id);
        return !!h && matchesVehicleSearch(h, q);
      });
    }
    if (filter) {
      list = list.filter((v) => matchesFilter(v, filter));
    }
    // Online → Parado → Offline, ANTES de paginar (vale pra todas as
    // páginas); refaz a cada poll de 15s, então quem muda de status muda
    // de posição sozinho.
    return sortVehiclesByStatus(list);
  }, [vehicles, q, filter, haystacks]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  useEffect(() => { setPage(1); }, [q, filter, filtered.length]);
  const currentPage = Math.min(page, totalPages);
  const pageItems = useMemo(
    () => filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [filtered, currentPage],
  );

  // Fotos reais da frota, só de quem está na página atual (pageItems, 20 no
  // máximo) — nunca da frota inteira. Antes pedia por TODOS os veículos
  // carregados de uma vez (`vehicles ?? []`), construindo uma URL com
  // ?deviceIds=id1,id2,...,id5000 — contra uma frota de 5000 isso estourava
  // o limite de tamanho de URL do Nginx e voltava 414 (Request-URI Too
  // Large) sempre, pra frota inteira, em toda renderização da lista — nunca
  // carregava foto nenhuma, e ainda gastava tempo montando/mandando essa
  // URL gigante à toa. A lista já é paginada (PAGE_SIZE=20); só faz sentido
  // pedir foto de quem está de fato na tela. Achado no teste de carga de
  // 5000 veículos, 2026-09-02.
  const pageVehicleIds = useMemo(() => pageItems.map((v) => v.id), [pageItems]);
  // gpsDriveReady evita a mesma corrida de boot já corrigida em
  // routes/users.tsx e routes/vehicles.tsx (2026-09-03): sem isso, a busca
  // de fotos disparava assim que a frota do Traccar chegava, sem esperar o
  // access token do GPS Drive Backend existir, tomava 401 e só reaparecia
  // certa depois do retry — "aparece cache antigo e atualiza em segundos".
  // (gpsDriveStatus vem do useAuth() da busca, acima.)
  const { data: vehicleImages } = useVehicleImages(pageVehicleIds, gpsDriveStatus !== "loading");
  // Etiqueta "Revenda: <nome>" (pedido 2026-09-07) — vazio pra qualquer
  // empresa sem filhas (a imensa maioria), mesmo gate de gpsDriveStatus que
  // vehicleImages acima pro mesmo motivo (evita 401 na corrida de boot).
  const { data: resellerCompanyMap } = useVehicleResellerCompanyMap(
    pageVehicleIds,
    gpsDriveStatus !== "loading",
  );

  const toggleFilter = (k: FilterKey) => {
    setFilter((prev) => (prev === k ? null : k));
  };
  const clearFilter = () => setFilter(null);

  const selected = (vehicles ?? []).find((v) => v.id === selectedId) ?? null;

  // Levantado de VehicleDetailSidebar pra cá (pedido 2026-09-04: "quando
  // ativar a âncora tem que desenhar o perímetro igual a geocerca") — o
  // mapa (MapView) e o painel de detalhe precisam do MESMO resultado desta
  // query pra nunca divergir (um desenhando o círculo, outro dizendo que
  // não há âncora), e useVehicleAnchor já é cacheado por deviceId via
  // react-query, então levantar não duplica chamada nenhuma.
  const { anchor: selectedAnchor } = useVehicleAnchor(selected ? Number(selected.id) : undefined);

  // Ícone de âncora na LISTA da frota inteira (pedido 2026-09-04) —
  // diferente de selectedAnchor acima, não dá pra usar useVehicleAnchor por
  // linha (N+1 query numa frota de milhares, mesmo erro de performance já
  // corrido antes aqui — ver Overpass/sticky-seed nas notas do projeto).
  // Reaproveita a MESMA busca em lote que a página Cercas Virtuais já usa
  // pro badge "Com bloqueio" (uma chamada só, todas as regras de geocerca
  // da empresa) e filtra pelo prefixo fixo do nome da âncora.
  const { data: geofenceAlertRules } = useGeofenceAlertRules();
  const anchoredDeviceIds = useMemo(() => {
    const ids = new Set<number>();
    for (const r of geofenceAlertRules ?? []) {
      if (r.name?.startsWith(ANCHOR_NAME_PREFIX)) ids.add(r.traccarDeviceId);
    }
    return ids;
  }, [geofenceAlertRules]);

  const centerOn = (id: string) => {
    setSelectedId(id);
    setFocusTick((t) => t + 1);
  };

  const showIgnitionAlerts = isPolitrackerCompany();
  const { data: ignitionAlertRules } = useIgnitionAlertSummary(showIgnitionAlerts);
  const ignitionAlertTypes = useMemo(() => {
    const byDevice = new Map<string, Set<string>>();
    for (const r of ignitionAlertRules ?? []) {
      const key = String(r.traccarDeviceId);
      byDevice.set(key, (byDevice.get(key) ?? new Set()).add(r.type));
    }
    return byDevice;
  }, [ignitionAlertRules]);
  const ignitionAlertsFor = (id: string) => ({
    on: !!ignitionAlertTypes.get(id)?.has("ignition_on"),
    off: !!ignitionAlertTypes.get(id)?.has("ignition_off"),
  });

  const openDetail = (id: string) => {
    setFleetPanelClosed(false);
    setSelectedId(id);
    setDetailMode(true);
    setFocusTick((t) => t + 1);
    // Clicar no veículo (card da lista ou marcador do mapa) já entra em modo
    // "seguir" — sem isso o mapa centralizava uma vez só e o usuário tinha
    // que ficar arrastando manualmente atrás do veículo em movimento.
    setFollowId(id);
  };

  // Aplica o ?vehicle=<id> (ver validateSearch acima) uma única vez, assim
  // que a lista de veículos carregar — antes disso `vehicles` está vazio e
  // não dá pra confirmar que o id realmente existe.
  const { vehicle: deepLinkVehicleId } = Route.useSearch();
  const deepLinkAppliedRef = useRef(false);
  useEffect(() => {
    if (deepLinkAppliedRef.current || !deepLinkVehicleId) return;
    if (!vehicles?.some((v) => v.id === deepLinkVehicleId)) return;
    deepLinkAppliedRef.current = true;
    openDetail(deepLinkVehicleId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkVehicleId, vehicles]);

  const closeDetail = () => {
    setDetailMode(false);
    setSelectedId(null);
  };

  const openHistory = (id: string) => {
    setHistoryVehicleId(id);
    setDetailMode(false);
    setFollowId(null);
  };

  const toggleFollow = (id: string) => {
    setFollowId((cur) => (cur === id ? null : id));
    setSelectedId(id);
  };

  // Só entra no mapa quem já reportou pelo menos uma posição GPS real —
  // dispositivos recém-cadastrados sem posição ainda ficam de fora dos
  // marcadores (mas continuam aparecendo nos cards e na lista da frota).
  // useMemo (não um filter direto): sem isso, todo render de MapPage por
  // QUALQUER motivo (digitar na busca, trocar de página, abrir um popup)
  // criava um array novo mesmo com o mesmo conteúdo, o que descia como
  // identidade nova de `vehicles` pra dentro de MapView — cascata
  // desnecessária de recomputações (activeVehicleIds, visibleVehicles etc.)
  // toda vez. Ver comentário em VEHICLES_PER_BATCH dentro de MapView.
  const allMapVehicles = useMemo(() => (vehicles ?? []).filter((v) => v.hasPosition !== false), [vehicles]);
  // Com o painel de detalhes aberto (veículo buscado/clicado), o mapa mostra
  // SÓ esse veículo (pedido 2026-09-26) — o resto da frota volta ao sair do
  // detalhe (closeDetail / abrir histórico).
  const focusedMapVehicleId = detailMode ? selectedId : null;
  const mapVehicles = useMemo(
    () =>
      focusedMapVehicleId
        ? allMapVehicles.filter((v) => v.id === focusedMapVehicleId)
        : allMapVehicles,
    [allMapVehicles, focusedMapVehicleId],
  );

  return (
    <div className="relative flex h-screen w-full overflow-hidden">
      {/* Fleet panel — floats over the map */}
      <button
        type="button"
        onClick={() => setFleetPanelClosed((c) => !c)}
        aria-label={fleetPanelClosed ? "Abrir painel da frota" : "Fechar painel da frota"}
        title={fleetPanelClosed ? "Abrir painel da frota" : "Fechar painel da frota"}
        className={cn(
          "absolute top-1/2 z-[1001] grid h-12 w-7 -translate-y-1/2 place-items-center rounded-r-xl border border-l-0 border-border bg-card text-muted-foreground shadow-md transition-[left] duration-200 hover:text-foreground",
          fleetPanelClosed ? "left-0" : "left-[472px]",
        )}
      >
        <ChevronLeft className={cn("h-5 w-5 transition-transform", fleetPanelClosed && "rotate-180")} />
      </button>
      <aside
        aria-hidden={fleetPanelClosed}
        className={cn(
          "absolute left-3 top-3 bottom-3 z-[1000] flex w-[460px] shrink-0 flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-[0_10px_30px_-10px_rgb(0_0_0/0.45)] transition-transform duration-200",
          fleetPanelClosed && "-translate-x-[calc(100%+1rem)]",
        )}
      >
        {detailMode && selected ? (
          <VehicleDetailSidebar
            v={selected}
            anchor={selectedAnchor}
            onBack={closeDetail}
            onCenter={() => centerOn(selected.id)}
            onEdit={() => setEditVehicle(selected)}
          />
        ) : (
          <>
            <div className="border-b border-border px-5 py-4">
              <div className="flex items-center justify-between gap-2">
                <h1 className="text-base font-semibold tracking-tight">Frota</h1>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] uppercase tracking-widest text-muted-foreground">
                    Tempo real
                  </span>
                  <button
                    type="button"
                    onClick={() => setNewVehicleOpen(true)}
                    disabled={!canCreateDevices}
                    aria-label="Adicionar dispositivo"
                    title={canCreateDevices ? "Adicionar dispositivo" : "Sem permissão"}
                    className="group relative inline-flex h-8 items-center gap-1.5 overflow-hidden rounded-full border border-emerald-500/40 bg-gradient-to-br from-emerald-500 to-emerald-600 px-3 text-[12px] font-bold text-white shadow-[0_6px_20px_-6px_rgba(16,185,129,0.7)] transition-all hover:-translate-y-0.5 hover:shadow-[0_10px_24px_-6px_rgba(16,185,129,0.9)] active:translate-y-0 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0"
                  >
                    <span aria-hidden className="text-base leading-none transition-transform group-hover:rotate-90">➕</span>
                    <span>Novo</span>
                    <span aria-hidden className="text-sm leading-none">📡</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Stat cards */}
            <div className="grid grid-cols-4 gap-2 border-b border-border px-3 py-3">
              <StatCard label="Total" value={stats.total} tone="neutral" />
              <StatCard label="Online" value={stats.online} tone="online" />
              <StatCard label="Parado" value={stats.idle} tone="idle" />
              <StatCard label="Offline" value={stats.offline} tone="offline" />
            </div>

            {/* Quick filters (collapsible) */}
            <div className="border-b border-border px-3 py-2">
              <button
                type="button"
                onClick={() => setFiltersOpen((o) => !o)}
                className="flex w-full items-center justify-between rounded-md px-1 py-1.5 text-left hover:bg-accent/50"
              >
                <span className="flex items-center gap-2">
                  <span className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
                    Aplicar filtros
                  </span>
                  {filter && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-foreground px-2 py-0.5 text-[10px] font-medium text-background">
                      {filterDefs.find((f) => f.key === filter)?.label}
                    </span>
                  )}
                </span>
                <ChevronDown
                  className={cn(
                    "h-4 w-4 text-muted-foreground transition-transform",
                    filtersOpen && "rotate-180",
                  )}
                />
              </button>
              {filtersOpen && (
                <div className="mt-2">
                  <div className="mb-2 flex items-center justify-end">
                    {filter && (
                      <button
                        onClick={clearFilter}
                        className="text-[10px] font-medium text-muted-foreground hover:text-foreground"
                      >
                        Limpar
                      </button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {filterDefs.map((f) => (
                      <FilterChip
                        key={f.key}
                        label={f.label}
                        icon={f.icon}
                        active={filter === f.key}
                        onClick={() => toggleFilter(f.key)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Search */}
            <div className="border-b border-border p-3">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Placa, IMEI, linha, cliente..."
                  className="h-9 border-border bg-background pl-8"
                />
              </div>
            </div>

            {/* List */}
            <div className="flex flex-1 min-h-0 flex-col">
              <div className="flex-1 overflow-y-auto">
                {isLoading || error || !vehicles ? (
                  <div className="p-4">
                    <WaitingBackend error={error} />
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="p-6 text-center text-sm text-muted-foreground">
                    Nenhum veículo encontrado.
                  </div>
                ) : (
                  <ul className="flex flex-col gap-3 bg-muted/45 px-3 py-4">
                    {pageItems.map((v) => (
                      <VehicleRow
                        key={v.id}
                        v={v}
                        imageUrls={vehicleImages?.[v.id]}
                        active={selectedId === v.id}
                        hasAnchor={anchoredDeviceIds.has(Number(v.id))}
                        resellerCompanyName={resellerCompanyMap?.[v.id]?.companyName}
                        ignitionAlerts={showIgnitionAlerts ? ignitionAlertsFor(v.id) : undefined}
                        following={followId === v.id}
                        onSelect={() => openDetail(v.id)}
                        onCommand={() => setCommandVehicle(v)}
                        onShowHistory={() => openHistory(v.id)}
                        onFollow={() => toggleFollow(v.id)}
                        onEdit={() => setEditVehicle(v)}
                        hasTrail={!!trails[v.id]}
                        onShowTrail={(style) => handleShowTrail(v.id, style)}
                        onHideTrail={() => handleHideTrail(v.id)}
                      />
                    ))}
                  </ul>
                )}
              </div>
              {filtered.length > PAGE_SIZE && (
                <Pagination
                  page={currentPage}
                  totalPages={totalPages}
                  total={filtered.length}
                  pageSize={PAGE_SIZE}
                  onChange={setPage}
                />
              )}
            </div>
          </>
        )}
      </aside>

      {/* Map area */}
      <div className="relative flex-1">
        {isLoading || error || !vehicles ? (
          <div className="flex h-full items-center justify-center p-6">
            <WaitingBackend error={error} />
          </div>
        ) : (
          <MapView
            vehicles={mapVehicles}
            vehiclesColorReady={vehiclesColorReady}
            focusId={selectedId}
            focusTick={focusTick}
            anchorGeofence={selectedAnchor?.geofence}
            onSelect={openDetail}
            followId={followId}
            onStopFollow={() => setFollowId(null)}
            trails={trails}
            onShowTrail={handleShowTrail}
            onHideTrail={handleHideTrail}
            onCommand={(v) => setCommandVehicle(v)}
            onShowHistory={(id) => openHistory(id)}
            onFollow={(id) => toggleFollow(id)}
            onEdit={(v) => setEditVehicle(v)}
          />
        )}
      </div>

      <RouteHistoryCenter
        vehicleId={historyVehicleId}
        vehicles={vehicles ?? []}
        onClose={() => setHistoryVehicleId(null)}
      />

      <VehicleEditDialog
        vehicle={editVehicle}
        onOpenChange={(open) => !open && setEditVehicle(null)}
      />

      <VehicleFormDialog
        open={newVehicleOpen}
        onOpenChange={setNewVehicleOpen}
        editing={null}
      />

      <VehicleSendCommandDialog
        vehicle={commandVehicle}
        open={!!commandVehicle}
        onOpenChange={(open) => !open && setCommandVehicle(null)}
      />
    </div>
  );
}

/* ---------- fleet UI ---------- */

function StatCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: number | null;
  tone: "neutral" | "online" | "idle" | "offline";
}) {
  const tones = {
    neutral: {
      dot: "bg-slate-400",
      ring: "border-border",
      bg: "bg-gradient-to-br from-background to-muted/40",
      value: "text-foreground",
      glow: "shadow-[inset_0_1px_0_rgb(255_255_255/0.04)]",
    },
    online: {
      dot: "bg-emerald-500 shadow-[0_0_0_3px_rgb(16_185_129/0.18)]",
      ring: "border-emerald-500/30",
      bg: "bg-gradient-to-br from-emerald-500/10 to-emerald-500/[0.03]",
      value: "text-emerald-600 dark:text-emerald-400",
      glow: "shadow-[0_4px_16px_-8px_rgb(16_185_129/0.5)]",
    },
    idle: {
      dot: "bg-amber-500 shadow-[0_0_0_3px_rgb(245_158_11/0.18)]",
      ring: "border-amber-500/30",
      bg: "bg-gradient-to-br from-amber-500/10 to-amber-500/[0.03]",
      value: "text-amber-600 dark:text-amber-400",
      glow: "shadow-[0_4px_16px_-8px_rgb(245_158_11/0.5)]",
    },
    offline: isPolitrackerCompany()
      ? {
          // Desligado preto, igual ao ícone (Politracker, 2026-10-31).
          dot: "bg-black ring-1 ring-white/50",
          ring: "border-slate-900/40 dark:border-white/30",
          bg: "bg-gradient-to-br from-slate-900/15 to-slate-900/[0.04] dark:from-white/10 dark:to-white/[0.03]",
          value: "text-slate-950 dark:text-white",
          glow: "shadow-[0_4px_16px_-8px_rgb(0_0_0/0.5)]",
        }
      : {
          dot: "bg-red-500 shadow-[0_0_0_3px_rgb(239_68_68/0.18)]",
          ring: "border-red-500/30",
          bg: "bg-gradient-to-br from-red-500/10 to-red-500/[0.03]",
          value: "text-red-600 dark:text-red-400",
          glow: "shadow-[0_4px_16px_-8px_rgb(239_68_68/0.5)]",
        },
  }[tone];
  return (
    <div className={cn("relative overflow-hidden rounded-xl border px-2.5 py-2.5 transition-all hover:-translate-y-0.5", tones.ring, tones.bg, tones.glow)}>
      <div className="flex items-center gap-1.5">
        <span className={cn("h-1.5 w-1.5 rounded-full", tones.dot)} />
        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </span>
      </div>
      <div className={cn("mt-1.5 text-2xl font-bold tabular-nums leading-none tracking-tight", tones.value)}>
        {value === null ? <span className="inline-block h-5 w-5 animate-pulse rounded bg-current opacity-20" /> : value}
      </div>
    </div>
  );
}

function Pagination({
  page,
  totalPages,
  total,
  pageSize,
  onChange,
}: {
  page: number;
  totalPages: number;
  total: number;
  pageSize: number;
  onChange: (p: number) => void;
}) {
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  // Build page number list with ellipsis: 1 … p-1 p p+1 … N
  const nums: (number | "…")[] = [];
  const push = (n: number | "…") => {
    if (nums[nums.length - 1] !== n) nums.push(n);
  };
  const window = 1;
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || (i >= page - window && i <= page + window)) {
      push(i);
    } else if (i === 2 || i === totalPages - 1) {
      push("…");
    }
  }

  return (
    <div className="flex flex-col gap-2 border-t border-border bg-background/60 px-3 py-2.5">
      <div className="flex items-center justify-between text-[11px] text-muted-foreground">
        <span>
          {from}–{to} de <span className="font-semibold text-foreground">{total}</span>
        </span>
        <span>
          Página {page} de {totalPages}
        </span>
      </div>
      <div className="flex flex-wrap items-center justify-center gap-1">
        <button
          type="button"
          onClick={() => onChange(Math.max(1, page - 1))}
          disabled={page <= 1}
          className="inline-flex h-7 items-center rounded-md border border-border bg-background px-2 text-[11px] font-medium transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-40"
        >
          ‹
        </button>
        {nums.map((n, i) =>
          n === "…" ? (
            <span key={`e${i}`} className="px-1 text-xs text-muted-foreground">
              …
            </span>
          ) : (
            <button
              key={n}
              type="button"
              onClick={() => onChange(n)}
              className={cn(
                "inline-flex h-7 min-w-7 items-center justify-center rounded-md border px-2 text-[11px] font-semibold transition-colors",
                n === page
                  ? "border-primary bg-primary text-primary-foreground shadow-[0_2px_8px_-2px_rgba(16,185,129,0.5)]"
                  : "border-border bg-background text-foreground hover:bg-accent",
              )}
            >
              {n}
            </button>
          ),
        )}
        <button
          type="button"
          onClick={() => onChange(Math.min(totalPages, page + 1))}
          disabled={page >= totalPages}
          className="inline-flex h-7 items-center rounded-md border border-border bg-background px-2 text-[11px] font-medium transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-40"
        >
          ›
        </button>
      </div>
    </div>
  );
}

function IgnitionAlertTag({ label, on }: { label: string; on: boolean }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-1.5 py-0.5",
        on ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400" : "bg-muted text-muted-foreground",
      )}
      title={`${label}: ${on ? "ativo" : "desativado"}`}
    >
      {on ? <Bell className="h-2.5 w-2.5" /> : <BellOff className="h-2.5 w-2.5" />}
      {label}: {on ? "ativo" : "off"}
    </span>
  );
}

function VehicleRow({
  v,
  imageUrls,
  active,
  hasAnchor,
  ignitionAlerts,
  resellerCompanyName,
  following,
  onSelect,
  onCommand,
  onShowHistory,
  onFollow,
  onEdit,
  hasTrail,
  onShowTrail,
  onHideTrail,
}: {
  v: Vehicle;
  imageUrls?: import("@/types/vehicle-metadata").VehicleImageUrls;
  active: boolean;
  hasAnchor: boolean;
  // Só Politracker (2026-10-31): alerta de ignição ligado neste veículo?
  ignitionAlerts?: { on: boolean; off: boolean };
  resellerCompanyName?: string;
  following: boolean;
  onSelect: () => void;
  onCommand: () => void;
  onShowHistory: () => void;
  onFollow: () => void;
  onEdit: () => void;
  hasTrail: boolean;
  onShowTrail: (style: TrailStyle) => void;
  onHideTrail: () => void;
}) {
  const [trailPanelOpen, setTrailPanelOpen] = useState(false);
  const canEditDevices = usePermission("devices", "edit");
  const canSendCommand = usePermission("commands", "send_command");
  const icon = resolveVehicleIcon(v);
  // Foto real cadastrada tem prioridade sobre o ícone genérico — variante
  // "128" é suficiente para o tamanho do card (44px), nunca "original".
  // Sem foto (a maioria dos casos hoje), cai no ícone de sempre — nunca
  // deixa o card sem imagem nenhuma.
  const photoUrl = imageUrls ? resolveGpsDriveUrl(imageUrls["128"] ?? imageUrls.original) : undefined;
  // Se a foto real falhar ao carregar (ex.: URL antiga em cache apontando
  // pra um arquivo já substituído/apagado, ver invalidação de
  // ["vehicle-images"] em onPickImage), cai pro ícone do veículo em vez de
  // deixar o navegador mostrar o ícone de imagem quebrada. Reseta sempre
  // que a própria photoUrl muda (ex.: query revalidou com a chave nova) —
  // nunca reseta sozinho via onError de novo, então não há loop.
  const [photoFailed, setPhotoFailed] = useState(false);
  useEffect(() => setPhotoFailed(false), [photoUrl]);
  const queryClient = useQueryClient();
  const photoInputRef = useRef<HTMLInputElement>(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const handlePickPhoto = async (file?: File) => {
    if (!file) return;
    setPhotoUploading(true);
    try {
      await vehicleMetadataService.uploadImage(v.id, file);
      // Mesmo motivo do invalidate em onPickImage (VehicleFormPanel): a
      // foto antiga é apagada do storage assim que a nova é gravada, então
      // sem invalidar aqui o card continuaria servindo a URL antiga em
      // cache (agora um 404 real).
      await queryClient.invalidateQueries({ queryKey: ["vehicle-images"] });
      await queryClient.invalidateQueries({ queryKey: queryKeys.vehicleMetadata(v.id) });
    } catch (err) {
      toast.error("Não foi possível trocar a foto do veículo", {
        description: describeImageUploadError(err),
      });
    } finally {
      setPhotoUploading(false);
    }
  };
  const odo = typeof v.odometer === "number" ? v.odometer : null;
  const since = useVehicleStatusSince(v);
  // Cor do indicador de status no avatar: prioriza o que exige atenção
  // imediata (bloqueado > offline > sem GPS) antes de refletir ignição —
  // mesma paleta de STATUS_TINTS (única fonte de verdade em colors.ts) para
  // nunca divergir do resto do app, mas com prioridade própria: aqui
  // "ignição ligada" pesa mais que "em movimento", ao contrário do modo de
  // cor automática do ícone do veículo (resolveAutoColor), que usa `moving`.
  const blocked = isVehicleBlocked(v);
  const ignitionOn = ignitionState(v) === "on";
  const ringTint = blocked
    ? STATUS_TINTS.bloqueado
    : v.status === "offline"
      ? STATUS_TINTS.offline
      : v.gpsSignal === false
        ? STATUS_TINTS.semGps
        : ignitionOn
          ? STATUS_TINTS.ligado
          : STATUS_TINTS.parado;
  // Fonte única das ações (mesma lista usada no popup do mapa via
  // VehicleActionsMenu) — o botão direito (ContextMenu) reaproveita os
  // mesmos itens, só trocando o componente de item pro do ContextMenu.
  const actionHandlers: VehicleActionHandlers = {
    onCommand,
    onShowHistory,
    onFollow,
    onShowTrailPanel: () => setTrailPanelOpen(true),
    onEdit,
    following,
    canEdit: canEditDevices,
    canSendCommand,
  };
  const actionItems = getVehicleActionItems(actionHandlers);
  const ctxItems = (
    <>
      {actionItems.map((item) => (
        <ContextMenuItem key={item.key} onSelect={item.onSelect}>
          <span className="mr-2">{item.emoji}</span> {item.label}
        </ContextMenuItem>
      ))}
    </>
  );
  return (
    <Popover open={trailPanelOpen} onOpenChange={setTrailPanelOpen}>
    <ContextMenu>
      <ContextMenuTrigger asChild>
      <PopoverAnchor asChild>
        <li
          onClick={onSelect}
          className={cn(
            "group relative cursor-pointer overflow-hidden rounded-lg border-2 bg-background p-3 shadow-[0_10px_24px_-18px_rgb(0_0_0/0.7)] transition-all hover:-translate-y-0.5 hover:shadow-[0_14px_28px_-20px_rgb(0_0_0/0.75)]",
            active
              ? "border-primary bg-accent/45 ring-2 ring-primary/30"
              : "border-border hover:border-primary/45",
          )}
        >
          {/* actions menu (top-right) */}
          <div className="absolute right-3 top-3">
            <VehicleActionsMenu handlers={actionHandlers}>
              <button
                type="button"
                aria-label="Ações"
                className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border bg-background text-muted-foreground shadow-sm transition hover:bg-muted hover:text-foreground"
              >
                <MoreHorizontal className="h-4 w-4" strokeWidth={2.25} />
              </button>
            </VehicleActionsMenu>
          </div>

          <div className="flex items-center gap-3 pr-8">
            <div className="relative shrink-0">
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => {
                  void handlePickPhoto(e.target.files?.[0]);
                  e.target.value = "";
                }}
              />
              <button
                type="button"
                aria-label="Trocar foto do veículo"
                title="Trocar foto do veículo"
                disabled={photoUploading}
                onClick={(e) => {
                  e.stopPropagation();
                  photoInputRef.current?.click();
                }}
                className={cn(
                  "group/avatar relative flex h-16 w-16 items-center justify-center overflow-hidden rounded-full border-2 bg-card shadow-[inset_0_1px_4px_rgb(0_0_0/0.12)] transition",
                  active ? "border-primary/50" : "border-border",
                )}
              >
                {photoUrl && !photoFailed ? (
                  // Sem loading="lazy" de propósito: este <img> vive dentro do
                  // scroller aninhado da sidebar (overflow-y-auto, não o
                  // documento), e o WebKit (Safari/todo navegador iOS,
                  // incluindo Chrome-iOS) tem uma falha conhecida de nunca
                  // disparar o carregamento lazy nesse cenário — a imagem
                  // fica com src correto mas o navegador nunca chega a pedir
                  // o arquivo. Blink (Chrome/Edge desktop, Android) não tem
                  // esse problema, mas a imagem é um avatar de 44px — o custo
                  // de sempre carregar sem lazy é desprezível de qualquer forma.
                  <img
                    src={photoUrl}
                    alt={v.name}
                    className="h-full w-full object-cover"
                    onError={() => setPhotoFailed(true)}
                  />
                ) : (
                  <img
                    src={icon.url}
                    alt={icon.model.label}
                    loading="lazy"
                    className="h-14 w-14 object-contain drop-shadow-md"
                  />
                )}
                <span
                  aria-hidden
                  className={cn(
                    "absolute inset-0 flex items-center justify-center bg-black/55 text-white opacity-0 transition-opacity group-hover/avatar:opacity-100",
                    photoUploading && "opacity-100",
                  )}
                >
                  {photoUploading ? (
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                  ) : (
                    <Camera className="h-5 w-5" strokeWidth={2.25} />
                  )}
                </span>
              </button>
              <span
                aria-hidden
                className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full ring-2 ring-background"
                style={{ backgroundColor: ringTint, boxShadow: `0 0 7px 0 ${ringTint}` }}
              />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline gap-1.5">
                <span className="min-w-0 flex-1 truncate text-[13px] font-bold tracking-tight" title={v.name}>{v.name}</span>
                <StatusPill status={v.status} since={since} compact />
                {following && (
                  <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-primary/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-primary">
                    <Target className="h-2.5 w-2.5" strokeWidth={2.75} /> Seguindo
                  </span>
                )}
              </div>
              {ignitionAlerts && (
                <div className="mt-1 flex flex-wrap items-center gap-1 text-[10px] font-semibold">
                  <IgnitionAlertTag label="Alerta ignição ligada" on={ignitionAlerts.on} />
                  <IgnitionAlertTag label="Alerta ignição desligada" on={ignitionAlerts.off} />
                </div>
              )}
              <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px]">
                <LicensePlate plate={v.plate} size="xs" />
                <span className="inline-flex items-center gap-1 font-bold text-foreground">
                  <Zap className="h-3 w-3 text-amber-500" strokeWidth={2.5} /> {v.speed} km/h
                </span>
                {odo !== null && (
                  <>
                    <span className="text-border">·</span>
                    <span className="inline-flex items-center gap-1 font-semibold text-muted-foreground">
                      <RouteIcon className="h-3 w-3" strokeWidth={2.25} /> {odo.toLocaleString("pt-BR")} km
                    </span>
                  </>
                )}
              </div>
              <VehicleAddress vehicleId={v.id} lat={v.lat} lng={v.lng} positionTimestamp={v.positionTimestamp} />
              <VehicleFlags v={v} hasAnchor={hasAnchor} resellerCompanyName={resellerCompanyName} />
            </div>
          </div>
        </li>
      </PopoverAnchor>
      </ContextMenuTrigger>
      <ContextMenuContent className="w-56 z-[9999]">{ctxItems}</ContextMenuContent>
    </ContextMenu>
      <PopoverContent
        align="start"
        className="w-72"
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        <TrailPanel
          vehicleId={v.id}
          hasTrail={hasTrail}
          onApply={(style) => {
            onShowTrail(style);
            setTrailPanelOpen(false);
          }}
          onHide={() => {
            onHideTrail();
            setTrailPanelOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

interface TrailStyle {
  color: string;
  weight: number;
  opacity: number;
}

const TRAIL_COLORS = ["#2563eb", "#dc2626", "#16a34a", "#d97706", "#9333ea", "#0891b2"];

// Painel pequeno de configuração do rastro (cor/espessura/transparência) —
// SEMPRE em tempo real (pedido 2026-08-28: sem escolha de janela tipo
// "30 min/1h/3h", "a rota é em tempo real"), nunca busca histórico. Não mexe
// no marcador do veículo: só define o estilo e liga o acúmulo ao vivo (ver
// handleShowTrail em MapPage, que alimenta o array a cada novo fix).
function TrailPanel({
  hasTrail,
  onApply,
  onHide,
}: {
  vehicleId: string;
  hasTrail: boolean;
  onApply: (style: TrailStyle) => void;
  onHide: () => void;
}) {
  const [color, setColor] = useState(TRAIL_COLORS[0]);
  const [weight, setWeight] = useState(4);
  const [opacity, setOpacity] = useState(0.85);

  return (
    <div className="space-y-3">
      <div>
        <div className="mb-1.5 text-xs font-semibold text-foreground">Cor da linha</div>
        <div className="flex items-center gap-2">
          {TRAIL_COLORS.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setColor(c)}
              className={cn(
                "grid h-6 w-6 place-items-center rounded-full transition-transform",
                color === c ? "ring-2 ring-foreground ring-offset-2 ring-offset-popover" : "hover:scale-110",
              )}
              style={{ background: c }}
              aria-label={`Cor ${c}`}
            >
              {color === c && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
            </button>
          ))}
        </div>
      </div>

      <div>
        <div className="mb-1 flex items-center justify-between text-xs font-semibold text-foreground">
          <span>Espessura</span>
          <span className="font-normal text-muted-foreground">{weight}px</span>
        </div>
        <Slider value={[weight]} min={1} max={10} step={1} onValueChange={([val]) => setWeight(val)} />
      </div>

      <div>
        <div className="mb-1 flex items-center justify-between text-xs font-semibold text-foreground">
          <span>Transparência</span>
          <span className="font-normal text-muted-foreground">{Math.round(opacity * 100)}%</span>
        </div>
        <Slider
          value={[Math.round(opacity * 100)]}
          min={10}
          max={100}
          step={5}
          onValueChange={([val]) => setOpacity(val / 100)}
        />
      </div>

      <div className="flex items-center gap-2 pt-1">
        <Button size="sm" className="flex-1" onClick={() => onApply({ color, weight, opacity })}>
          {hasTrail ? "Atualizar estilo" : "Ativar rastro em tempo real"}
        </Button>
        {hasTrail && (
          <Button size="sm" variant="outline" onClick={onHide}>
            Ocultar
          </Button>
        )}
      </div>
    </div>
  );
}

function VehicleAddress({
  vehicleId,
  lat,
  lng,
}: {
  vehicleId: string;
  lat: number;
  lng: number;
  positionTimestamp?: string;
}) {
  const addr = useReverseGeocode(lat, lng, { stableKey: vehicleId });
  return (
    <div className="mt-1.5 flex items-center gap-1 text-[11.5px] font-semibold text-blue-600 dark:text-blue-400">
      <span aria-hidden className="leading-none shrink-0">📍</span>
      <span className="truncate leading-snug" title={addr ?? undefined}>
        {addr ?? "Localizando endereço…"}
      </span>
    </div>
  );
}




// Etiqueta única de status: dot sólido colorido (com pulso quando online,
// como um "LED" ao vivo — sem o emoji de círculo colorido antigo, que
// renderiza como sticker/emoji de chat e não como indicador de painel) +
// rótulo + (quando disponível) "• há X" no mesmo badge — sem `since`, mostra
// só o status (usado nos lugares que ainda não têm um veículo real, ex.:
// dados de exemplo).
function StatusPill({
  status,
  since,
  compact,
}: {
  status: VehicleStatus;
  since?: string | null;
  compact?: boolean;
}) {
  if (compact) {
    // Explícito por status (regra alterada 2026-08-31 a pedido do usuário:
    // offline agora É vermelho, junto com bloqueio/alerta crítico).
    const textColor =
      status === "online"
        ? "text-emerald-600 dark:text-emerald-400"
        : status === "idle"
          ? "text-amber-600 dark:text-amber-400"
          : isPolitrackerCompany()
            ? "text-slate-950 dark:text-white"
            : "text-red-600 dark:text-red-400";
    const dotColor =
      status === "online"
        ? "bg-emerald-500"
        : status === "idle"
          ? "bg-amber-500"
          : isPolitrackerCompany()
            ? "bg-black ring-1 ring-white/50"
            : "bg-red-500";
    return (
      <span
        className={cn(
          "inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-[10.5px] font-bold uppercase tracking-wide",
          textColor,
        )}
      >
        <span aria-hidden className="relative flex h-1.5 w-1.5">
          {status === "online" && (
            <span className={cn("absolute inline-flex h-full w-full animate-ping rounded-full opacity-75", dotColor)} />
          )}
          <span className={cn("relative inline-flex h-1.5 w-1.5 rounded-full", dotColor)} />
        </span>
        {statusLabel(status)}
        {since && <span className="lowercase tracking-normal opacity-75">• {since}</span>}
      </span>
    );
  }

  const styles =
    status === "online"
      ? "border-emerald-500/60 bg-emerald-500 text-white shadow-[0_2px_8px_-2px_rgba(16,185,129,0.55)] dark:bg-emerald-500/20 dark:text-emerald-300"
      : status === "idle"
      ? "border-yellow-500/60 bg-yellow-400 text-yellow-950 shadow-[0_2px_8px_-2px_rgba(234,179,8,0.55)] dark:bg-yellow-500/20 dark:text-yellow-300"
      : isPolitrackerCompany()
      ? "border-black bg-black text-white"
      : "border-red-500/60 bg-red-500 text-white shadow-[0_2px_8px_-2px_rgba(239,68,68,0.55)] dark:bg-red-500/20 dark:text-red-300";
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wider",
        styles,
      )}
    >
      <span aria-hidden className="h-1.5 w-1.5 shrink-0 rounded-full bg-current opacity-90" />
      {statusLabel(status)}
      {since && <span className="lowercase tracking-normal opacity-80">• {since}</span>}
    </span>
  );
}

function fmtVoltage(n?: number): string {
  return n != null ? `${n.toFixed(1)} V` : "—";
}

// Chips sólidos (bg de cor cheia + ícone branco) em vez das antigas pílulas
// translúcidas com emoji — mesma paleta de sempre (verde/vermelho/âmbar/azul/
// cinza), mas bem mais saturada: pedido explícito do usuário ("cores mais
// fortes, mais vermelho quando bloqueado, ignição ligada mais verde") depois
// de achar os emojis "infantis".
const flagTone = {
  green: "bg-emerald-600 text-white shadow-[0_2px_8px_-2px_rgba(16,185,129,0.65)]",
  red: "bg-red-600 text-white shadow-[0_2px_8px_-2px_rgba(239,68,68,0.65)]",
  amber: "bg-amber-500 text-amber-950 shadow-[0_2px_8px_-2px_rgba(245,158,11,0.6)]",
  sky: "bg-sky-600 text-white shadow-[0_2px_8px_-2px_rgba(14,165,233,0.6)]",
  slate: "bg-slate-500 text-white shadow-[0_2px_8px_-2px_rgba(100,116,139,0.55)]",
  black: "bg-black text-white shadow-[0_2px_8px_-2px_rgba(0,0,0,0.6)]",
  blue: "bg-blue-600 text-white shadow-[0_2px_8px_-2px_rgba(37,99,235,0.6)]",
} as const;

function VehicleFlags({
  v,
  hasAnchor,
  resellerCompanyName,
}: {
  v: Vehicle;
  hasAnchor: boolean;
  resellerCompanyName?: string;
}) {
  // Bloqueio nem sempre vem em todo pacote de posição (ex: gt06 só manda em
  // relatórios de status específicos) — sem isso o badge sumia. Sem o
  // atributo, assume não bloqueado (fail-safe visual, não decide comando
  // nenhum). Ignição é diferente: nunca cai para "ligada" por padrão — ver
  // src/lib/vehicle-state.ts.
  const blocked = isVehicleBlocked(v);
  const ignition = ignitionState(v);
  const alarmMsg = alarmLabel(v.alarm);
  const online = v.status !== "offline";
  // powerVoltage vem de attributes.power (fallback attributes.adc1) — é o
  // campo real preenchido pelos rastreadores em campo. attributes.battery
  // (batteryVoltage) nunca é enviado por nenhum protocolo em uso aqui,
  // confirmado direto no banco do Traccar (tc_positions.attributes).
  const voltage = fmtVoltage(v.powerVoltage);
  // Mesmas cores do ícone: ligado verde, parado amarelo, desligado preto.
  const poli = isPolitrackerCompany();
  const flags: {
    key: string;
    icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
    label: string;
    cls: string;
    show: boolean;
    value?: string;
  }[] = [
    { key: "moving", icon: Play, label: "Em movimento", cls: poli ? flagTone.green : flagTone.sky, show: !!v.moving },
    { key: "stopped", icon: Pause, label: "Parado", cls: poli ? flagTone.amber : flagTone.slate, show: !v.moving },
    { key: "ignitionOn", icon: KeyRound, label: "Ignição ligada", cls: flagTone.green, show: ignition === "on" },
    { key: "ignitionOff", icon: PowerOff, label: "Ignição desligada", cls: poli ? flagTone.black : flagTone.red, show: ignition === "off" },
    { key: "ignitionUnknown", icon: HelpCircle, label: "Ignição desconhecida", cls: flagTone.slate, show: ignition === "unknown" },
    { key: "blocked", icon: Lock, label: "Bloqueado", cls: flagTone.red, show: blocked },
    { key: "unblocked", icon: Unlock, label: "Desbloqueado", cls: flagTone.green, show: !blocked },
    { key: "noGps", icon: WifiOff, label: "Sem GPS", cls: flagTone.red, show: v.gpsSignal === false },
    { key: "alerts", icon: AlertTriangle, label: alarmMsg ?? "Alertas", cls: flagTone.amber, show: !!v.hasAlerts },
    { key: "lowBattery", icon: BatteryLow, label: "Bateria baixa", cls: flagTone.red, show: !!v.batteryLow },
    // Pedido 2026-09-04: "nos ícones da lista de frota tem que ter o ícone
    // da âncora pra ver se está ativa ou desativada" — mesmo par
    // ativo/inativo já usado acima pra bloqueado/desbloqueado, vindo de
    // hasAnchor (calculado em lote no MapPage, nunca uma query por linha).
    { key: "anchorActive", icon: Anchor, label: "Âncora ativada", cls: flagTone.green, show: hasAnchor },
    { key: "anchorInactive", icon: Anchor, label: "Sem âncora", cls: flagTone.slate, show: !hasAnchor },
    // Revenda (pedido 2026-09-07): só aparece pro admin da empresa-mãe,
    // nunca pro próprio admin da revenda (GET /vehicles/company-map só
    // devolve devices de empresa FILHA da empresa do chamador — ver
    // VehiclesRepository#findResellerCompanyMap no backend).
    {
      key: "reseller",
      icon: Building2,
      label: `Revenda: ${resellerCompanyName}`,
      cls: flagTone.sky,
      show: !!resellerCompanyName,
    },
    // Voltímetro fica sempre visível (não é condicional como os demais) para
    // o usuário acompanhar a tensão que o rastreador está reportando.
    { key: "voltage", icon: Zap, label: `Voltímetro: ${voltage}`, cls: poli ? flagTone.blue : online ? flagTone.green : flagTone.slate, show: true, value: voltage },
  ];
  const visible = flags.filter((f) => f.show);
  if (visible.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5">
      {visible.map((f) => (
        <span
          key={f.key}
          title={f.label}
          aria-label={f.label}
          className={cn(
            "inline-flex h-7 items-center justify-center rounded-md transition-transform hover:scale-110",
            f.value ? "gap-1 px-2" : "w-7",
            f.cls,
          )}
        >
          <f.icon className="h-3.5 w-3.5 shrink-0" strokeWidth={2.5} />
          {f.value && <span className="text-[11px] font-bold leading-none">{f.value}</span>}
        </span>
      ))}
    </div>
  );
}

/* ---------- detail sidebar (fleet list replaced when a vehicle is opened) ---------- */

type DetailTab = "geral" | "alertas" | "sensores";

function VehicleDetailSidebar({
  v,
  anchor,
  onBack,
  onCenter,
  onEdit: _onEdit,
}: {
  v: Vehicle;
  anchor: VehicleAnchor | undefined;
  onBack: () => void;
  onCenter: () => void;
  onEdit: () => void;
}) {
  const nowTick = useNowTick();
  const [tab, setTab] = useState<DetailTab>("geral");
  const [fav, setFav] = useState(false);
  const [sending, setSending] = useState(false);
  // Bug real 2026-09-01: mesmo incidente do menu de ações (ver
  // vehicle-actions-menu.tsx) — Bloquear/Desbloquear Veículo não checava
  // devices.lock/devices.unlock, então qualquer usuário logado conseguia
  // imobilizar o motor mesmo sem nenhuma permissão ativa.
  const canLock = usePermission("devices", "lock");
  const canUnlock = usePermission("devices", "unlock");
  // Âncora (pedido 2026-09-04) — mesmo padrão de canLock/canUnlock acima,
  // categoria "devices" ação "anchor" (ver PERMISSION_CATEGORIES).
  const canAnchor = usePermission("devices", "anchor");
  const [anchorDialogOpen, setAnchorDialogOpen] = useState(false);
  // Botão "i" (pedido 2026-09-26): linha/IMEI/operadora/modelo. Admin
  // sempre; funcionário só com devices.tracker_info; cliente nunca.
  const { gpsDriveUser } = useAuth();
  const hasTrackerInfoPerm = usePermission("devices", "tracker_info");
  const canSeeTrackerInfo =
    gpsDriveUser?.accountType === "admin" ||
    (gpsDriveUser?.accountType === "funcionario" && hasTrackerInfoPerm);
  const [trackerInfoOpen, setTrackerInfoOpen] = useState(false);
  // Popup de confirmação (pedido 2026-09-16, "no web tem que aparecer o
  // popup também") — antes só o bloqueio tinha um window.confirm() cru
  // (nem estilizado nem usado pro desbloqueio); agora os dois passam pelo
  // mesmo VehicleBlockConfirmDialog do PWA (ver
  // vehicle-block-confirm-dialog.tsx).
  const [confirmBlockOpen, setConfirmBlockOpen] = useState(false);
  // "Aguardando confirmação" é só um marcador de UI para o comando que
  // acabou de sair — NUNCA a fonte do estado de bloqueio. O estado exibido
  // (`blocked` abaixo) continua sendo lido direto de `v.blocked`, que vem
  // do Traccar (position.attributes.blocked) via a query ["vehicles"]. Sem
  // isso, o botão flutuava para o valor otimista e depois voltava ao valor
  // antigo assim que o próximo refetch confirmava que o rastreador ainda
  // não tinha processado o comando — dava a impressão de "bloqueou e
  // desbloqueou sozinho" quando na real o comando só ainda não tinha sido
  // confirmado pelo equipamento.
  const queryClient = useQueryClient();
  const blocked = isVehicleBlocked(v);
  // Compartilhado com o popup de comandos e o PWA (lib/pending-block-commands.ts):
  // some quando v.blocked bate com o pedido. Equipamento só-SMS (J4) espera
  // até confirmar; GPRS libera em 45s.
  const pending = usePendingBlockCommand(v.id, blocked, (p) => {
    toast.message("Ainda sem confirmação do rastreador", {
      description: `${v.name} · ${v.plate} — ${p.channel === "sms" ? "o SMS pode demorar para chegar ao equipamento." : "o comando pode levar mais tempo para ser processado pelo equipamento."}`,
    });
  });
  const pendingCommand = pending?.action ?? null;
  // SMS aguardando: deixa reenviar (operadora pode perder o SMS).
  const blockButtonLocked = !!pending && pending.channel === "gprs";
  const pendingLabel = pending?.channel === "sms" ? "Aguardando confirmação (SMS)…" : "Aguardando confirmação…";
  // Fallback SMS automático (pedido 2026-09-06) — se GPRS falhar (offline
  // ou erro do Traccar) e a empresa tiver SMS ativado + catálogo GT06
  // configurado, tenta reenviar por SMS via Veye antes de desistir. Ver
  // sendBlockCommandWithSmsFallback. Protocolo vem do cadastro GPS Drive
  // (vehicle_metadata.protocol, pedido 2026-09-07), nunca de v.protocol
  // (auto-detectado pelo Traccar).
  const { data: vehicleMetadata } = useVehicleMetadata(v.id);
  const smsFallback = useBlockCommandSmsFallback(vehicleMetadata?.protocol, vehicleMetadata?.trackerModel);

  // Envia um comando real de bloqueio/desbloqueio ao rastreador (Traccar
  // engineStop/engineResume) — interrompe fisicamente o motor do veículo.
  // Confirmação já aconteceu antes de chamar esta função (ver
  // VehicleBlockConfirmDialog abaixo), tanto pra bloquear quanto desbloquear.
  const sendBlockCommand = async (val: boolean) => {
    const action = val ? "block" : "unblock";
    // Pedido 2026-09-04: "se o veículo tiver sem comunicar, offline, não
    // pode deixar enviar comando, tem que enviar o erro" — Traccar
    // costuma aceitar (200) e só ENFILEIRAR o comando pra um device
    // offline, sem avisar ninguém que nada aconteceu de verdade ainda.
    // Desde 2026-09-06: esse caso vai direto pro fallback SMS (se
    // configurado) em vez de só erro — ver sendBlockCommandWithSmsFallback.
    const offline = v.status === "offline";
    setSending(true);
    try {
      const outcome = await sendBlockCommandWithSmsFallback({
        vehicleId: v.id,
        action,
        offline,
        fallback: smsFallback,
      });
      if (outcome.channel === "none") {
        if (outcome.reason === "offline") {
          toast.error(val ? "Não foi possível bloquear" : "Não foi possível desbloquear", {
            description: `${v.name} · ${v.plate} — veículo offline (sem comunicação com o rastreador agora).`,
          });
          logActivity({
            type: "ERROR",
            message: `Comando "${action}" recusado — ${v.name} (${v.plate}) está offline`,
            metadata: { vehicleId: v.id, commandType: action, reason: "device_offline" },
          });
        } else {
          toast.error(val ? "Falha ao bloquear o veículo" : "Falha ao desbloquear o veículo", {
            description: outcome.error instanceof Error ? outcome.error.message : undefined,
          });
        }
        return;
      }
      if (outcome.channel === "sms") {
        toast.success(val ? "Comando de bloqueio enviado por SMS" : "Comando de desbloqueio enviado por SMS", {
          description: smsFallback.smsOnly
            ? `${v.name} · ${v.plate} — aguardando o rastreador confirmar`
            : `${v.name} · ${v.plate} — enviado via SMS (GPRS indisponível)`,
        });
      } else {
        toast.success(val ? "Comando de bloqueio enviado" : "Comando de desbloqueio enviado", {
          description: `${v.name} · ${v.plate} — aguardando confirmação do rastreador`,
        });
      }
      await queryClient.invalidateQueries({ queryKey: ["vehicles"] });
    } catch (err) {
      toast.error(val ? "Falha ao bloquear o veículo" : "Falha ao desbloquear o veículo", {
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setSending(false);
    }
  };
  const address = useReverseGeocode(v.lat, v.lng, { stableKey: v.id, priority: true });

  // Usuário de acesso (login atual). Sem auth ainda: placeholder para integração futura.
  const accessUser =
    (typeof window !== "undefined" && window.localStorage.getItem("wp-access-user")) || "admin";

  // Explícito por status (regra alterada 2026-08-31 a pedido do usuário:
  // offline agora É vermelho, junto com bloqueio/estado crítico).
  const statusMeta = blocked
    ? { dot: "bg-red-500", label: "Bloqueado" }
    : v.status === "online"
      ? { dot: "bg-emerald-500", label: "Online" }
      : v.status === "idle"
        ? { dot: "bg-amber-500", label: "Parado" }
        : { dot: isPolitrackerCompany() ? "bg-black ring-1 ring-white/50" : "bg-red-500", label: "Offline" };

  
  const vehicleIcon = resolveVehicleIcon(v).url;
  // Foto cadastrada (vehicle_metadata) — mesma query em lote da lista da
  // frota, aqui só com o id do veículo aberto. Sem foto (ou se falhar ao
  // carregar) cai no ícone genérico de antes.
  const { data: detailImages } = useVehicleImages([v.id]);
  const detailImg = detailImages?.[v.id];
  const detailPhotoUrl = detailImg
    ? resolveGpsDriveUrl(detailImg["256"] ?? detailImg["128"] ?? detailImg.original)
    : undefined;
  const [detailPhotoFailed, setDetailPhotoFailed] = useState(false);
  const [detailPhotoLoaded, setDetailPhotoLoaded] = useState(false);
  useEffect(() => {
    setDetailPhotoFailed(false);
    setDetailPhotoLoaded(false);
  }, [detailPhotoUrl]);

  return (
    <div className="flex h-full min-h-0 flex-col bg-white text-foreground">
      {/* Header */}
      <div className="border-b border-border px-4 py-3">
        <div className="flex items-center justify-between">
          <button
            onClick={onBack}
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-white text-muted-foreground transition hover:bg-muted hover:text-foreground"
            aria-label="Voltar"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-1.5">
          {canSeeTrackerInfo && (
            <button
              onClick={() => setTrackerInfoOpen(true)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-white text-muted-foreground transition hover:bg-muted hover:text-foreground"
              aria-label="Informações do rastreador"
              title="Informações do rastreador"
            >
              <Info className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={() => setFav((f) => !f)}
            className={cn(
              "inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-white transition hover:bg-muted",
              fav ? "text-amber-500" : "text-muted-foreground hover:text-foreground",
            )}
            aria-label="Favoritar"
          >
            <Star className={cn("h-4 w-4", fav && "fill-amber-500")} />
          </button>
          </div>
        </div>
        {canSeeTrackerInfo && (
          <VehicleTrackerInfoDialog vehicle={v} open={trackerInfoOpen} onOpenChange={setTrackerInfoOpen} />
        )}

        <div className="mt-4 flex items-start gap-4">
          {/* Ícone do veículo fica embaixo enquanto a foto baixa — antes o
              quadro ficava em branco até a foto chegar (pedido 2026-10-01). */}
          <div className="relative flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-border bg-muted/40">
            {!(detailPhotoUrl && !detailPhotoFailed && detailPhotoLoaded) && (
              <img src={vehicleIcon} alt={v.name} className="h-16 w-16 object-contain drop-shadow-md" />
            )}
            {detailPhotoUrl && !detailPhotoFailed && (
              <img
                src={detailPhotoUrl}
                alt={v.name}
                fetchPriority="high"
                className={cn(
                  "absolute inset-0 h-full w-full object-cover transition-opacity duration-200",
                  detailPhotoLoaded ? "opacity-100" : "opacity-0",
                )}
                onLoad={() => setDetailPhotoLoaded(true)}
                onError={() => setDetailPhotoFailed(true)}
              />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-xl font-semibold tracking-tight text-foreground">{v.name}</h2>
            <div className="mt-1.5">
              <LicensePlate plate={v.plate} size="sm" />
            </div>
            <div className="mt-2 text-xs text-muted-foreground">
              Usuário: <span className="text-foreground">{accessUser}</span>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <div className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/40 px-2.5 py-1 text-[11px] font-medium">
                <span className={cn("h-1.5 w-1.5 rounded-full", statusMeta.dot)} />
                <span className="text-foreground">{statusMeta.label}</span>
              </div>
              {/* Pedido 2026-09-04: "o status tem que mudar âncora ativada"
                  — pílula própria (não substitui a de Online/Offline/
                  Bloqueado acima: âncora é ortogonal a esses estados, um
                  veículo pode estar ancorado E offline ao mesmo tempo). */}
              {anchor && (
                <div className="inline-flex items-center gap-1.5 rounded-full border border-sky-500/40 bg-sky-500/10 px-2.5 py-1 text-[11px] font-medium text-sky-700 dark:text-sky-400">
                  <Anchor className="h-3 w-3" />
                  <span>Âncora ativada</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Quick info */}
      <div className="grid grid-cols-1 gap-2 border-b border-border px-3 py-3">
        <QuickInfo
          label="Última posição"
          value={address ?? "Localizando endereço…"}
          sub={timeWithAgo(v.positionTimestamp ?? v.lastConnection, nowTick)}
          icon={MapPin}
          valueClassName="text-blue-600 dark:text-blue-400"
        />
        <QuickInfo
          label="Última comunicação"
          value={timeWithAgo(v.lastConnection, nowTick)}
          icon={Clock}
          valueClassName="text-foreground"
        />
        {/* Pedido 2026-09-26: sensores logo abaixo de "Última comunicação"
            com os MESMOS ícones do card da lista da frota (VehicleFlags). */}
        <div className="rounded-lg border border-border bg-card px-2.5 pb-2 pt-1 shadow-sm">
          <VehicleFlags v={v} hasAnchor={!!anchor} />
        </div>
      </div>


      {/* Actions */}
      {/* Pedido 2026-09-08: sem permissão o botão TEM que sumir, não só
          ficar desabilitado — antes ficava visível+cinza para qualquer
          usuário. */}
      {(canLock || canUnlock || canAnchor) && (
        <div className="grid grid-cols-1 gap-2 border-b border-border px-3 py-3">
          {blocked
            ? canUnlock && (
                <button
                  onClick={() => setConfirmBlockOpen(true)}
                  disabled={sending || blockButtonLocked}
                  className="flex items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <Unlock className="h-4 w-4" />
                  <span className="uppercase tracking-wider">
                    {sending ? "Enviando…" : pendingCommand ? pendingLabel : "Desbloquear Veículo"}
                  </span>
                </button>
              )
            : canLock && (
                <button
                  onClick={() => setConfirmBlockOpen(true)}
                  disabled={sending || blockButtonLocked}
                  className="flex items-center justify-center gap-2 rounded-xl bg-destructive px-4 py-3 text-sm font-semibold text-destructive-foreground shadow-sm transition-colors hover:bg-destructive/90 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <Lock className="h-4 w-4" />
                  <span className="uppercase tracking-wider">
                    {sending ? "Enviando…" : pendingCommand ? pendingLabel : "Bloquear Veículo"}
                  </span>
                </button>
              )}
          {canAnchor && (
            <button
              onClick={() => setAnchorDialogOpen(true)}
              className={cn(
                "flex items-center justify-center gap-2 rounded-xl border px-4 py-3 text-sm font-semibold shadow-sm transition-colors",
                anchor
                  ? "border-sky-500/40 bg-sky-500/10 text-sky-700 hover:bg-sky-500/20 dark:text-sky-400"
                  : "border-border bg-background text-foreground hover:bg-muted",
              )}
            >
              <Anchor className="h-4 w-4" />
              <span className="uppercase tracking-wider">
                {anchor ? "Âncora Ativa" : "Ativar Âncora"}
              </span>
            </button>
          )}
        </div>
      )}
      {anchorDialogOpen && (
        <VehicleAnchorDialog
          vehicle={{ ...v, offline: v.status === "offline" }}
          open={anchorDialogOpen}
          onClose={() => setAnchorDialogOpen(false)}
        />
      )}
      <VehicleBlockConfirmDialog
        open={confirmBlockOpen}
        onOpenChange={setConfirmBlockOpen}
        vehicleName={v.name}
        vehiclePlate={v.plate}
        canLock={canLock}
        canUnlock={canUnlock}
        sending={sending}
        onConfirm={(action) => sendBlockCommand(action === "block")}
      />


      {/* Tabs */}
      <nav className="flex items-center border-b border-border bg-white px-2">
        {(
          [
            { key: "geral", label: "Geral", emoji: "📋" },
            { key: "alertas", label: "Alertas", emoji: "🚨" },
            { key: "sensores", label: "Sensores", emoji: "📡" },
          ] as { key: DetailTab; label: string; emoji: string }[]
        ).map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={cn(
              "relative flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap px-3 py-2.5 text-xs font-medium transition-colors",
              tab === t.key ? "text-primary" : "text-muted-foreground hover:text-foreground",
            )}
          >
            <span className="text-sm leading-none">{t.emoji}</span>
            {t.label}
            {tab === t.key && (
              <span className="absolute inset-x-3 -bottom-px h-0.5 rounded-full bg-primary" />
            )}
          </button>
        ))}
      </nav>


      {/* Tab content */}
      <div className="min-h-0 flex-1 overflow-y-auto bg-white p-4">
        {tab === "geral" && <TabGeral v={v} address={address} onCenter={onCenter} />}
        {tab === "alertas" && <TabAlertas deviceId={Number(v.id)} />}
        {tab === "sensores" && <TabSensores v={v} />}
      </div>
    </div>
  );
}

function QuickInfo({
  icon: Icon,
  label,
  value,
  sub,
  valueClassName,
}: {
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  label: string;
  value: string;
  sub?: string;
  valueClassName?: string;
}) {
  return (
    <div className="rounded-lg border border-border bg-card px-2.5 py-2 shadow-sm">
      <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-wider text-muted-foreground">
        <Icon className="h-3 w-3" strokeWidth={1.75} />
        {label}
      </div>
      <div className={cn("mt-0.5 truncate text-xs font-semibold text-foreground", valueClassName)}>{value}</div>
      {sub && <div className="mt-0.5 truncate text-[10.5px] text-muted-foreground">{sub}</div>}
    </div>
  );
}

type ActionTone = "red" | "green" | "slate";
const actionToneStyles: Record<ActionTone, { bg: string; icon: string; hover: string }> = {
  red: {
    bg: "bg-red-500/10 border-red-500/30",
    icon: "text-red-400",
    hover: "hover:bg-red-500/20 hover:border-red-500/50",
  },
  green: {
    bg: "bg-emerald-500/10 border-emerald-500/30",
    icon: "text-emerald-400",
    hover: "hover:bg-emerald-500/20 hover:border-emerald-500/50",
  },
  slate: {
    bg: "bg-neutral-800 border-neutral-700",
    icon: "text-neutral-300",
    hover: "hover:bg-neutral-700 hover:border-neutral-600",
  },
};

function ActionButton({
  icon: Icon,
  label,
  tone,
  onClick,
}: {
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  label: string;
  tone: ActionTone;
  onClick?: () => void;
}) {
  const t = actionToneStyles[tone];
  return (
    <button
      onClick={onClick}
      className={cn(
        "flex flex-col items-center gap-1.5 rounded-lg border px-2 py-2.5 text-[10px] font-medium uppercase tracking-wider text-neutral-200 transition-all",
        t.bg,
        t.hover,
      )}
    >
      <Icon className={cn("h-5 w-5", t.icon)} strokeWidth={2} />
      {label}
    </button>
  );
}

function TabGeral({
  v,
  address,
  onCenter,
}: {
  v: Vehicle;
  address: string | null;
  onCenter: () => void;
}) {
  // Best-effort parse of address into street / city / state.
  const parts = (address ?? "").split(" · ").map((p) => p.trim()).filter(Boolean);
  const street = parts[0] ?? "—";
  const city = parts[parts.length - 1] ?? "—";

  const [showShare, setShowShare] = useState(false);

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-border bg-card p-3 shadow-sm">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
          Localização atual
        </div>
        <div className="mt-2 space-y-2">
          <InfoLine icon={MapPin} label="Endereço" value={street} />
          <InfoLine icon={Truck} label="Cidade" value={city} />
          <InfoLine icon={Hash} label="Estado" value="RS" />
          <InfoLine
            icon={Crosshair}
            label="Coordenadas"
            value={`${v.lat.toFixed(5)}, ${v.lng.toFixed(5)}`}
          />
        </div>
      </div>

      {/* Mini mapa em Street View (boneco do Google na rua onde o veículo
          está) — antes um hack legado (output=svembed, sem API key) que
          podia cair numa foto 360° de estabelecimento comercial em vez da
          rua de verdade ("esse problema do Street View esta tanto no app e
          tanto web", 2026-08-31). Componente compartilhado com o hero do
          app PWA, ver src/components/street-view-panorama.tsx. */}
      <div className="overflow-hidden rounded-xl border border-border shadow-sm">
        <StreetViewPanorama
          lat={v.lat}
          lng={v.lng}
          heading={v.bearing}
          className="h-40 w-full"
          fallback={
            <div className="flex h-full w-full items-center justify-center bg-muted text-xs text-muted-foreground">
              Sem imagem de rua disponível aqui
            </div>
          }
        />
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Button
          variant="outline"
          size="sm"
          className="h-9 gap-1.5"
          onClick={onCenter}
        >
          <Crosshair className="h-3.5 w-3.5" /> Centralizar
        </Button>
        {/* Era "Google Maps" (link externo) — trocado por Compartilhar
            localização, igual ao app (pedido 2026-09-29). */}
        <Button
          size="sm"
          className="h-9 gap-1.5 border-0 bg-gradient-to-br from-emerald-500 to-sky-500 text-white hover:opacity-90"
          onClick={() => setShowShare(true)}
        >
          <Share2 className="h-3.5 w-3.5" /> Compartilhar localização
        </Button>
      </div>
      {showShare && <LocationShareSheet asDialog vehicles={[v]} initialVehicleId={v.id} onClose={() => setShowShare(false)} />}
    </div>
  );
}

function InfoLine({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-wider text-muted-foreground">
        <Icon className="h-3 w-3" strokeWidth={1.75} />
        {label}
      </span>
      <span className="truncate text-xs font-medium text-foreground">{value}</span>
    </div>
  );
}

// ALERT_TYPE_META/ALERT_TONE_CLASSES movidos pra src/lib/alert-types.ts
// (2026-08-28) — reaproveitado agora também pela tela Notificações do app
// mobile (ver mobile-app.tsx), fonte única pra nunca duplicar/divergir a
// tradução PT-BR dos tipos do Traccar.
//
// Redesign 2026-09-01: passou a usar ALERT_CATALOG (2 grupos com cabeçalho,
// emoji, descrição em PT-BR, "Em breve" pros que ainda não existem de
// verdade) em vez da lista crua de GET /api/notifications/types — esse
// redesign de 2026-08-31 só tinha chegado no app PWA (MobileNotificationsPanel
// em mobile-app.tsx) e na aba Alertas de routes/vehicles.tsx
// (VehicleAlertsPanel, adicionada no mesmo dia); esta era a última tela
// ainda no visual antigo (bug relatado: "quando entro no mapa e clico em
// cima do veículo... design antigo"). Mesmo catálogo/lógica de toggle das
// outras duas, só que restilizado pros tokens de tema já usados no resto
// desta sidebar (border-border/bg-card/text-foreground, dark-mode-aware).
//
// Integração real com o Traccar: lê GET /api/notifications?deviceId=
// (regras já vinculadas a este veículo). Cada switch grava imediatamente
// via POST/DELETE /api/permissions (notifications.service.ts) — nada aqui
// é só visual, e nenhum mock é usado.
export function TabAlertas({ deviceId }: { deviceId: number }) {
  const notifQuery = useDeviceNotifications(deviceId);
  const toggleMutation = useToggleDeviceNotification(deviceId);
  const ruleQuery = useQuickToggleRules(deviceId);
  const ruleToggleMutation = useQuickToggleRule(deviceId);
  const [pendingType, setPendingType] = useState<string | null>(null);

  const enabledTypes = useMemo(
    () => new Set((notifQuery.data ?? []).map((n) => n.type)),
    [notifQuery.data],
  );
  const enabledRuleTypes = useMemo(
    () => new Set((ruleQuery.data ?? []).filter((r) => r.enabled).map((r) => r.type)),
    [ruleQuery.data],
  );

  const handleToggle = (types: string[], next: boolean) => {
    setPendingType(types[0]);
    Promise.all(types.map((type) => toggleMutation.mutateAsync({ type, enabled: next })))
      .then(() => toast.success(next ? "Alerta ativado" : "Alerta desativado"))
      .catch((err) =>
        toast.error("Não foi possível salvar o alerta", {
          description: err instanceof Error ? err.message : undefined,
        }),
      )
      .finally(() => setPendingType(null));
  };

  const ruleByType = useMemo(
    () => new Map((ruleQuery.data ?? []).filter((r) => r.geofenceId === null).map((r) => [r.type, r])),
    [ruleQuery.data],
  );

  const handleLimitsSave = (
    item: (typeof ALERT_CATALOG)[number],
    config: AlertRuleConfig,
    actionKind?: "notify" | "block",
  ) => {
    setPendingType(item.key);
    ruleToggleMutation.mutate(
      { type: item.alertRuleType!, enabled: true, config, ...(actionKind ? { actionKind } : {}) },
      {
        onSuccess: () => toast.success("Limites salvos", { description: item.label }),
        onError: (err) =>
          toast.error("Não foi possível salvar os limites", {
            description: err instanceof Error ? err.message : undefined,
          }),
        onSettled: () => setPendingType(null),
      },
    );
  };

  const handleRuleToggle = (item: (typeof ALERT_CATALOG)[number], next: boolean) => {
    setPendingType(item.key);
    ruleToggleMutation.mutate(
      { type: item.alertRuleType!, enabled: next },
      {
        onSuccess: () => toast.success(next ? "Alerta ativado" : "Alerta desativado", { description: item.label }),
        onError: (err) =>
          toast.error("Não foi possível salvar o alerta", {
            description: err instanceof Error ? err.message : undefined,
          }),
        onSettled: () => setPendingType(null),
      },
    );
  };

  if (notifQuery.isLoading || ruleQuery.isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-14 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  if (notifQuery.isError || ruleQuery.isError) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          Não foi possível carregar os alertas deste veículo. Tente novamente em instantes.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      {(["meus-alertas", "avancados"] as const).map((groupKey) => (
        <div key={groupKey}>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {groupKey === "meus-alertas" ? "Meus alertas" : "Alertas Avançados"}
          </h3>
          <ul className="space-y-2">
            {ALERT_CATALOG.filter((item) => item.group === groupKey).map((item) => {
              const togglable = !!item.alertRuleType || !!item.traccarTypes;
              const on = item.alertRuleType
                ? enabledRuleTypes.has(item.alertRuleType)
                : (item.traccarTypes?.every((t) => enabledTypes.has(t)) ?? false);
              const saving = item.alertRuleType
                ? pendingType === item.key && ruleToggleMutation.isPending
                : !!item.traccarTypes?.some((t) => pendingType === t) && toggleMutation.isPending;
              return (
                <li
                  key={item.key}
                  className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-card px-3 py-2.5 shadow-sm"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border bg-muted text-base">
                    {item.emoji}
                  </span>
                  <div className="min-w-0 flex-1">
                    <span className="block truncate text-xs font-medium text-foreground">{item.label}</span>
                    <span className="mt-0.5 block text-[10px] text-muted-foreground">
                      {togglable
                        ? on
                          ? ["Ativado", limitSummary(item.alertRuleType, ruleByType.get(item.alertRuleType!)?.config)]
                              .filter(Boolean)
                              .join(" · ")
                          : "Desativado"
                        : item.description}
                    </span>
                  </div>
                  {item.alertRuleType ? (
                    <Switch
                      checked={on}
                      disabled={saving}
                      onCheckedChange={(checked) => handleRuleToggle(item, checked)}
                      aria-label={item.label}
                    />
                  ) : item.traccarTypes ? (
                    <Switch
                      checked={on}
                      disabled={saving}
                      onCheckedChange={(checked) => handleToggle(item.traccarTypes!, checked)}
                      aria-label={item.label}
                    />
                  ) : (
                    <span className="shrink-0 rounded-full bg-muted px-2.5 py-1 text-[10px] font-semibold text-muted-foreground">
                      Em breve
                    </span>
                  )}
                  {on && isLimitEditable(item.alertRuleType) && (
                    <AlertLimitsEditor
                      key={JSON.stringify(ruleByType.get(item.alertRuleType!)?.config ?? null)}
                      type={item.alertRuleType!}
                      rule={ruleByType.get(item.alertRuleType!)}
                      saving={saving}
                      onSave={(config, actionKind) => handleLimitsSave(item, config, actionKind)}
                    />
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}

function TabSensores({ v }: { v: Vehicle }) {
  // v.ignition vem do atributo real reportado pelo rastreador
  // (position.attributes.ignition) — sem heurística por status: quando o
  // protocolo não reporta esse atributo nesse ciclo, o estado é
  // "desconhecida", nunca assumido como ligada. Ver src/lib/vehicle-state.ts.
  const ignition = ignitionState(v);
  const online = v.status !== "offline";
  const blocked = isVehicleBlocked(v);
  const alarm = alarmLabel(v.alarm);
  // Ligado/Desligado real (alimentação externa) — nunca a tensão. Ver
  // isVehiclePowered / powerStateFromAlarm: cai para Desligado só com sinal
  // explícito de corte de energia ou bateria violada.
  const powered = isVehiclePowered(v);

  type Sensor = {
    icon: typeof Power;
    label: string;
    value: string;
    good?: boolean;
    neutral?: boolean;
    blue?: boolean;
  };

  const estado: Sensor[] = [
    {
      icon: Power,
      label: "Ignição",
      value: ignitionLabel(ignition),
      good: ignition === "on",
      neutral: ignition === "unknown",
    },
  ];
  const blockingEnabled = useBlockingEnabled();
  const conexao: Sensor[] = [
    // Traccar não expõe força de sinal GSM padronizada entre protocolos —
    // mostramos conectividade real em vez de inventar "forte/fraco".
    { icon: Signal, label: "GSM", value: online ? "Conectado" : "Sem sinal", good: online },
  ];
  const visaoGeral: Sensor[] = [
    { icon: Gauge, label: "Velocidade", value: `${v.speed ?? 0} km/h`, neutral: true },
    { icon: Radio, label: "Alimentação", value: powered ? "Ligado" : "Desligado", good: powered },
    {
      icon: Activity,
      label: "Odômetro",
      value: v.odometer != null ? `${v.odometer.toLocaleString("pt-BR")} km` : "—",
      neutral: true,
    },
  ];
  const outros: Sensor[] = [
    {
      icon: Battery,
      // "(rastreador)" adicionado 2026-09-09: sem isso, era fácil confundir
      // com a tensão real do veículo (ver "Voltímetro" abaixo, v.powerVoltage)
      // — batteryLevel é sempre a bateria interna/backup do próprio
      // rastreador, nunca a do veículo.
      label: "Bateria (rastreador)",
      value: v.batteryLevel != null ? `${Math.round(v.batteryLevel)}%` : v.batteryLow ? "Baixa" : "—",
      good: !v.batteryLow,
    },
    ...(blockingEnabled
      ? [
          {
            icon: blocked ? Lock : Unlock,
            label: "Bloqueio",
            value: blocked ? "Bloqueado" : "Liberado",
            good: !blocked,
          } satisfies Sensor,
        ]
      : []),
    { icon: Radio, label: "Alimentação", value: powered ? "Ligado" : "Desligado", good: powered },
    {
      icon: Clock,
      label: "Horímetro",
      value: v.hours != null ? `${Math.round(v.hours / 3_600_000)} h` : "—",
      neutral: true,
    },
    {
      icon: Power,
      label: "Ignição",
      value: ignitionLabel(ignition),
      good: ignition === "on",
      neutral: ignition === "unknown",
    },
    { icon: Satellite, label: "Conexão", value: online ? "Online" : "Offline", good: online },
    { icon: BatteryLow, label: "Voltímetro", value: fmtVoltage(v.powerVoltage), neutral: !online, good: online, blue: isPolitrackerCompany() },
    ...(alarm
      ? [{ icon: AlertTriangle, label: "Alarme", value: alarm, good: false } satisfies Sensor]
      : []),
  ];

  const Card = ({ s }: { s: Sensor }) => (
    <div className="rounded-lg border border-border bg-card px-3 py-3 shadow-sm">
      <div className="flex items-center gap-2">
        <span
          className={cn(
            "flex h-8 w-8 items-center justify-center rounded-lg border",
            s.blue
              ? "border-blue-200 bg-blue-50 text-blue-600"
              : s.neutral
              ? "border-border bg-muted text-muted-foreground"
              : s.good
                ? "border-emerald-200 bg-emerald-50 text-emerald-600"
                : "border-red-200 bg-red-50 text-red-600",
          )}
        >
          <s.icon className="h-4 w-4" strokeWidth={2} />
        </span>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{s.label}</div>
          <div className="truncate text-xs font-medium text-foreground">{s.value}</div>
        </div>
      </div>
    </div>
  );

  const Group = ({ title, emoji, items }: { title: string; emoji: string; items: Sensor[] }) => (
    <div>
      <div className="mb-2 flex items-center gap-2 px-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        <span aria-hidden>{emoji}</span>
        <span>{title}</span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {items.map((s, i) => (
          <Card key={i} s={s} />
        ))}
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <Group title="Estado do veículo" emoji="🚘" items={estado} />
      <Group title="Conexão" emoji="📶" items={conexao} />
      <Group title="Visão geral" emoji="📊" items={visaoGeral} />
      <Group title="Outros" emoji="⚙️" items={outros} />
    </div>
  );
}



/* ---------- bottom detail bar ---------- */



function MapFiltersOverlay({
  stats,
  filters,
  onToggle,
  onClear,
}: {
  stats: {
    total: number | null;
    online: number | null;
    offline: number | null;
    blocked: number | null;
    moving: number | null;
  };
  filters: Set<FilterKey>;
  onToggle: (k: FilterKey) => void;
  onClear: () => void;
}) {
  return (
    <div className="pointer-events-none absolute left-4 right-4 top-[3.75rem] z-[450] flex flex-col gap-2">
      {/* quick indicators */}
      <div className="pointer-events-auto flex flex-wrap items-center gap-1.5">
        <IndicatorBadge label="Total" value={stats.total} tone="neutral" />
        <IndicatorBadge label="Online" value={stats.online} tone="online" />
        <IndicatorBadge label="Offline" value={stats.offline} tone="offline" />
        <IndicatorBadge label="Bloqueados" value={stats.blocked} tone="blocked" />
        <IndicatorBadge label="Em Movimento" value={stats.moving} tone="moving" />
      </div>
      {/* filter chips */}
      <div className="pointer-events-auto flex flex-wrap items-center gap-1.5">
        <FilterChip
          label="Todos"
          active={filters.size === 0}
          onClick={onClear}
        />
        {filterDefs.map((f) => (
          <FilterChip
            key={f.key}
            label={f.label}
            icon={f.icon}
            active={filters.has(f.key)}
            onClick={() => onToggle(f.key)}
          />
        ))}
      </div>
    </div>
  );
}

function IndicatorBadge({
  label,
  value,
  tone,
}: {
  label: string;
  value: number | null;
  tone: "neutral" | "online" | "offline" | "blocked" | "moving";
}) {
  const dot =
    tone === "online" ? "bg-emerald-500"
    : tone === "offline" ? (isPolitrackerCompany() ? "bg-black" : "bg-red-500")
    : tone === "blocked" ? "bg-orange-500"
    : tone === "moving" ? "bg-sky-500"
    : "bg-muted-foreground";
  return (
    <div className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card/95 px-2.5 py-1 text-[11px] font-medium shadow-sm backdrop-blur">
      <span className={cn("h-1.5 w-1.5 rounded-full", dot)} />
      <span className="text-muted-foreground">{label}</span>
      <span className="tabular-nums font-semibold text-foreground">{value === null ? "···" : value}</span>
    </div>
  );
}

function FilterChip({
  label,
  icon: Icon,
  active,
  onClick,
}: {
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium shadow-sm backdrop-blur transition-colors",
        active
          ? "border-foreground/30 bg-foreground text-background"
          : "border-border bg-card/95 text-foreground hover:bg-accent",
      )}
    >
      {Icon && <Icon className="h-3 w-3" />}
      {label}
    </button>
  );
}

/* ---------- map ---------- */

// Estrutura extensível: para adicionar um novo provedor, basta acrescentar
// uma entrada abaixo com { urls, attribution, label, maxZoom }.
type LayerKey = "streets" | "satellite" | "google_hybrid";
// Objeto `layers` (URLs de tile cru hot-linkadas em mt{s}.google.com,
// endpoint não-oficial da Google) removido na migração pra Google Maps real
// 2026-09-03 — ver LAYER_LABELS/mapTypeId em MapView, o próprio Maps
// JavaScript API já resolve roadmap/hybrid/satellite nativamente, sem URL
// de tile manual nem crossfade entre camadas.

/** Alerta quando um veículo em movimento supera o `maxspeed` do OSM (com 10% tolerância). */
// Teto por ciclo além do corte por viewport (ver chamada em MapView) — um
// viewport bem aberto (zoom out pra ver a frota inteira) ainda pode conter
// milhares de veículos em movimento; sem isso o loop sequencial abaixo
// nunca terminaria antes do próximo poll de 15s chegar. Prioriza os mais
// rápidos (mais chance de excesso real) em vez de pegar sempre os N
// primeiros do array.
const SPEED_ALERT_MAX_PER_CYCLE = 60;

// NO_GLIDE_IDS/GLIDE_MAX_ACTIVE (desligar glide acima de N veículos ativos)
// removidos na migração pra deck.gl 2026-09-03 — eram mitigação específica
// pro custo de re-renderizar milhares de <Marker> Leaflet a cada frame de
// animação; o deck.gl atualiza a IconLayer inteira via GPU a cada tick sem
// esse custo, MapView agora sempre passa activeVehicleIds (nunca desliga o
// glide). Cluster (MarkerClusterGroup) também removido — deck.gl não
// precisa agrupar pra manter DOM baixo (só 1 canvas, sempre), não implementa
// clustering visual ainda (marcadores podem se sobrepor com zoom bem
// aberto), ver plano de arquitetura 2026-09-03.

// Câmera do mapa (centro + zoom) persistida a nível de módulo — sobrevive a
// sair da tela (ex.: ir pra Usuários) e voltar, sem re-fazer o fit da frota
// inteira do zero. Formato {lat,lng} (google.maps.Map), não mais o
// [lat,lng] do Leaflet. Achado 2026-09-02 (usuário reportou "se eu for na
// tela de usuário e voltar pro mapa não pode carregar tudo de novo").
let persistedCamera: { center: { lat: number; lng: number }; zoom: number } | null = null;

function useSpeedLimitAlerts(vehicles: Vehicle[]) {
  const notifiedRef = useRef<Map<string, number>>(new Map());
  useEffect(() => {
    const NOTIFY_COOLDOWN_MS = 60_000;
    let cancelled = false;
    const candidates = vehicles
      .filter((v) => v.moving && (v.speed ?? 0) >= 10)
      .sort((a, b) => (b.speed ?? 0) - (a.speed ?? 0))
      .slice(0, SPEED_ALERT_MAX_PER_CYCLE);

    (async () => {
      for (const v of candidates) {
        if (cancelled) return;
        try {
          const limit = await getCachedMaxspeed(v.lat, v.lng);
          if (cancelled || !limit) continue;
          const excess = (v.speed ?? 0) - limit;
          if (excess > limit * 0.1) {
            const last = notifiedRef.current.get(v.id) ?? 0;
            const now = Date.now();
            if (now - last > NOTIFY_COOLDOWN_MS) {
              notifiedRef.current.set(v.id, now);
              toast.warning(
                `${v.name} está a ${Math.round(v.speed ?? 0)} km/h em via de ${limit} km/h`,
                { description: `Excesso de ${Math.round(excess)} km/h — Placa ${v.plate}` },
              );
            }
          }
        } catch {
          /* silencioso */
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // roda quando a lista de veículos muda (posição/velocidade)
  }, [vehicles]);
}

// Contagem de veículos vinculados a uma geocerca, para o popup do mapa
// principal. Só busca (GET /api/geofences?deviceId= por dispositivo, via
// useGeofenceDevices) quando o popup dessa geocerca está realmente aberto —
// evitar N chamadas por geocerca de uma vez só quando a camada é ligada.
function GeofenceVehicleCount({
  geofenceId,
  deviceIds,
  active,
}: {
  geofenceId: number;
  deviceIds: number[];
  active: boolean;
}) {
  const { data: linked, isLoading } = useGeofenceDevices(geofenceId, deviceIds, active);
  if (!active) return null;
  return (
    <div>
      Veículos vinculados: <b>{isLoading ? "…" : (linked?.length ?? 0)}</b>
    </div>
  );
}

// Popup do marcador no mapa — layout "cartão" (pedido 2026-10-09, mockup
// com foto + 4 indicadores + grade de sensores + barra de ações). Só dados
// reais: Combustível/Temperatura do mockup ficaram de fora porque nenhum
// rastreador da frota manda esses atributos (ver tipo Vehicle) — no lugar
// entram Bateria interna e Âncora. O "⋯" abre o MESMO menu de ações do
// card da lista (VehicleActionsMenu), e "Mostrar Rastro" continua
// reaproveitando o <TrailPanel> via Popover.
const VEHICLE_TYPE_LABEL: Record<NonNullable<Vehicle["type"]>, string> = {
  car: "Carro",
  moto: "Moto",
  truck: "Caminhão",
  van: "Van",
  machine: "Máquina",
};

function PopupStat({
  icon: Icon,
  iconCls,
  label,
  value,
  sub,
}: {
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  iconCls: string;
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="flex min-w-0 items-center gap-2.5 rounded-xl bg-white px-3 py-2.5 shadow-[0_1px_3px_rgba(15,23,42,0.08)] dark:bg-slate-800">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-700">
        <Icon className={cn("h-[18px] w-[18px]", iconCls)} strokeWidth={2.25} />
      </span>
      <div className="min-w-0">
        <div className="truncate text-[11px] text-slate-500 dark:text-slate-400">{label}</div>
        <div className="truncate text-[15px] font-bold leading-tight text-slate-900 dark:text-white">{value}</div>
        {sub && <div className="truncate text-[10.5px] text-slate-500 dark:text-slate-400">{sub}</div>}
      </div>
    </div>
  );
}

function PopupSensor({
  icon: Icon,
  iconCls,
  label,
  value,
  tone,
}: {
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  iconCls: string;
  label: string;
  value: string;
  tone: "ok" | "warn" | "bad" | "off";
}) {
  const dot =
    tone === "ok" ? "bg-emerald-500" : tone === "warn" ? "bg-amber-500" : tone === "bad" ? "bg-red-500" : "bg-slate-400";
  return (
    <div className="flex min-w-0 items-center gap-2.5 rounded-xl border border-slate-200/80 bg-white px-2.5 py-2 dark:border-slate-700 dark:bg-slate-800">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-700">
        <Icon className={cn("h-4 w-4", iconCls)} strokeWidth={2.25} />
      </span>
      <div className="min-w-0">
        <div className="truncate text-[12px] font-semibold text-slate-900 dark:text-white">{label}</div>
        <div className="flex items-center gap-1.5 text-[11.5px] text-slate-600 dark:text-slate-300">
          <span className={cn("h-2 w-2 shrink-0 rounded-full", dot)} />
          <span className="truncate">{value}</span>
        </div>
      </div>
    </div>
  );
}

function VehicleMarkerPopup({
  vehicle,
  following,
  hasTrail,
  onClose,
  onCenter,
  onShowDetails,
  onCommand,
  onShowHistory,
  onFollow,
  onShowTrail,
  onHideTrail,
  onEdit,
}: {
  vehicle: Vehicle;
  following: boolean;
  hasTrail: boolean;
  onClose: () => void;
  onCenter: () => void;
  onShowDetails: () => void;
  onCommand: () => void;
  onShowHistory: () => void;
  onFollow: () => void;
  onShowTrail: (style: TrailStyle) => void;
  onHideTrail: () => void;
  onEdit: () => void;
}) {
  const v = vehicle;
  const [trailPanelOpen, setTrailPanelOpen] = useState(false);
  const since = useVehicleStatusSince(v);
  const odo = typeof v.odometer === "number" ? v.odometer : null;
  const canEditDevices = usePermission("devices", "edit");
  const canSendCommand = usePermission("commands", "send_command");
  const { data: metadata } = useVehicleMetadata(v.id);
  const { anchor } = useVehicleAnchor(Number(v.id));
  const { data: images } = useVehicleImages([v.id]);
  const img = images?.[v.id];
  const photoUrl = img ? resolveGpsDriveUrl(img["256"] ?? img["128"] ?? img.original) : undefined;
  const [photoFailed, setPhotoFailed] = useState(false);
  useEffect(() => setPhotoFailed(false), [photoUrl]);

  const blockingEnabled = useBlockingEnabled();
  const blocked = isVehicleBlocked(v);
  const ignition = ignitionState(v);
  const title = [metadata?.brand, metadata?.model].filter(Boolean).join(" ") || v.name;
  const subtitle = [
    VEHICLE_TYPE_LABEL[detectVehicleType(v)],
    metadata?.year ? String(metadata.year) : null,
    metadata?.color ?? v.color ?? null,
  ].filter(Boolean);
  const lastConn = v.lastConnection ? new Date(v.lastConnection) : null;
  const statusCls =
    v.status === "online"
      ? "bg-emerald-500/20 text-emerald-300"
      : v.status === "idle"
        ? "bg-amber-500/20 text-amber-300"
        : isPolitrackerCompany()
          ? "bg-black text-white"
          : "bg-red-500/20 text-red-300";
  const statusDot =
    v.status === "online"
      ? "bg-emerald-400"
      : v.status === "idle"
        ? "bg-amber-400"
        : isPolitrackerCompany()
          ? "bg-white"
          : "bg-red-400";

  const actionHandlers: VehicleActionHandlers = {
    onCommand,
    onShowHistory,
    onFollow,
    onShowTrailPanel: () => setTrailPanelOpen(true),
    onEdit,
    following,
    canEdit: canEditDevices,
    canSendCommand,
  };

  return (
    <Popover open={trailPanelOpen} onOpenChange={setTrailPanelOpen}>
      <div className="vehicle-map-card w-[min(600px,calc(100vw-64px))] overflow-hidden rounded-2xl bg-slate-50 text-left font-sans dark:bg-slate-900">
        {/* Cabeçalho escuro: foto + nome + placa + status */}
        <div className="relative flex gap-3.5 bg-[#0f1b33] px-3.5 pb-6 pt-3.5 text-white">
          <div className="relative h-[84px] w-[112px] shrink-0 overflow-hidden rounded-xl bg-white/10 ring-1 ring-white/10">
            {photoUrl && !photoFailed ? (
              <img src={photoUrl} alt="" className="h-full w-full object-cover" onError={() => setPhotoFailed(true)} />
            ) : (
              <img src={iconUrlFor(v)} alt="" className="h-full w-full object-contain p-2" />
            )}
          </div>
          <div className="min-w-0 flex-1 pr-9">
            <div className="truncate text-[19px] font-bold leading-tight tracking-tight">{title}</div>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] text-slate-300">
              <LicensePlate plate={v.plate} size="sm" />
              {subtitle.length > 0 && <span className="truncate">{subtitle.join(" · ")}</span>}
            </div>
            <div className={cn("mt-1.5 inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-[12px]", statusCls)}>
              <span className={cn("h-2 w-2 rounded-full", statusDot)} />
              <span className="font-bold uppercase tracking-wide">{statusLabel(v.status)}</span>
              {since && <span className="opacity-90">· {since}</span>}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
          >
            <X className="h-4 w-4" strokeWidth={2.5} />
          </button>
        </div>

        <div className="-mt-3 space-y-3 rounded-t-2xl bg-slate-50 px-3 pb-3 pt-3 dark:bg-slate-900">
          {/* Indicadores principais */}
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <PopupStat icon={Gauge} iconCls="text-blue-600" label="Velocidade" value={`${Math.round(v.speed ?? 0)} km/h`} />
            <PopupStat
              icon={Waypoints}
              iconCls="text-blue-600"
              label="Quilometragem"
              value={odo !== null ? `${odo.toLocaleString("pt-BR")} km` : "—"}
            />
            <PopupStat
              icon={Zap}
              iconCls={ignition === "on" ? "text-amber-500" : "text-slate-400"}
              label="Ignição"
              value={ignition === "on" ? "Ligada" : ignition === "off" ? "Desligada" : "—"}
            />
            <PopupStat
              icon={Clock}
              iconCls="text-blue-600"
              label="Última comunicação"
              value={lastConn ? agoLabel(v.lastConnection) : "—"}
              sub={clockLabel(v.lastConnection) ?? undefined}
            />
          </div>

          {/* Sensores e status */}
          <div>
            <div className="mb-2 flex items-center justify-between px-0.5">
              <div className="flex items-center gap-1.5 text-[13.5px] font-bold text-slate-900 dark:text-white">
                <Radar className="h-4 w-4" strokeWidth={2.25} /> Sensores e status
              </div>
              <button
                type="button"
                onClick={onShowDetails}
                className="inline-flex items-center gap-0.5 text-[12.5px] font-medium text-blue-600 hover:underline"
              >
                Ver todos <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              <PopupSensor
                icon={Satellite}
                iconCls="text-blue-600"
                label="GPS"
                value={v.gpsSignal === false ? "Sem sinal" : "Normal"}
                tone={v.gpsSignal === false ? "bad" : "ok"}
              />
              <PopupSensor
                icon={Battery}
                iconCls={isPolitrackerCompany() ? "text-blue-600" : "text-emerald-600"}
                label="Bateria"
                value={v.powerVoltage != null ? fmtVoltage(v.powerVoltage) : v.powered === false ? "Desconectada" : "—"}
                tone={v.powered === false ? "bad" : v.batteryLow ? "warn" : v.powerVoltage != null ? "ok" : "off"}
              />
              <PopupSensor
                icon={BatteryLow}
                iconCls="text-sky-600"
                label="Bateria interna"
                value={v.batteryLevel != null ? `${Math.round(v.batteryLevel)}%` : "—"}
                tone={v.batteryLevel == null ? "off" : v.batteryLevel < 20 ? "bad" : v.batteryLevel < 50 ? "warn" : "ok"}
              />
              {blockingEnabled && (
                <PopupSensor
                  icon={blocked ? Lock : Unlock}
                  iconCls={blocked ? "text-red-600" : "text-blue-600"}
                  label="Bloqueio"
                  value={blocked ? "Bloqueado" : "Desligado"}
                  tone={blocked ? "bad" : "ok"}
                />
              )}
              <PopupSensor
                icon={Car}
                iconCls="text-blue-600"
                label="Movimento"
                value={v.moving ? "Em movimento" : "Parado"}
                tone={v.moving ? "ok" : "off"}
              />
              <PopupSensor
                icon={Anchor}
                iconCls={anchor ? "text-sky-600" : "text-slate-400"}
                label="Âncora"
                value={anchor ? "Ativada" : "Desativada"}
                tone={anchor ? "ok" : "off"}
              />
            </div>
          </div>

          {/* Ações */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onShowHistory}
              className="inline-flex h-11 flex-[1.4] items-center justify-center gap-2 rounded-xl bg-blue-600 px-3 text-[13.5px] font-semibold text-white shadow-sm transition-colors hover:bg-blue-700"
            >
              <HistoryIcon className="h-4 w-4" /> Ver histórico <ChevronRight className="h-4 w-4 opacity-80" />
            </button>
            <button
              type="button"
              onClick={onCenter}
              className="inline-flex h-11 flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-[13px] font-medium text-slate-800 transition-colors hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
            >
              <Crosshair className="h-4 w-4" /> Centralizar
            </button>
            <a
              href={`https://www.google.com/maps/dir/?api=1&destination=${v.lat},${v.lng}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex h-11 flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-[13px] font-medium text-slate-800 transition-colors hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
            >
              <Navigation className="h-4 w-4" /> Rota até aqui
            </a>
            <VehicleActionsMenu handlers={actionHandlers} align="end">
              <PopoverAnchor asChild>
                <button
                  type="button"
                  aria-label="Mais ações"
                  className="inline-flex h-11 w-12 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-800 transition-colors hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                >
                  <MoreHorizontal className="h-5 w-5" />
                </button>
              </PopoverAnchor>
            </VehicleActionsMenu>
          </div>
        </div>
      </div>
      <PopoverContent align="end" className="w-72" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        <TrailPanel
          vehicleId={v.id}
          hasTrail={hasTrail}
          onApply={(style) => {
            onShowTrail(style);
            setTrailPanelOpen(false);
          }}
          onHide={() => {
            onHideTrail();
            setTrailPanelOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

// Rótulos de camada exibidos no seletor — mapTypeId do Google (roadmap/
// hybrid/satellite) é nativo, não precisa mais de URLs de tile cru nem do
// crossfade manual entre camadas (o próprio Google Maps já cuida do
// carregamento/cache dos tiles). Substitui o antigo objeto `layers`
// (URLs mt{s}.google.com hot-linkadas, endpoint não-oficial — ver plano de
// arquitetura 2026-09-03).
const LAYER_LABELS: Record<LayerKey, string> = {
  streets: "Mapa",
  google_hybrid: "Híbrido HD",
  satellite: "Satélite HD",
};

function padBounds(bounds: google.maps.LatLngBounds, factor: number): google.maps.LatLngBounds {
  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();
  const latSpan = ne.lat() - sw.lat();
  const lngSpan = ne.lng() - sw.lng();
  return new google.maps.LatLngBounds(
    { lat: sw.lat() - latSpan * factor, lng: sw.lng() - lngSpan * factor },
    { lat: ne.lat() + latSpan * factor, lng: ne.lng() + lngSpan * factor },
  );
}

// colorReady=false só na primeira pintura, antes do fetch real resolver
// (ver comentário em MapPage#vehiclesColorReady) — usa o mesmo cinza
// neutro do preview do picker (AUTO_PREVIEW_COLOR) em vez da cor calculada
// a partir de um status que pode já estar desatualizado, pra nunca mostrar
// uma cor errada por 1-2s só pra "corrigir sozinha" logo em seguida.
function iconUrlFor(v: Vehicle, colorReady = true): string {
  const model = resolveVehicleModel(v.iconKey, detectVehicleType(v));
  const color = colorReady ? resolveMapVehicleColor(v) : AUTO_PREVIEW_COLOR;
  return v.mapIconStyle === "real" ? vehicleRealIconUrl(model, color) : vehicleIconDataUri(model, color);
}

// Camada de veículos — deck.gl IconLayer sobre um GoogleMapsOverlay
// (WebGLOverlayView por baixo). Substitui o antigo <Marker>/L.divIcon do
// Leaflet: nenhum elemento DOM por veículo, um único <canvas> pra frota
// inteira — migração 2026-09-03, ver
// gpsdrive_deckgl_google_maps_pilot_2026-09-03 na memória. Popup rico
// (VehicleMarkerPopup) montado sob demanda num único google.maps.InfoWindow
// reaproveitado (nunca um popup pré-criado por veículo) via createRoot,
// já que deck.gl não renderiza componentes React por feature.
function VehicleLayer({
  Mod,
  vehicles,
  vehiclesColorReady,
  bounds,
  zoom,
  focusId,
  onSelect,
  followId,
  trails,
  onCommand,
  onShowHistory,
  onFollow,
  onShowTrail,
  onHideTrail,
  onEdit,
  interleaved,
}: {
  Mod: any;
  vehicles: Vehicle[];
  vehiclesColorReady: boolean;
  bounds: MapBoundsBox | null;
  zoom: number;
  focusId?: string | null;
  onSelect: (id: string) => void;
  followId?: string | null;
  trails?: Record<string, { points: RoutePoint[] } & TrailStyle>;
  onCommand?: (v: Vehicle) => void;
  onShowHistory?: (id: string) => void;
  onFollow?: (id: string) => void;
  onShowTrail?: (vehicleId: string, style: TrailStyle) => void;
  onHideTrail?: (vehicleId: string) => void;
  onEdit?: (v: Vehicle) => void;
  // Ver comentário completo no efeito de criação do overlay logo abaixo —
  // `true` (padrão vetorial) evita a distorção de zoom fracionário, `false`
  // só é necessário no raster (Satélite/Híbrido).
  interleaved: boolean;
}) {
  const map = Mod.useMap();
  const overlayRef = useRef<any>(null);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const popupRootRef = useRef<ReturnType<typeof createRoot> | null>(null);
  const [popupVehicleId, setPopupVehicleId] = useState<string | null>(null);
  // Momento do último fechamento — lido no onClick da IconLayer (ver
  // closePopup). Ref, pra não recriar as camadas quando o popup fecha.
  const popupClosedAtRef = useRef(0);
  // Rotação do mapa (heading, mapa vetorial): o ícone gira no espaço da
  // TELA, então desconta o giro do mapa pra continuar apontando pro rumo real
  // (pedido 2026-10-27, ícone realista). Só muda quando o mapa gira.
  const [mapHeading, setMapHeading] = useState(0);
  useEffect(() => {
    if (!map) return;
    const sync = () => setMapHeading(map.getHeading?.() ?? 0);
    sync();
    const l = map.addListener("heading_changed", sync);
    return () => l.remove();
  }, [map]);
  // Agrupamento (clustering) — ver use-vehicle-clusters.ts. Reintroduzido
  // 2026-09-03: sem ele, com zoom bem aberto (fitBounds sobre uma frota
  // espalhada pelo país inteiro, ver CameraController) centenas de ícones
  // de 44px caem no mesmo pixel e viram uma mancha sólida. `focusId`
  // (veículo em foco, ver comentário em use-vehicle-clusters.ts) garante
  // que ele nunca some por causa de bounds/câmera ainda animando.
  const { items: clusterItems, expansionZoomFor } = useVehicleClusters(vehicles, bounds, zoom, focusId);
  const individualVehicles = useMemo(
    () => clusterItems.filter((i) => i.type === "vehicle").map((i) => i.vehicle),
    [clusterItems],
  );
  const clusters = useMemo(() => clusterItems.filter((i) => i.type === "cluster"), [clusterItems]);

  // Foto só dos veículos desenhados soltos (fora de cluster), nunca da
  // frota inteira — ver hooks/use-photo-markers.ts.
  const baseIconUrl = useCallback((v: Vehicle) => iconUrlFor(v, vehiclesColorReady), [vehiclesColorReady]);
  const { circleFor, headingLayer, photoSize } = usePhotoMarkers(individualVehicles, vehiclesColorReady, baseIconUrl, zoom, map);
  // createRoot(container) monta uma árvore React TOTALMENTE separada da
  // árvore principal do app (o container vive dentro do InfoWindow do
  // Google Maps, fora do DOM que o React da rota controla) — sem re-prover
  // o QueryClientProvider aqui, qualquer hook de dados dentro de
  // VehicleMarkerPopup (usePermission -> useMyPermissions -> useQuery)
  // quebra com "No QueryClient set". queryClient em si vem daqui de cima
  // (este componente ESTÁ na árvore principal), só o conteúdo renderizado
  // dentro do InfoWindow que precisa reaprender o provider.
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!map) return;
    // `interleaved` agora segue o layer ativo (pedido 2026-09-10, prints
    // reais mostrando o ícone do veículo como um retângulo preto sólido e
    // pinch-zoom manual, não só a animação inicial): `interleaved: false`
    // resolvia o bug de arrastar/zoom travado no raster (Satélite/Híbrido,
    // 2026-09-03, ver comentário histórico abaixo), mas esse modo NÃO
    // suporta zoom fracionário (documentado pelo próprio deck.gl) — QUALQUER
    // zoom fracionário (inclusive pinch/scroll manual do usuário, não só
    // fitBounds/panTo animados) deixa a camada do deck.gl dessincronizada da
    // projeção do mapa, e no IconLayer (textura, não só geometria) isso
    // aparece como o ícone virando um retângulo preto sólido em vez do
    // carrinho. `interleaved: true` é o modo recomendado pra basemap
    // vetorial (não sofre esse problema) — só troca pra `false` quando o
    // layer é raster de verdade (Satélite/Híbrido), que é onde o bug de
    // 2026-09-03 realmente vivia.
    //
    // Comentário histórico (bug 2026-09-03): sem `interleaved: false` a lib
    // deixa de aplicar pointer-events:none no <div> do canvas do deck.gl
    // quando o mapa cai pra renderização raster (Satélite/Híbrido, mesmo com
    // Map ID vetorial), bloqueando arrastar/zoom nesses layers.
    // Mapa vetorial (interleaved): ícones sempre por cima, sem disputar
    // profundidade com ruas/nomes — sem isso os nomes "piscavam" (print
    // 2026-10-21 com o Map ID próprio). Ver DECK_ON_TOP.
    const overlay = new Mod.GoogleMapsOverlay({ interleaved, parameters: DECK_ON_TOP });
    overlay.setMap(map);
    overlayRef.current = overlay;
    return () => {
      overlay.finalize?.();
      overlay.setMap(null);
      overlayRef.current = null;
    };
  }, [map, Mod, interleaved]);

  // Estilo "Realista" com quadros em perspectiva (2026-10-27): veículo com
  // grade pronta pro modelo+cor sai da camada comum e vai pra uma camada por
  // grade, mostrando o quadro do rumo (sem girar a imagem — a lateral do
  // carro tem que continuar do lado certo). Sem grade = ícone comum.
  const { isoGroups, plainVehicles } = useMemo(() => {
    const groups = new Map<string, { url: string; meta: IsoSheetMeta; data: Vehicle[] }>();
    const plain: Vehicle[] = [];
    for (const v of individualVehicles) {
      const sheet =
        v.mapIconStyle === "real" && vehiclesColorReady
          ? resolveIsoSheet(resolveVehicleModel(v.iconKey, detectVehicleType(v)).key, resolveMapVehicleColor(v))
          : undefined;
      if (!sheet) {
        plain.push(v);
        continue;
      }
      const g = groups.get(sheet.url) ?? { ...sheet, data: [] };
      g.data.push(v);
      groups.set(sheet.url, g);
    }
    return { isoGroups: [...groups.values()], plainVehicles: plain };
  }, [individualVehicles, vehiclesColorReady]);

  useEffect(() => {
    const overlay = overlayRef.current;
    if (!overlay) return;
    // Modo foto: círculo (foto ou ícone 3D) sem girar + seta de direção
    // separada. Enquanto o círculo não fica pronto, mostra o ícone normal.
    overlay.setProps({
      layers: [
        ...headingLayer(Mod, "vehicle-heading", individualVehicles, mapHeading),
        new Mod.IconLayer({
          id: "vehicles",
          data: plainVehicles,
          pickable: true,
          sizeUnits: "pixels",
          getPosition: (v: Vehicle) => [v.lng, v.lat],
          // Moto é estreita: mapScale (models.ts) aumenta só o marcador dela.
          getSize: (v: Vehicle) =>
            circleFor(v) ? photoSize : 44 * (resolveVehicleModel(v.iconKey, v.type).mapScale ?? 1),
          // Sentido/offset de rotação não confirmado visualmente em
          // produção ainda — course do Traccar é graus a partir do norte,
          // sentido horário; ajustar sinal aqui se aparecer invertido.
          getAngle: (v: Vehicle) => (circleFor(v) ? 0 : 360 - ((v.bearing ?? 0) - mapHeading)),
          // width/height reduzidos de 1024 pra 128 (pedido 2026-09-10, print
          // real: com a conta de teste de 5000 veículos, o mapa no Android
          // aparecia coberto de retângulos pretos sólidos em vez do ícone).
          // Causa real, confirmada contra issues do próprio deck.gl no
          // GitHub: `getIcon` reserva width×height no atlas de textura do
          // WebGL POR COMBINAÇÃO ÚNICA (modelo+cor) — declarar 1024×1024
          // (a resolução nativa do PNG gerado, pensada pra tela de detalhe/
          // preview, não pro marcador do mapa) empurra o atlas pra perto ou
          // além do limite de textura da GPU em muitos aparelhos Android
          // (bem menor que numa GPU de desktop); o sintoma documentado desse
          // estouro é exatamente "ícone vira um quadrado/retângulo preto
          // sólido". O marcador só é desenhado a 44px na tela (`getSize`
          // abaixo) — 128×128 já dá margem de sobra pra tela retina/HiDPI
          // sem chegar perto do limite.
          getIcon: (v: Vehicle) =>
            photoIconProps(circleFor(v)) ?? {
              url: iconUrlFor(v, vehiclesColorReady),
              width: 128,
              height: 128,
              anchorY: 64,
            },
          updateTriggers: { getIcon: [circleFor, vehiclesColorReady], getSize: [circleFor, photoSize], getAngle: [circleFor, mapHeading] },
          onClick: (info: any) => {
            if (!info.object) return;
            // Clique "fantasma" logo após fechar pelo X (mesmo gesto) não
            // reabre — ver closePopup.
            if (Date.now() - popupClosedAtRef.current < 400) return;
            const v = info.object as Vehicle;
            onSelect(v.id);
            setPopupVehicleId(v.id);
          },
        }),
        ...isoGroups.map(
          (g) =>
            new Mod.IconLayer({
              id: `vehicles-iso-${g.url}`,
              data: g.data,
              pickable: true,
              sizeUnits: "pixels",
              iconAtlas: g.url,
              iconMapping: isoIconMapping(g.meta),
              getPosition: (v: Vehicle) => [v.lng, v.lat],
              // O quadro tem margem em volta do carro — um pouco maior que o
              // ícone comum pra o carro sair do mesmo tamanho.
              getSize: (v: Vehicle) => 60 * (resolveVehicleModel(v.iconKey, v.type).mapScale ?? 1),
              getIcon: (v: Vehicle) => isoFrameFor(g.meta, (v.bearing ?? 0) - mapHeading),
              updateTriggers: { getIcon: [mapHeading] },
              onClick: (info: any) => {
                if (!info.object) return;
                if (Date.now() - popupClosedAtRef.current < 400) return;
                const v = info.object as Vehicle;
                onSelect(v.id);
                setPopupVehicleId(v.id);
              },
            }),
        ),
        new Mod.ScatterplotLayer({
          id: "vehicle-clusters",
          data: clusters,
          pickable: true,
          stroked: true,
          filled: true,
          radiusUnits: "pixels",
          getPosition: (c: { lng: number; lat: number }) => [c.lng, c.lat],
          getRadius: (c: { count: number }) => clusterBubbleRadiusPx(c.count),
          getFillColor: [14, 165, 233, 220], // sky-500
          getLineColor: [255, 255, 255, 255],
          lineWidthUnits: "pixels",
          getLineWidth: 2,
          onClick: (info: any) => {
            if (!info.object || !map) return;
            const c = info.object as { clusterId: number; lat: number; lng: number };
            const nextZoom = expansionZoomFor(c.clusterId);
            map.panTo({ lat: c.lat, lng: c.lng });
            map.setZoom(nextZoom);
          },
        }),
        new Mod.TextLayer({
          id: "vehicle-cluster-labels",
          data: clusters,
          getPosition: (c: { lng: number; lat: number }) => [c.lng, c.lat],
          getText: (c: { count: number }) => String(c.count),
          getSize: 11,
          getColor: [255, 255, 255, 255],
          fontFamily: "system-ui, sans-serif",
          fontWeight: 700,
          getTextAnchor: "middle",
          getAlignmentBaseline: "center",
          pickable: false,
        }),
      ],
    });
  }, [individualVehicles, plainVehicles, isoGroups, clusters, Mod, map, expansionZoomFor, vehiclesColorReady, circleFor, photoSize, mapHeading]);

  // Pedido 2026-10-09: "ao clicar no X o popup deve fechar na hora, sem o
  // cursor tremer, sem o mapa oscilar e sem reabrir sozinho". Causas:
  // 1) este efeito chamava infoWindow.open(map) a CADA poll da frota
  //    (`vehicles` muda a cada ~10s) e a cada mudança de followId/trails —
  //    cada open() faz autopan, que brigava com o panTo do modo "seguir"
  //    (CameraController) e deixava o mapa oscilando;
  // 2) eventos de mouse/clique de dentro do popup subiam até o mapa, onde o
  //    deck.gl faz picking (hover → cursor alternando, clique → podia
  //    selecionar o veículo embaixo e reabrir o popup).
  // Agora: open() só quando o veículo do popup muda (autopan só nessa hora),
  // setPosition só quando a coordenada muda de verdade, o conteúdo é só
  // re-renderizado (mesma raiz React, nunca recriada) e o container corta a
  // propagação. Clique de veículo logo após fechar é ignorado (guarda curta).
  const openedPopupIdRef = useRef<string | null>(null);
  const popupPosRef = useRef<{ lat: number; lng: number } | null>(null);

  const closePopup = useCallback(() => {
    popupClosedAtRef.current = Date.now();
    infoWindowRef.current?.close();
    openedPopupIdRef.current = null;
    popupPosRef.current = null;
    setPopupVehicleId(null);
  }, []);

  const popupVehicle = popupVehicleId ? vehicles.find((x) => x.id === popupVehicleId) : undefined;

  // Abre/fecha — só reage à troca de veículo, nunca ao poll.
  useEffect(() => {
    if (!map || typeof google === "undefined") return;
    if (!popupVehicleId) {
      infoWindowRef.current?.close();
      openedPopupIdRef.current = null;
      popupPosRef.current = null;
      return;
    }
    if (!popupVehicle || openedPopupIdRef.current === popupVehicleId) return;
    if (!infoWindowRef.current) {
      const container = document.createElement("div");
      // Bubble no próprio container: os handlers do React (registrados na
      // raiz = este container) rodam antes; aqui só impede o evento de
      // continuar até o mapa/deck.gl. Roda (wheel) não entra — zoom sobre
      // o popup continua funcionando.
      for (const type of [
        "click",
        "dblclick",
        "contextmenu",
        "mousedown",
        "mouseup",
        "mousemove",
        "pointerdown",
        "pointerup",
        "pointermove",
        "touchstart",
        "touchend",
      ]) {
        container.addEventListener(type, (e) => e.stopPropagation());
      }
      popupRootRef.current = createRoot(container);
      // Sem o cabeçalho/botão X nativo do Google — o cartão tem o próprio
      // (ver VehicleMarkerPopup); padding/raio zerados no CSS
      // (.gm-style-iw-c:has(.vehicle-map-card), styles.css).
      const iw = new google.maps.InfoWindow({ content: container, headerDisabled: true, maxWidth: 640 });
      iw.addListener("closeclick", closePopup);
      infoWindowRef.current = iw;
    }
    const pos = { lat: popupVehicle.lat, lng: popupVehicle.lng };
    popupPosRef.current = pos;
    infoWindowRef.current.setPosition(pos);
    infoWindowRef.current.open({ map, shouldFocus: false });
    openedPopupIdRef.current = popupVehicleId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [popupVehicleId, !!popupVehicle, map, closePopup]);

  // Acompanha o veículo em movimento sem reabrir (setPosition não faz autopan).
  useEffect(() => {
    const iw = infoWindowRef.current;
    if (!iw || !popupVehicle || openedPopupIdRef.current !== popupVehicle.id) return;
    const last = popupPosRef.current;
    if (last && last.lat === popupVehicle.lat && last.lng === popupVehicle.lng) return;
    popupPosRef.current = { lat: popupVehicle.lat, lng: popupVehicle.lng };
    iw.setPosition(popupPosRef.current);
  }, [popupVehicle?.id, popupVehicle?.lat, popupVehicle?.lng]);

  // Conteúdo — re-render na mesma raiz (dados novos do poll, seguir, rastro).
  useEffect(() => {
    const v = popupVehicle;
    if (!v || !map || !popupRootRef.current) return;
    popupRootRef.current.render(
      <QueryClientProvider client={queryClient}>
        <VehicleMarkerPopup
          vehicle={v}
          following={followId === v.id}
          hasTrail={!!trails?.[v.id]}
          onClose={closePopup}
          onCenter={() => {
            map.panTo({ lat: v.lat, lng: v.lng });
            if ((map.getZoom() ?? 0) < 16) map.setZoom(16);
          }}
          onShowDetails={() => onSelect(v.id)}
          onCommand={() => onCommand?.(v)}
          onShowHistory={() => onShowHistory?.(v.id)}
          onFollow={() => onFollow?.(v.id)}
          onShowTrail={(style) => onShowTrail?.(v.id, style)}
          onHideTrail={() => onHideTrail?.(v.id)}
          onEdit={() => onEdit?.(v)}
        />
      </QueryClientProvider>,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [popupVehicle, popupVehicleId, map, followId, trails, queryClient, closePopup]);

  useEffect(
    () => () => {
      infoWindowRef.current?.close();
      popupRootRef.current?.unmount();
    },
    [],
  );

  return null;
}

// Geocercas — camadas nativas do próprio Google Maps (Circle/Polygon), não
// deck.gl: são pouquíssimos elementos (nunca milhares como veículos), sem
// custo de DOM/GPU relevante, e ganham de graça o hit-testing/hover que o
// deck.gl exigiria configurar à mão. Popup no mesmo padrão de VehicleLayer
// (InfoWindow único reaproveitado + createRoot).
function GeofencesLayer({
  Mod,
  geofences,
  visible,
  deviceIds,
  strokeColor,
  fillColor,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Mod: any;
  geofences: Geofence[];
  visible: boolean;
  deviceIds: number[];
  // Pedido 2026-09-04: âncora precisa de cor PRÓPRIA (verde fraco/borda
  // vermelha), diferente do azul padrão de geocerca — quando informados,
  // sobrescrevem a cor customizada/padrão de cada geocerca individual.
  strokeColor?: string;
  fillColor?: string;
}) {
  // useMap() (não um `map` recebido por prop) — reage à instância real assim
  // que existe; um ref passado como prop (mapRef.current) não dispara
  // re-render sozinho quando o mapa termina de montar, deixando as
  // geocercas escondidas até o MapView re-renderizar por outro motivo.
  const map = Mod.useMap();
  const overlaysRef = useRef<Map<number, google.maps.Circle | google.maps.Polygon>>(new Map());
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const popupRootRef = useRef<ReturnType<typeof createRoot> | null>(null);
  const deviceIdsRef = useRef(deviceIds);
  deviceIdsRef.current = deviceIds;
  // Mesmo motivo do QueryClientProvider em VehicleLayer: GeofenceVehicleCount
  // usa useGeofenceDevices (useQuery) e o container do InfoWindow é uma
  // árvore React separada, sem o provider da árvore principal.
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!map || typeof google === "undefined") return;
    const current = overlaysRef.current;
    for (const ov of current.values()) ov.setMap(null);
    current.clear();
    if (!visible) return;

    for (const g of geofences) {
      const parsed = parseWkt(g.area);
      if (!parsed) continue;
      const attrColor = g.attributes?.color;
      const color = typeof attrColor === "string" && attrColor ? attrColor : "#0ea5e9";
      const baseOptions = {
        map,
        strokeColor: strokeColor ?? color,
        strokeWeight: 2,
        fillColor: fillColor ?? color,
        fillOpacity: fillColor ? 0.15 : 0.2,
        clickable: true,
      };
      const overlay: google.maps.Circle | google.maps.Polygon =
        parsed.type === "circle"
          ? new google.maps.Circle({
              ...baseOptions,
              center: { lat: parsed.data.lat, lng: parsed.data.lng },
              radius: parsed.data.radius,
            })
          : new google.maps.Polygon({
              ...baseOptions,
              paths: parsed.data.points.map((p) => ({ lat: p.lat, lng: p.lng })),
            });
      const typeLabel = parsed.type === "circle" ? "Círculo" : "Polígono";
      overlay.addListener("click", (e: google.maps.MapMouseEvent) => {
        if (!map || !e.latLng) return;
        if (!infoWindowRef.current) {
          const container = document.createElement("div");
          popupRootRef.current = createRoot(container);
          infoWindowRef.current = new google.maps.InfoWindow({ content: container });
        }
        popupRootRef.current!.render(
          <QueryClientProvider client={queryClient}>
            <div className="space-y-1">
              <div className="font-semibold">{g.name}</div>
              <div>
                Tipo: <b>{typeLabel}</b>
              </div>
              <GeofenceVehicleCount geofenceId={g.id} deviceIds={deviceIdsRef.current} active />
            </div>
          </QueryClientProvider>,
        );
        infoWindowRef.current.setPosition(e.latLng);
        infoWindowRef.current.open(map);
      });
      overlaysRef.current.set(g.id, overlay);
    }
  }, [map, geofences, visible, strokeColor, fillColor]);

  useEffect(
    () => () => {
      for (const ov of overlaysRef.current.values()) ov.setMap(null);
      overlaysRef.current.clear();
      infoWindowRef.current?.close();
      popupRootRef.current?.unmount();
    },
    [],
  );

  return null;
}

// "Mostrar Rastro" — mesma ideia do TrailsLayer antigo (Leaflet Polyline),
// agora com google.maps.Polyline nativo. Poucos elementos (um por veículo
// com rastro ativo, nunca a frota inteira), sem necessidade de deck.gl.
function TrailsLayer({
  Mod,
  trails,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Mod: any;
  trails?: Record<string, { points: RoutePoint[] } & TrailStyle>;
}) {
  // useMap() em vez de receber `map` por prop — ver comentário em
  // GeofencesLayer acima.
  const map = Mod.useMap();
  const overlaysRef = useRef<Map<string, google.maps.Polyline>>(new Map());

  useEffect(() => {
    if (!map || typeof google === "undefined") return;
    const current = overlaysRef.current;
    const seen = new Set<string>();
    if (trails) {
      for (const [vehicleId, trail] of Object.entries(trails)) {
        if (trail.points.length < 2) continue;
        seen.add(vehicleId);
        const path = trail.points.map((p) => ({ lat: p.lat, lng: p.lng }));
        let pl = current.get(vehicleId);
        if (!pl) {
          pl = new google.maps.Polyline({
            map,
            path,
            strokeColor: trail.color,
            strokeWeight: trail.weight,
            strokeOpacity: trail.opacity,
          });
          current.set(vehicleId, pl);
        } else {
          pl.setPath(path);
          pl.setOptions({ strokeColor: trail.color, strokeWeight: trail.weight, strokeOpacity: trail.opacity });
        }
      }
    }
    for (const [id, pl] of current) {
      if (!seen.has(id)) {
        pl.setMap(null);
        current.delete(id);
      }
    }
  }, [map, trails]);

  useEffect(
    () => () => {
      for (const pl of overlaysRef.current.values()) pl.setMap(null);
      overlaysRef.current.clear();
    },
    [],
  );

  return null;
}

const FOLLOW_RESUME_MS = 15_000;

// Câmera (fit inicial / seguir veículo / focar veículo / persistir
// posição+bounds) — mesmos 4 comportamentos que BoundsWatcher/InitialFit/
// FollowCam/Focuser cobriam no Leaflet, reescritos em cima da API nativa do
// google.maps.Map. `onBoundsChange` alimenta activeVehicleIds (throttle do
// glide + escopo do alerta de velocidade via Overpass — ver useEffect mais
// abaixo em MapView), não mais pra decidir quem ganha marcador (o deck.gl
// não precisa de corte por viewport pra isso).
function CameraController({
  Mod,
  vehicles,
  focusId,
  focusTick,
  followId,
  followResumeTick,
  onFollowPausedChange,
  onBoundsChange,
  mapRef,
}: {
  Mod: any;
  vehicles: Vehicle[];
  focusId: string | null;
  focusTick: number;
  followId?: string | null;
  followResumeTick: number;
  onFollowPausedChange: (paused: boolean) => void;
  onBoundsChange: (bounds: google.maps.LatLngBounds, zoom: number) => void;
  mapRef: React.MutableRefObject<google.maps.Map | null>;
}) {
  const map = Mod.useMap();
  const didInitialFitRef = useRef(false);
  const lastFocusTickRef = useRef(-1);
  const followPausedRef = useRef(false);
  const draggingRef = useRef(false);
  const zoomingUntilRef = useRef(0);
  const lastFollowPosRef = useRef<{ lat: number; lng: number } | null>(null);
  const onFollowPausedChangeRef = useRef(onFollowPausedChange);
  onFollowPausedChangeRef.current = onFollowPausedChange;
  const onBoundsChangeRef = useRef(onBoundsChange);
  onBoundsChangeRef.current = onBoundsChange;

  useEffect(() => {
    mapRef.current = map;
  }, [map, mapRef]);

  // Bug relatado 2026-09-09, confirmado por print real: ao abrir o mapa,
  // as bolhas de cluster (e possivelmente os ícones) aparecem esticadas/
  // deformadas por alguns segundos. Causa raiz real (não suposição —
  // documentada pelo próprio deck.gl): o overlay usa `interleaved: false`
  // (ver useEffect do overlay logo acima), e esse modo NÃO suporta zoom
  // fracionário — exatamente o que o Google Maps percorre durante QUALQUER
  // animação suave de zoom (fitBounds/setZoom/panTo padrão). Nesse
  // intervalo, a camada do deck.gl fica temporariamente dessincronizada da
  // projeção real do mapa, o que estica os círculos/ícones até a animação
  // terminar. map.moveCamera() aplica a mesma posição/zoom final SEM
  // passar por zoom fracionário/animado — usa fitBounds só como
  // calculadora (já atualiza getCenter()/getZoom() de forma síncrona) e
  // aplica o resultado via moveCamera pra pular direto pro estado final.
  // Tentativa anterior no mesmo dia foi revertida por parecer não
  // resolver + introduzir "borda preta" — hipótese agora, com o print em
  // mãos, é que aquela "borda preta" JÁ ERA esse mesmo bug de distorção
  // (só descrito de forma diferente), não uma regressão nova desta
  // mudança. Reaplicando com essa base mais sólida; precisa de teste real
  // pra confirmar.
  useEffect(() => {
    if (!map || didInitialFitRef.current) return;
    if (persistedCamera) {
      didInitialFitRef.current = true;
      return;
    }
    if (!vehicles.length) return;
    didInitialFitRef.current = true;
    if (vehicles.length === 1) {
      map.moveCamera({ center: { lat: vehicles[0].lat, lng: vehicles[0].lng }, zoom: 17 });
      return;
    }
    const bounds = new google.maps.LatLngBounds();
    for (const v of vehicles) bounds.extend({ lat: v.lat, lng: v.lng });
    map.fitBounds(bounds, 60);
    const c = map.getCenter();
    const z = map.getZoom();
    if (c && z != null) map.moveCamera({ center: { lat: c.lat(), lng: c.lng() }, zoom: z });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, vehicles.length]);

  const followedVehicle = followId ? vehicles.find((v) => v.id === followId) : undefined;
  useEffect(() => {
    if (!map || !followedVehicle) return;
    if (followPausedRef.current || draggingRef.current || performance.now() < zoomingUntilRef.current) return;
    // moveCamera, não panTo (2026-10-30, "o veículo anda e o mapa não segue"):
    // a posição vem deslizando a cada quadro e cada panTo reiniciava a
    // animação do anterior, deixando o mapa atrás do ícone. Mesma solução
    // do app (MobileVehicleLayer).
    // Anda o mapa o mesmo tanto que o veículo (pedido 2026-11-01): quem
    // arrastou ou deu zoom mantém o enquadramento e continua seguindo.
    const last = lastFollowPosRef.current;
    const center = map.getCenter();
    lastFollowPosRef.current = { lat: followedVehicle.lat, lng: followedVehicle.lng };
    map.moveCamera({
      center:
        last && center
          ? { lat: center.lat() + followedVehicle.lat - last.lat, lng: center.lng() + followedVehicle.lng - last.lng }
          : { lat: followedVehicle.lat, lng: followedVehicle.lng },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, followedVehicle?.lat, followedVehicle?.lng]);

  // Mexer no mapa não desliga o seguir (pedido 2026-11-01). Só pausa se o
  // veículo saiu da tela no arraste: volta depois de FOLLOW_RESUME_MS. O X do
  // aviso é que para de seguir.
  const followedRef = useRef(followedVehicle);
  followedRef.current = followedVehicle;
  const resumeFollowRef = useRef(() => {});
  useEffect(() => {
    if (!map || !followId) return;
    lastFollowPosRef.current = null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const setPaused = (paused: boolean) => {
      followPausedRef.current = paused;
      onFollowPausedChangeRef.current(paused);
    };
    const inView = () => {
      const v = followedRef.current;
      return !v || (map.getBounds()?.contains({ lat: v.lat, lng: v.lng }) ?? true);
    };
    const resume = () => {
      clearTimeout(timer);
      setPaused(false);
      const v = followedRef.current;
      if (!v) return;
      lastFollowPosRef.current = { lat: v.lat, lng: v.lng };
      map.moveCamera({ center: { lat: v.lat, lng: v.lng } });
    };
    resumeFollowRef.current = resume;
    const onStart = map.addListener("dragstart", () => {
      draggingRef.current = true;
      clearTimeout(timer);
      setPaused(false);
    });
    const onEnd = map.addListener("dragend", () => {
      draggingRef.current = false;
      if (inView()) return;
      setPaused(true);
      timer = setTimeout(resume, FOLLOW_RESUME_MS);
    });
    // Mover a câmera no meio da animação do zoom atrapalharia o zoom.
    const onZoom = map.addListener("zoom_changed", () => {
      zoomingUntilRef.current = performance.now() + 500;
    });
    // Zoom pela posição do cursor pode tirar o veículo da tela.
    const onIdle = map.addListener("idle", () => {
      if (!draggingRef.current && !followPausedRef.current && !inView()) resume();
    });
    return () => {
      clearTimeout(timer);
      onStart.remove();
      onEnd.remove();
      onZoom.remove();
      onIdle.remove();
      draggingRef.current = false;
      setPaused(false);
    };
  }, [map, followId]);
  useEffect(() => {
    if (followResumeTick > 0) resumeFollowRef.current();
  }, [followResumeTick]);

  useEffect(() => {
    if (!map || !focusId) return;
    if (lastFocusTickRef.current === focusTick) return;
    lastFocusTickRef.current = focusTick;
    const v = vehicles.find((x) => x.id === focusId);
    if (v) {
      // moveCamera (instantâneo, sem zoom fracionário/animado) — mesmo
      // motivo do fit-to-fleet acima. É este efeito que roda ao clicar
      // "Acessar rastreamento" no Início.
      map.moveCamera({ center: { lat: v.lat, lng: v.lng }, zoom: Math.max(map.getZoom() ?? 0, 16) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusTick]);

  // Bug relatado 2026-09-09 "entro no mapa e o veículo fica sumindo e
  // reaparecendo": numa sessão nova (sem persistedCamera ainda), o mapa
  // nasce no framing padrão (`center`/zoom 5, ver defaultCenter/defaultZoom
  // abaixo) e dispara seu PRIMEIRO "idle" quase imediatamente — ANTES dos
  // veículos carregarem e do efeito de fit-to-fleet/foco (acima) conseguir
  // reposicionar a câmera de verdade. Esse primeiro idle alimentava
  // onBoundsChange com o bounds do framing padrão; como "Agrupar veículos"
  // (showClusters) vem ligado por padrão, o clustering (use-vehicle-
  // clusters.ts) usa esse bounds pra excluir quem está fora dele — qualquer
  // veículo fora da área do zoom 5 padrão sumia até o SEGUNDO idle (já com
  // a câmera na posição final, pós fit/foco) disparar. Ignora esse primeiro
  // idle quando a sessão começou sem posição persistida — o segundo idle
  // (poucos ms/segundos depois, quando a posição real assenta) já chega
  // logo em seguida com o bounds correto.
  const hadPersistedCameraAtMountRef = useRef(!!persistedCamera);
  const skippedFirstIdleRef = useRef(false);
  useEffect(() => {
    if (!map) return;
    const listener = map.addListener("idle", () => {
      const c = map.getCenter();
      const z = map.getZoom();
      if (c && z != null) persistedCamera = { center: { lat: c.lat(), lng: c.lng() }, zoom: z };
      if (!hadPersistedCameraAtMountRef.current && !skippedFirstIdleRef.current) {
        skippedFirstIdleRef.current = true;
        return;
      }
      const b = map.getBounds();
      if (b && z != null) onBoundsChangeRef.current(b, z);
    });
    // Mapa reaproveitado (reuseMaps): a câmera já está assentada e pode não
    // vir nenhum "idle" novo — sem isso os bounds ficariam null até o
    // usuário mexer no mapa.
    const b = map.getBounds();
    const z = map.getZoom();
    if (b && z != null) onBoundsChangeRef.current(b, z);
    return () => listener.remove();
  }, [map]);

  return null;
}

function MapView({
  vehicles: allVehicles,
  vehiclesColorReady,
  focusId,
  focusTick,
  anchorGeofence,
  onSelect,
  followId,
  onStopFollow,
  trails,
  onShowTrail,
  onHideTrail,
  onCommand,
  onShowHistory,
  onFollow,
  onEdit,
}: {
  vehicles: Vehicle[];
  // false só na primeira pintura (snapshot do localStorage) antes do
  // primeiro fetch real resolver — ver comentário em MapPage. Controla
  // apenas a cor do ícone (iconUrlFor), nunca a posição.
  vehiclesColorReady: boolean;
  focusId: string | null;
  focusTick: number;
  // Pedido 2026-09-04: perímetro da âncora ativa do veículo aberto no
  // painel de detalhe — independente do toggle "Mostrar geocercas" (ver
  // showGeofences abaixo), pra aparecer assim que a âncora é ativada sem
  // depender de o operador ligar a camada geral de geocercas. Tem seu
  // próprio toggle "Âncora" no menu de camadas (showAnchor) desde que ficou
  // relatado que ela nunca desaparecia (não existia toggle nenhum).
  anchorGeofence?: Geofence;
  onSelect: (id: string) => void;
  followId?: string | null;
  onStopFollow?: () => void;
  trails?: Record<string, { points: RoutePoint[] } & TrailStyle>;
  onShowTrail?: (vehicleId: string, style: TrailStyle) => void;
  onHideTrail?: (vehicleId: string) => void;
  onCommand?: (v: Vehicle) => void;
  onShowHistory?: (id: string) => void;
  onFollow?: (id: string) => void;
  onEdit?: (v: Vehicle) => void;
}) {
  // Área visível (+ margem) — não decide mais quem ganha marcador (deck.gl
  // não precisa de corte por viewport pra isso, ver VehicleLayer acima),
  // só alimenta duas coisas que continuam legítimas independente do motor
  // de desenho: throttle do glide (useMovingVehicles) e escopo do alerta de
  // velocidade via Overpass (useSpeedLimitAlerts — API pública, sem SLA,
  // nunca a frota inteira de uma vez).
  // Pedido 2026-10-01: poder esconder as tags pelo olho do mapa. Ligado
  // por padrão. Veículo selecionado/seguido continua visível mesmo sendo
  // tag, senão o painel aberto apontaria pra um ícone que sumiu.
  const [showTags, setShowTags] = useState(
    () => typeof window === "undefined" || window.localStorage.getItem("wp-map-show-tags") !== "0",
  );
  useEffect(() => {
    window.localStorage.setItem("wp-map-show-tags", showTags ? "1" : "0");
  }, [showTags]);
  const rawVehicles = useMemo(
    () =>
      showTags
        ? allVehicles
        : allVehicles.filter((v) => !v.isTag || v.id === focusId || v.id === followId),
    [allVehicles, showTags, focusId, followId],
  );
  const [followPaused, setFollowPaused] = useState(false);
  const [followResumeTick, setFollowResumeTick] = useState(0);
  const [mapBounds, setMapBounds] = useState<google.maps.LatLngBounds | null>(null);
  const [mapZoom, setMapZoom] = useState(5);
  // Formato plano (west/south/east/north) que o clustering (ver
  // use-vehicle-clusters.ts) espera — supercluster não conhece
  // google.maps.LatLngBounds.
  const clusterBounds = useMemo((): MapBoundsBox | null => {
    if (!mapBounds) return null;
    const ne = mapBounds.getNorthEast();
    const sw = mapBounds.getSouthWest();
    return { west: sw.lng(), south: sw.lat(), east: ne.lng(), north: ne.lat() };
  }, [mapBounds]);
  const activeVehicleIds = useMemo(() => {
    if (!mapBounds) return null;
    const padded = padBounds(mapBounds, 0.5);
    const ids = new Set<string>();
    for (const v of rawVehicles) if (padded.contains({ lat: v.lat, lng: v.lng })) ids.add(v.id);
    return ids;
  }, [rawVehicles, mapBounds]);
  const vehicles = useMovingVehicles(rawVehicles, activeVehicleIds);

  const speedAlertVehicles = useMemo(() => {
    if (!activeVehicleIds) return [];
    return rawVehicles.filter((v) => activeVehicleIds.has(v.id));
  }, [rawVehicles, activeVehicleIds]);
  useSpeedLimitAlerts(speedAlertVehicles);

  // Mod: react-google-maps + deck.gl carregados via import() dinâmico (não
  // estático) — mesmo motivo do antigo Mod do react-leaflet: são libs
  // pesadas (deck.gl core ~275KB gzip), só fazem sentido carregar quando o
  // mapa de fato monta, nunca no bundle inicial da rota.
  const [Mod, setMod] = useState<any>(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      const [rgm, dgm, dgl] = await Promise.all([
        import("@vis.gl/react-google-maps"),
        import("@deck.gl/google-maps"),
        import("@deck.gl/layers"),
      ]);
      if (alive) {
        setMod({
          APIProvider: rgm.APIProvider,
          Map: rgm.Map,
          useMap: rgm.useMap,
          GoogleMapsOverlay: dgm.GoogleMapsOverlay,
          IconLayer: dgl.IconLayer,
          ScatterplotLayer: dgl.ScatterplotLayer,
          TextLayer: dgl.TextLayer,
        });
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const [layer, setLayer] = useState<LayerKey>("streets");
  const [showEyeMenu, setShowEyeMenu] = useState(false);
  // "Alertas do Waze" (ícones no próprio mapa) — ver
  // components/waze-alerts-layer.tsx.
  const [showWaze, setShowWaze] = useWazeAlertsToggle();
  const [fullscreen, setFullscreen] = useState(false);
  const [showGeofences, setShowGeofences] = useState(
    () => typeof window !== "undefined" && window.localStorage.getItem("wp-map-show-geofences") === "1",
  );
  useEffect(() => {
    window.localStorage.setItem("wp-map-show-geofences", showGeofences ? "1" : "0");
  }, [showGeofences]);
  const { data: geofences = [] } = useGeofences(showGeofences);
  // Bug relatado 2026-09-04: âncora aparecia com "2 cores" sobrepostas no
  // mapa. Causa: a geocerca da âncora é uma geocerca de verdade (ver
  // useVehicleAnchor) e por isso também vinha nesta lista geral — desenhada
  // 2x, uma vez aqui (azul padrão) e outra pela instância própria da âncora
  // logo abaixo (verde/vermelho). Filtra pelo mesmo prefixo usado em
  // ANCHOR_NAME_PREFIX pra essa lista de "Geocercas" nunca incluir a
  // geocerca que já tem desenho dedicado.
  const nonAnchorGeofences = useMemo(
    () => geofences.filter((g) => !g.name?.startsWith(ANCHOR_NAME_PREFIX)),
    [geofences],
  );
  // Array de 1 item memoizado — sem isso, um array `[anchorGeofence]` literal
  // no JSX recriaria identidade a cada render de MapView (posição do veículo
  // muda a cada poll), disparando o efeito de redesenho em GeofencesLayer à
  // toa mesmo sem a âncora ter mudado de verdade.
  const anchorGeofences = useMemo(() => (anchorGeofence ? [anchorGeofence] : []), [anchorGeofence]);
  // Agrupamento (clustering) — ligado por padrão (sem isso, zoom aberto com
  // frota densa vira mancha de ícones sobrepostos, ver
  // use-vehicle-clusters.ts); pedido 2026-09-03 pra poder desligar, igual
  // já dava pra fazer com geocercas.
  const [showClusters, setShowClusters] = useState(
    () => typeof window === "undefined" || window.localStorage.getItem("wp-map-show-clusters") !== "0",
  );
  useEffect(() => {
    window.localStorage.setItem("wp-map-show-clusters", showClusters ? "1" : "0");
  }, [showClusters]);
  // Pedido 2026-09-04: o perímetro da âncora era desenhado sempre que
  // existia (visible={!!anchorGeofence}, comentário acima), sem nenhum jeito
  // de esconder — igual "Geocercas"/"Agrupar veículos", ligado por padrão.
  const [showAnchor, setShowAnchor] = useState(
    () => typeof window === "undefined" || window.localStorage.getItem("wp-map-show-anchor") !== "0",
  );
  useEffect(() => {
    window.localStorage.setItem("wp-map-show-anchor", showAnchor ? "1" : "0");
  }, [showAnchor]);
  const [trailPanelOpen, setTrailPanelOpen] = useState(false);
  const [addr, setAddr] = useState("");
  const [searching, setSearching] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);

  useEffect(() => {
    if (!fullscreen) return;
    const onEsc = (e: KeyboardEvent) => e.key === "Escape" && setFullscreen(false);
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [fullscreen]);

  const geofenceDeviceIds = useMemo(() => rawVehicles.map((v) => Number(v.id)), [rawVehicles]);

  const apiKey = (import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined) ?? "";

  // rawVehicles (posição real, não a interpolada) — evita recalcular esta
  // média ~20x/s à toa; o centro inicial não precisa de posição suavizada.
  const center = rawVehicles.length
    ? {
        lat: rawVehicles.reduce((s, v) => s + v.lat, 0) / rawVehicles.length,
        lng: rawVehicles.reduce((s, v) => s + v.lng, 0) / rawVehicles.length,
      }
    : { lat: -30.03, lng: -51.2 };

  async function searchAddress(e: React.FormEvent) {
    e.preventDefault();
    if (!addr.trim() || !mapRef.current) return;
    setSearching(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(addr)}`,
      );
      const data = (await res.json()) as Array<{ lat: string; lon: string }>;
      if (data[0]) {
        mapRef.current.setCenter({ lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) });
        mapRef.current.setZoom(15);
      }
    } catch {
      /* ignore */
    } finally {
      setSearching(false);
    }
  }

  if (!Mod || !apiKey) {
    return (
      <div className="flex h-full items-center justify-center">
        <WaitingBackend />
      </div>
    );
  }

  const mapTypeId = layer === "streets" ? "roadmap" : layer === "satellite" ? "satellite" : "hybrid";

  return (
    <div ref={wrapRef} className={cn("relative h-full w-full", fullscreen && "fixed inset-0 z-[999] bg-background")}>
      <Mod.APIProvider apiKey={apiKey}>
        <Mod.Map
          mapId={resolveGoogleMapId()}
          // Print 2026-10-05 (mapa congelado com tiles esticados): sem isso,
          // cada volta pra tela do mapa criava um google.maps.Map novo — e um
          // contexto WebGL novo, que o Google nunca libera. Somado aos
          // Street Views, estourava o limite de ~16 contextos do Chrome e o
          // navegador derrubava o do mapa. reuseMaps guarda a instância ao
          // desmontar e devolve a mesma na próxima montagem.
          reuseMaps
          defaultCenter={persistedCamera?.center ?? center}
          defaultZoom={persistedCamera?.zoom ?? 5}
          // Sem isso, dava pra tirar o zoom até o nível "mundo" (Mercator
          // repete os tiles horizontalmente nesse nível — o "mapa
          // duplicado do lado" relatado 2026-09-03) e o fitBounds()
          // abaixo, que enquadra a frota inteira, empurrava até esse
          // extremo sozinho com uma frota de teste espalhada por 18 polos
          // no Brasil inteiro — provável gatilho do crash de WebGL (google
          // vetorial + IconLayer do deck.gl desenhando milhares de
          // veículos de uma vez nesse zoom). minZoom=3 ainda mostra o país
          // inteiro sem repetição de tile.
          minZoom={3}
          mapTypeId={mapTypeId}
          disableDefaultUI
          zoomControl={false}
          clickableIcons={false}
          gestureHandling="greedy"
          style={{ height: "100%", width: "100%" }}
        >
          <SatelliteZoomGuard Mod={Mod} mapTypeId={mapTypeId} />
          <MapTileRecovery Mod={Mod} />
          <CameraController
            Mod={Mod}
            vehicles={vehicles}
            focusId={focusId}
            focusTick={focusTick}
            followId={followId}
            followResumeTick={followResumeTick}
            onFollowPausedChange={setFollowPaused}
            onBoundsChange={(b, z) => {
              setMapBounds(b);
              setMapZoom(z);
            }}
            mapRef={mapRef}
          />
          <VehicleLayer
            Mod={Mod}
            vehicles={vehicles}
            vehiclesColorReady={vehiclesColorReady}
            bounds={showClusters ? clusterBounds : null}
            zoom={mapZoom}
            focusId={focusId}
            onSelect={onSelect}
            followId={followId}
            trails={trails}
            onCommand={onCommand}
            onShowHistory={onShowHistory}
            onFollow={onFollow}
            onShowTrail={onShowTrail}
            onHideTrail={onHideTrail}
            onEdit={onEdit}
            interleaved={layer === "streets"}
          />
          <WazeAlertsLayer
            Mod={Mod}
            bounds={clusterBounds}
            visible={showWaze}
            interleaved={layer === "streets"}
          />
          <GeofencesLayer
            Mod={Mod}
            geofences={nonAnchorGeofences}
            visible={showGeofences}
            deviceIds={geofenceDeviceIds}
          />
          {/* Perímetro da âncora ativa — reaproveita o MESMO GeofencesLayer
              (é literalmente uma geocerca, ver hooks/queries/index.ts#
              useVehicleAnchor), instância própria pra nunca depender do
              toggle "Mostrar geocercas" acima. Cor própria (pedido
              2026-09-04: "verde fraco com a borda em vermelho"), diferente
              do azul padrão das geocercas comuns — só pra ficar claro que
              é uma âncora, não uma cerca desenhada pelo operador. */}
          <GeofencesLayer
            Mod={Mod}
            geofences={anchorGeofences}
            visible={showAnchor && !!anchorGeofence}
            deviceIds={geofenceDeviceIds}
            strokeColor="#ef4444"
            fillColor="#86efac"
          />
          <TrailsLayer Mod={Mod} trails={trails} />
        </Mod.Map>
      </Mod.APIProvider>

      <div className="absolute right-4 top-4 z-[400] flex flex-col overflow-hidden rounded-lg border border-border bg-card/95 shadow-lg backdrop-blur" style={{ top: "calc(1rem + env(safe-area-inset-top, 0px))" }}>
        <button
          className="flex h-9 w-9 items-center justify-center text-foreground transition-colors hover:bg-accent"
          onClick={() => mapRef.current?.setZoom((mapRef.current.getZoom() ?? 5) + 1)}
          aria-label="Aumentar zoom"
        >
          <Plus className="h-4 w-4" />
        </button>
        <div className="h-px bg-border" />
        <button
          className="flex h-9 w-9 items-center justify-center text-foreground transition-colors hover:bg-accent"
          onClick={() => mapRef.current?.setZoom((mapRef.current.getZoom() ?? 5) - 1)}
          aria-label="Diminuir zoom"
        >
          <Minus className="h-4 w-4" />
        </button>
        <div className="h-px bg-border" />
        <button
          className="flex h-9 w-9 items-center justify-center text-primary transition-colors hover:bg-accent"
          onClick={() => {
            if (!navigator.geolocation) return;
            navigator.geolocation.getCurrentPosition(
              (pos) => {
                mapRef.current?.setCenter({ lat: pos.coords.latitude, lng: pos.coords.longitude });
                mapRef.current?.setZoom(17);
              },
              () => {
                /* ignore */
              },
              { enableHighAccuracy: true, timeout: 8000 },
            );
          }}
          aria-label="Minha localização"
          title="Minha localização"
        >
          <LocateFixed className="h-4 w-4" />
        </button>
      </div>

      {followId && vehicles.find((v) => v.id === followId) && (
        <div className="absolute left-1/2 top-4 z-[400] flex -translate-x-1/2 items-center gap-2 rounded-full border border-primary/40 bg-card/95 py-1.5 pl-3 pr-1.5 text-xs font-semibold shadow-lg backdrop-blur" style={{ top: "calc(1rem + env(safe-area-inset-top, 0px))" }}>
          <span className="relative flex h-2 w-2">
            {!followPaused && <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75" />}
            <span className={cn("relative inline-flex h-2 w-2 rounded-full", followPaused ? "bg-muted-foreground" : "bg-primary")} />
          </span>
          {followPaused ? "Pausado" : "Seguindo"} {vehicles.find((v) => v.id === followId)?.name}
          {followPaused && (
            <button
              type="button"
              onClick={() => setFollowResumeTick((t) => t + 1)}
              className="rounded-full px-2 py-0.5 text-primary transition hover:bg-accent"
            >
              Voltar ao veículo
            </button>
          )}
          <button
            type="button"
            onClick={onStopFollow}
            className="rounded-full p-1 text-muted-foreground transition hover:bg-accent hover:text-foreground"
            aria-label="Parar de seguir"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      <form
        onSubmit={searchAddress}
        className="absolute left-4 top-4 z-[400] flex items-center overflow-hidden rounded-lg border border-border bg-card/95 shadow-lg backdrop-blur"
        style={{ top: "calc(1rem + env(safe-area-inset-top, 0px))" }}
      >
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            value={addr}
            onChange={(e) => setAddr(e.target.value)}
            placeholder="Buscar endereço..."
            className="h-9 w-72 border-0 bg-transparent pl-8 focus-visible:ring-0"
          />
        </div>
        <button
          type="submit"
          disabled={searching}
          className="flex h-9 items-center border-l border-border px-3 text-xs font-medium text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50"
        >
          {searching ? "..." : "Ir"}
        </button>
      </form>

      <div className="absolute right-4 top-[7.5rem] z-[400] flex flex-col gap-2">
        <div className="relative">
          <button
            onClick={() => setShowEyeMenu((s) => !s)}
            className={cn(
              "flex h-9 w-9 items-center justify-center rounded-lg border border-border shadow-lg backdrop-blur transition-colors",
              showEyeMenu ? "bg-accent" : "bg-card/95 hover:bg-accent",
            )}
            aria-label="Camadas do mapa"
          >
            <Layers className="h-4 w-4" />
          </button>
          {showEyeMenu && (
            <div className="absolute right-11 top-0 w-40 overflow-hidden rounded-lg border border-border bg-card/95 shadow-lg backdrop-blur">
              {(Object.keys(LAYER_LABELS) as LayerKey[]).map((k) => (
                <button
                  key={k}
                  onClick={() => {
                    setLayer(k);
                    setShowEyeMenu(false);
                  }}
                  className={cn(
                    "flex w-full items-center justify-between px-3 py-2 text-xs transition-colors hover:bg-accent",
                    layer === k && "bg-accent font-medium",
                  )}
                >
                  {LAYER_LABELS[k]}
                  {layer === k && <CircleDot className="h-3 w-3" />}
                </button>
              ))}
              <div className="h-px bg-border" />
              <button
                onClick={() => setShowGeofences((s) => !s)}
                className="flex w-full items-center gap-2 px-3 py-2 text-xs transition-colors hover:bg-accent"
              >
                <LandPlot className="h-4 w-4" /> Geocercas
                {showGeofences && <Check className="ml-auto h-3.5 w-3.5" />}
              </button>
              <button
                onClick={() => setShowClusters((s) => !s)}
                className="flex w-full items-center gap-2 px-3 py-2 text-xs transition-colors hover:bg-accent"
              >
                <Combine className="h-4 w-4" /> Agrupar veículos
                {showClusters && <Check className="ml-auto h-3.5 w-3.5" />}
              </button>
              <button
                onClick={() => setShowAnchor((s) => !s)}
                className="flex w-full items-center gap-2 px-3 py-2 text-xs transition-colors hover:bg-accent"
              >
                <Anchor className="h-4 w-4" /> Âncora
                {showAnchor && <Check className="ml-auto h-3.5 w-3.5" />}
              </button>
              <button
                onClick={() => setShowTags((s) => !s)}
                className="flex w-full items-center gap-2 px-3 py-2 text-xs transition-colors hover:bg-accent"
              >
                <Tag className="h-4 w-4" /> Tags
                {showTags && <Check className="ml-auto h-3.5 w-3.5" />}
              </button>
              <div className="h-px bg-border" />
              <button
                onClick={() => setShowWaze(!showWaze)}
                className="flex w-full items-center gap-2 px-3 py-2 text-xs transition-colors hover:bg-accent"
              >
                <WazeIcon className="h-4 w-4" /> Alertas do Waze
                {showWaze && <Check className="ml-auto h-3.5 w-3.5" />}
              </button>
            </div>
          )}
        </div>
        <button
          onClick={() => setFullscreen((f) => !f)}
          className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-card/95 text-foreground shadow-lg backdrop-blur transition-colors hover:bg-accent"
          aria-label="Tela cheia"
        >
          {fullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
        </button>
        <Popover modal={false} open={trailPanelOpen} onOpenChange={setTrailPanelOpen}>
          <PopoverTrigger asChild>
            <button
              onClick={() => {
                if (!focusId) {
                  toast.info("Selecione um veículo primeiro", {
                    description: "Clique no veículo na lista ou no marcador do mapa para ver o rastro dele.",
                  });
                  return;
                }
                setTrailPanelOpen((s) => !s);
              }}
              className={cn(
                "flex h-9 w-9 items-center justify-center rounded-lg border border-border shadow-lg backdrop-blur transition-colors",
                focusId && trails?.[focusId]
                  ? "bg-primary text-primary-foreground"
                  : "bg-card/95 text-foreground hover:bg-accent",
              )}
              aria-label="Ver rastro do veículo selecionado"
              title={focusId ? "Ver rastro do veículo selecionado" : "Selecione um veículo para ver o rastro"}
            >
              <Waypoints className="h-4 w-4" />
            </button>
          </PopoverTrigger>
          {focusId && (
            <PopoverContent align="end" className="w-72">
              <TrailPanel
                vehicleId={focusId}
                hasTrail={!!trails?.[focusId]}
                onApply={(style) => {
                  onShowTrail?.(focusId, style);
                  setTrailPanelOpen(false);
                }}
                onHide={() => {
                  onHideTrail?.(focusId);
                  setTrailPanelOpen(false);
                }}
              />
            </PopoverContent>
          )}
        </Popover>
      </div>
    </div>
  );
}

/* ---------- vehicle form dialogs — cadastro real (GPS Drive Backend) ----------
   Cadastrar veículo e Editar dispositivo usam o mesmo VehicleFormPanel (mesmo
   form state, mesma lógica de salvar) — só o `vehicle` prop muda: null =
   cadastro, Vehicle = edição. Dois wrappers finos abaixo só porque são
   disparados por gatilhos diferentes (botão "+" vs. ação "Editar" do
   card/menu/mapa) — ver VEHICLE_FORM_ARCHITECTURE.md §8.1. */

const VEHICLE_FORM_DIALOG_CLASS =
  "flex h-[90vh] w-[90vw] max-w-[90vw] flex-col gap-0 overflow-hidden border-border bg-card p-0 sm:rounded-xl";
const VEHICLE_RENAME_DIALOG_CLASS =
  "flex w-[92vw] max-w-md flex-col gap-0 overflow-hidden border-border bg-card p-0 sm:rounded-xl";

// Conta cliente (accountType "usuario") só renomeia o dispositivo — ver
// VehicleFormPanel/VehicleRenamePanel.
function useIsNameOnlyVehicleEditor() {
  const { gpsDriveUser } = useAuth();
  return isRestrictedAccountType(gpsDriveUser?.accountType);
}

// Salvar do Editar dispositivo (pedido 2026-10-26: "demora muito para
// salvar, só fica carregando"): antes cada campo alterado esperava o refetch
// da frota INTEIRA (~800KB, milhares de veículos) antes de seguir/fechar.
// Agora a linha muda na hora no cache e a lista se revalida por trás (o
// refetch de 15s de useVehicles já cobre o resto).
function patchVehicleInCache(qc: QueryClient, id: string, patch: { name?: string; plate?: string; imei?: string }) {
  qc.setQueriesData<{ id: string }[]>({ queryKey: ["vehicles"], exact: true }, (list) =>
    Array.isArray(list) ? list.map((v) => (v.id === id ? { ...v, ...patch } : v)) : list,
  );
  void qc.invalidateQueries({ queryKey: ["vehicles"] });
}

export function VehicleEditDialog({
  vehicle,
  onOpenChange,
}: {
  vehicle: Vehicle | null;
  onOpenChange: (open: boolean) => void;
}) {
  const nameOnly = useIsNameOnlyVehicleEditor();
  return (
    <Dialog open={!!vehicle} onOpenChange={onOpenChange}>
      <DialogContent
        className={nameOnly ? VEHICLE_RENAME_DIALOG_CLASS : VEHICLE_FORM_DIALOG_CLASS}
      >
        {vehicle && <VehicleFormPanel vehicle={vehicle} onClose={() => onOpenChange(false)} />}
      </DialogContent>
    </Dialog>
  );
}

export function VehicleFormDialog({
  open,
  onOpenChange,
  editing,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editing?: Vehicle | null;
  onSaved?: (id: string) => void;
}) {
  const nameOnly = useIsNameOnlyVehicleEditor() && !!editing;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={nameOnly ? VEHICLE_RENAME_DIALOG_CLASS : VEHICLE_FORM_DIALOG_CLASS}
      >
        {open && (
          <VehicleFormPanel
            vehicle={editing ?? null}
            onClose={() => onOpenChange(false)}
            onSaved={onSaved}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

// Mesmo formatador usado no seletor de plano da aba FATURAMENTO
// (routes/users.tsx) — aqui só pro Select de Plano da aba Principal.
const planCurrencyFormatter = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });

type TabKey = "principal" | "hardware" | "alertas" | "logs" | "excluir";

const TABS: { key: TabKey; label: string; emoji?: string }[] = [
  { key: "principal", label: "Principal", emoji: "🚗" },
  { key: "hardware", label: "Hardware e Simcard", emoji: "📟" },
  { key: "alertas", label: "Alertas", emoji: "🔔" },
  { key: "logs", label: "Logs", emoji: "🧾" },
  { key: "excluir", label: "Excluir", emoji: "🗑️" },
];

// Sentinela do item "Outro" no Select de protocolo (pedido 2026-09-16) —
// nunca colide com um `value` real do catálogo, que só aceita minúsculas/
// dígitos/hífen (ver createTrackerProtocolDto no backend, sem "_"). Ao
// escolher, mostra um campo de texto pro usuário digitar o nome; no
// handleSave isso é resolvido pra um protocolo de verdade via
// useQuickCreateTrackerProtocol (cadastra se não existir, reaproveita se já
// existir) antes de gravar em vehicle_metadata.
const CUSTOM_PROTOCOL_VALUE = "__custom__";

// Regra pedida 2026-09-16: todo Tag começa com "00000" — o usuário só
// digita o restante do número, o prefixo é fixo e automático. Existe pra
// evitar de novo o bug real já visto (comentário mais abaixo, perto de
// `imei`) de um tag cadastrado sem os zeros à esquerda que o próprio
// aparelho manda.
const TAG_IMEI_PREFIX = "00000";

// O tag (GT06) se identifica com 15 dígitos, zeros à esquerda (ex.:
// 000003092528340). Bug 2026-10-27: com o cadastro antigo curto
// ("3092528340") o campo mostrava o número inteiro e quem digitava o código
// completo ganhava os zeros DUAS vezes (00000 + 000003092528340 = 20
// dígitos, "Unknown device"). Aceita o número de qualquer jeito — curto,
// completo ou com zeros a mais — e sempre devolve os 15 dígitos.
function normalizeTagImei(raw: string): string {
  const digits = raw.replace(/\D/g, "").replace(/^0+/, "");
  return digits ? digits.padStart(15, "0") : "";
}

// Estado local (buffer) do formulário inteiro de cadastro — as duas abas
// (Principal/Hardware) leem e escrevem aqui, e só o botão "Salvar
// alterações" do header dispara o PUT de verdade no GPS Drive Backend.
// Strings vazias viram null no payload (ver buildSavePayload) — nunca
// grava string vazia como valor real.
interface CadastroFormState {
  customName: string;
  brand: string;
  model: string;
  year: string;
  planId: string;
  color: string;
  fipeValue: string;
  chassis: string;
  notes: string;
  trackerSupplier: string;
  simCarrier: string;
  simIccid: string;
  simNumber: string;
  simApn: string;
  trackerBrand: string;
  trackerModel: string;
  trackerPrice: string;
  // Protocolo do rastreador (pedido 2026-09-07; deixou de ser obrigatório
  // em 2026-09-16) — pode ser um valor do catálogo (Super Master >
  // Protocolos) ou CUSTOM_PROTOCOL_VALUE ("Outro", resolvido pra um
  // protocolo de verdade em handleSave antes de ir pro payload, ver
  // customProtocolLabel/useQuickCreateTrackerProtocol). Nunca confundir com
  // vehicle.protocol (auto-detectado pelo Traccar).
  protocol: string;
  // IMEI da câmera JT/T808 instalada neste veículo (pedido 2026-09-16, tela
  // Câmera) — opcional, nunca confundir com o IMEI do rastreador (acima,
  // fora deste form: vem do Traccar).
  cameraImei: string;
  // Tipo de rastreador (pedido 2026-09-16): "rastreador" | "tag" | "camera"
  // — decide se a aba Hardware mostra 1 ou 2 campos de IMEI (ver
  // TabHardware). Sempre preenchido (default "rastreador"), nunca "".
  trackerType: string;
  installCompany: string;
  installTechnician: string;
  installedAt: string;
  installLocation: string;
  installWarranty: string;
  installNotes: string;
}

const emptyCadastroForm: CadastroFormState = {
  customName: "",
  brand: "",
  model: "",
  year: "",
  planId: "",
  color: "",
  fipeValue: "",
  chassis: "",
  notes: "",
  trackerSupplier: "",
  simCarrier: "",
  simIccid: "",
  simNumber: "",
  simApn: "",
  trackerBrand: "",
  trackerModel: "",
  trackerPrice: "",
  protocol: "",
  cameraImei: "",
  trackerType: "rastreador",
  installCompany: "",
  installTechnician: "",
  installedAt: "",
  installLocation: "",
  installWarranty: "",
  installNotes: "",
};

function formFromMetadata(
  metadata: import("@/types/vehicle-metadata").VehicleMetadata | undefined,
  vehicle: Vehicle,
): CadastroFormState {
  if (!metadata) return { ...emptyCadastroForm, customName: vehicle.name };
  return {
    customName: metadata.customName ?? vehicle.name,
    brand: metadata.brand ?? "",
    model: metadata.model ?? "",
    year: metadata.year != null ? String(metadata.year) : "",
    planId: metadata.planId ?? "",
    color: metadata.color ?? "",
    fipeValue: metadata.fipeValue ?? "",
    chassis: metadata.chassis ?? "",
    notes: metadata.notes ?? "",
    trackerSupplier: metadata.trackerSupplier ?? "",
    simCarrier: metadata.simCarrier ?? "",
    simIccid: metadata.simIccid ?? "",
    simNumber: metadata.simNumber ?? "",
    simApn: metadata.simApn ?? "",
    trackerBrand: metadata.trackerBrand ?? "",
    trackerModel: metadata.trackerModel ?? "",
    trackerPrice: metadata.trackerPrice ?? "",
    protocol: metadata.protocol ?? "",
    cameraImei: metadata.cameraImei ?? "",
    trackerType: metadata.trackerType ?? "rastreador",
    installCompany: metadata.installCompany ?? "",
    installTechnician: metadata.installTechnician ?? "",
    installedAt: metadata.installedAt ? metadata.installedAt.slice(0, 10) : "",
    installLocation: metadata.installLocation ?? "",
    installWarranty: metadata.installWarranty ?? "",
    installNotes: metadata.installNotes ?? "",
  };
}

// Traduz o erro de upload de imagem pra uma mensagem que faz sentido pro
// usuário — nunca o texto técnico bruto do backend (ex.:
// "FST_REQ_FILE_TOO_LARGE"/"request file too large", que pode vir tanto
// do limite do @fastify/multipart quanto do PayloadTooLargeError do
// StorageService, os dois como 413). Não muda em nada o limite real
// (STORAGE_MAX_FILE_SIZE_MB, backend) nem o fluxo de persistência — só a
// mensagem exibida.
export function describeImageUploadError(err: unknown): string {
  if (err instanceof GpsDriveApiError) {
    if (err.status === 413) {
      return "A imagem é muito grande. O tamanho máximo permitido é 5 MB.";
    }
    // 415 = StorageService#validateImage recusou o arquivo (mimetype fora
    // da allowlist, ou conteúdo que não bate com o mimetype declarado —
    // ver storage.constants.ts#IMAGE_SIGNATURES). O backend já manda uma
    // mensagem específica e segura de mostrar (nunca stack/código interno),
    // bem mais útil aqui do que o "tente novamente" genérico de antes —
    // pedido 2026-09-13: usuário via sempre a mesma mensagem opaca mesmo
    // com causas diferentes (formato não suportado vs. arquivo corrompido).
    if (err.status === 415) {
      return err.message || "Formato de imagem não suportado. Envie um JPEG, PNG, WEBP ou GIF.";
    }
  }
  return "Não foi possível enviar a imagem. Tente novamente.";
}

// null explícito para todo campo em branco — nunca manda string vazia (o
// backend trataria "" como um valor real, não como "sem valor").
function buildSavePayload(form: CadastroFormState) {
  const s = toNullableString;
  const n = toNullableNumber;
  return buildMetadataPayload({
    customName: s(form.customName),
    brand: s(form.brand),
    model: s(form.model),
    year: n(form.year),
    planId: s(form.planId),
    color: s(form.color),
    fipeValue: n(form.fipeValue),
    chassis: s(form.chassis),
    notes: s(form.notes),
    trackerSupplier: s(form.trackerSupplier),
    simCarrier: s(form.simCarrier),
    simIccid: s(form.simIccid),
    simNumber: s(form.simNumber),
    simApn: s(form.simApn),
    trackerBrand: s(form.trackerBrand),
    trackerModel: s(form.trackerModel),
    trackerPrice: n(form.trackerPrice),
    protocol: s(form.protocol),
    cameraImei: s(form.cameraImei),
    trackerType: s(form.trackerType),
    installCompany: s(form.installCompany),
    installTechnician: s(form.installTechnician),
    installedAt: s(form.installedAt),
    installLocation: s(form.installLocation),
    installWarranty: s(form.installWarranty),
    installNotes: s(form.installNotes),
  });
}

// Mesmos limites de updateVehicleMetadataDto (backend) — checados ANTES de
// mandar (2026-10-01): o backend devolvia 422 "String must contain at most
// 17 character(s)" sem dizer o campo, e o operador não entendia por que
// não salvou.
const CADASTRO_TEXT_LIMITS: Array<[keyof CadastroFormState, string, number, TabKey]> = [
  ["customName", "Descrição / Nome", 120, "principal"],
  ["model", "Modelo do veículo", 80, "principal"],
  ["brand", "Marca", 80, "principal"],
  ["color", "Cor do veículo", 40, "principal"],
  ["chassis", "Chassis", 17, "principal"],
  ["notes", "Observações", 2000, "principal"],
  ["trackerSupplier", "Fornecedor", 80, "hardware"],
  ["simCarrier", "Operadora", 80, "hardware"],
  ["simIccid", "ICCID", 30, "hardware"],
  ["simApn", "APN", 80, "hardware"],
  ["simNumber", "Número SIM", 30, "hardware"],
  ["trackerBrand", "Fabricante do rastreador", 80, "hardware"],
  ["trackerModel", "Modelo do rastreador", 80, "hardware"],
  ["installCompany", "Empresa da instalação", 120, "hardware"],
  ["installLocation", "Local da instalação", 200, "hardware"],
  ["installTechnician", "Instalador", 120, "hardware"],
  ["installWarranty", "Garantia", 120, "hardware"],
  ["installNotes", "Observações da instalação", 2000, "hardware"],
];

function validateCadastroForm(form: CadastroFormState): { message: string; tab: TabKey } | null {
  for (const [key, label, max, tab] of CADASTRO_TEXT_LIMITS) {
    const value = String(form[key] ?? "").trim();
    if (value.length > max) {
      return {
        message: `"${label}" aceita no máximo ${max} caracteres (tem ${value.length}).`,
        tab,
      };
    }
  }
  const year = String(form.year ?? "").trim();
  if (year && !(Number.isInteger(Number(year)) && Number(year) >= 1900 && Number(year) <= 2100)) {
    return { message: '"Ano" deve ser um número entre 1900 e 2100.', tab: "principal" };
  }
  for (const [key, label] of [
    ["fipeValue", "Valor FIPE"],
    ["trackerPrice", "Valor do equipamento"],
  ] as const) {
    const raw = String(form[key] ?? "").trim();
    if (raw && !(Number(raw) >= 0)) {
      return { message: `"${label}" deve ser um número positivo.`, tab: key === "fipeValue" ? "principal" : "hardware" };
    }
  }
  const cameraImei = String(form.cameraImei ?? "").trim();
  if (cameraImei && !/^\d{5,21}$/.test(cameraImei)) {
    return { message: '"IMEI da câmera" deve ter só números (5 a 21 dígitos).', tab: "hardware" };
  }
  return null;
}

// Corpo de formulário único, usado tanto por "Cadastrar veículo" quanto por
// "Editar dispositivo" — vehicle=null é o único sinal de modo cadastro.
// Antes desta consolidação existiam duas implementações completas e
// independentes (VehicleFormDialog fazia o cadastro real mas nunca era
// alcançado em modo edição; VehicleEditPanel fazia a edição real mas não
// sabia criar um device) — ver VEHICLE_FORM_ARCHITECTURE.md §0/§8.1.
//
// Pedido 2026-09-24: conta cliente (accountType "usuario", titular ou
// sub-usuário) com devices:edit só pode renomear o dispositivo — nunca
// placa/IMEI/hardware/plano/ícone/alertas. Vale pro web e pro PWA, que
// reaproveita este mesmo VehicleEditDialog (ver mobile-app.tsx).
function VehicleFormPanel(props: {
  vehicle: Vehicle | null;
  onClose: () => void;
  onSaved?: (id: string) => void;
}) {
  const nameOnly = useIsNameOnlyVehicleEditor();
  if (props.vehicle && nameOnly) {
    return <VehicleRenamePanel vehicle={props.vehicle} onClose={props.onClose} onSaved={props.onSaved} />;
  }
  return <VehicleFullFormPanel {...props} />;
}

function VehicleRenamePanel({
  vehicle,
  onClose,
  onSaved,
}: {
  vehicle: Vehicle;
  onClose: () => void;
  onSaved?: (id: string) => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(vehicle.name);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const trimmed = name.trim();

  const handleSave = async () => {
    if (!trimmed) return;
    if (trimmed === vehicle.name) {
      onClose();
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      // Pelo backend, nunca PUT direto no Traccar: o cliente é
      // deviceReadonly lá (ver renameDevice).
      await vehicleMetadataService.renameDevice(vehicle.id, trimmed);
      patchVehicleInCache(queryClient, vehicle.id, { name: trimmed });
      onSaved?.(vehicle.id);
      onClose();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Erro ao renomear o veículo.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="flex items-center gap-3 border-b border-border bg-background/40 px-6 py-4">
        <div className="flex h-11 w-11 items-center justify-center rounded-lg border border-border bg-background">
          <Truck className="h-5 w-5" strokeWidth={1.75} />
        </div>
        <div className="min-w-0">
          <DialogTitle className="truncate text-base">{`Editar dispositivo — ${vehicle.name}`}</DialogTitle>
          <DialogDescription className="mt-0.5 flex items-center gap-1.5 font-mono text-[11px]">
            {vehicle.plate && <LicensePlate plate={vehicle.plate} size="xs" />}ID {vehicle.id}
          </DialogDescription>
        </div>
      </div>
      <form
        className="flex flex-col gap-2 p-6"
        onSubmit={(e) => {
          e.preventDefault();
          void handleSave();
        }}
      >
        <label htmlFor="vehicle-rename-input" className="text-sm font-medium">
          Nome do dispositivo
        </label>
        <Input
          id="vehicle-rename-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
          maxLength={128}
        />
        {saveError && <p className="text-xs font-medium text-red-600">{saveError}</p>}
        <div className="mt-4 flex justify-end gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button type="submit" size="sm" disabled={saving || !trimmed}>
            {saving ? "Salvando…" : "Salvar"}
          </Button>
        </div>
      </form>
    </>
  );
}

function VehicleFullFormPanel({
  vehicle,
  onClose,
  onSaved,
}: {
  vehicle: Vehicle | null;
  onClose: () => void;
  onSaved?: (id: string) => void;
}) {
  const isCreate = vehicle === null;
  // Bug real 2026-09-10: a aba "Excluir" (TabExcluir, exclui o dispositivo
  // no Traccar) aparecia pra qualquer usuário, sem checar devices:delete —
  // só isCreate escondia (ver filtro de TABS abaixo). Mesma classe de
  // brecha já corrigida em geofences.tsx no mesmo dia.
  const canDeleteDevices = usePermission("devices", "delete");
  const queryClient = useQueryClient();
  // Isolamento multiempresa 2026-08-16 (ver relatório da etapa): nunca
  // useUsers() aqui — esse lista todo mundo direto do Traccar sem filtro
  // de empresa. useLinkableUsers() já devolve só quem é da minha empresa +
  // quem ainda não foi provisionado por ninguém.
  const { data: users } = useLinkableUsers();
  const { data: metadata, isLoading: metadataLoading } = useVehicleMetadata(vehicle?.id);
  const updateMetadata = useUpdateVehicleMetadata();

  const [tab, setTab] = useState<TabKey>("principal");

  // Pré-carrega a aba Alertas assim que o diálogo abre (ver
  // prefetchVehicleAlerts) — antes ela só começava a buscar no clique.
  const alertsQueryClient = useQueryClient();
  useEffect(() => {
    if (vehicle?.id) prefetchVehicleAlerts(alertsQueryClient, Number(vehicle.id));
  }, [alertsQueryClient, vehicle?.id]);

  const [form, setForm] = useState<CadastroFormState>(() =>
    vehicle ? formFromMetadata(undefined, vehicle) : { ...emptyCadastroForm },
  );
  useEffect(() => {
    setForm(vehicle ? formFromMetadata(metadata, vehicle) : { ...emptyCadastroForm });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metadata?.id, vehicle?.id]);
  const patchForm = (patch: Partial<CadastroFormState>) => setForm((f) => ({ ...f, ...patch }));

  // Nome/Placa/IMEI são sempre do Traccar, nunca vehicle_metadata. Em modo
  // cadastro viram o POST /api/devices; em modo edição viram PUT
  // /api/devices/:id (ver updateDeviceUniqueIdInTraccar/updateAttributes em
  // vehiclesService, chamados no handleSave abaixo). Placa/IMEI eram
  // só-leitura em edição até 2026-09-15 — pedido do usuário depois de achar
  // um tag cadastrado com o IMEI sem os zeros à esquerda que o próprio
  // aparelho manda, sem nenhum jeito de corrigir pela tela.
  const [name, setName] = useState(vehicle?.name ?? "");
  const [plate, setPlate] = useState(vehicle?.plate ?? "");
  const [imei, setImei] = useState(vehicle?.imei ?? "");
  const [salesClientUserId, setSalesClientUserId] = useState<number | null>(null);
  // Nome digitado quando form.protocol === CUSTOM_PROTOCOL_VALUE (opção
  // "Outro" do Select) — nunca vai pro payload direto, só serve de entrada
  // pro useQuickCreateTrackerProtocol no handleSave (ver CUSTOM_PROTOCOL_VALUE).
  const [customProtocolLabel, setCustomProtocolLabel] = useState("");
  const quickCreateProtocol = useQuickCreateTrackerProtocol();

  const [iconKey, setIconKey] = useState<VehicleModelKey>(
    vehicle ? resolveVehicleIcon(vehicle).model.key : resolveVehicleModel(undefined, "car").key,
  );
  const [iconColorMode, setIconColorMode] = useState<"auto" | "fixed">(vehicle?.iconColorMode ?? "auto");
  const [mapIconStyle, setMapIconStyle] = useState<"model" | "photo" | "real">(vehicle?.mapIconStyle ?? "model");
  const [iconColor, setIconColor] = useState<VehicleColorKey>(
    (vehicle?.iconColor as VehicleColorKey) ?? DEFAULT_VEHICLE_COLOR,
  );
  const onIconChange = async (
    patch: Partial<{ iconKey: VehicleModelKey; iconColorMode: "auto" | "fixed"; iconColor: VehicleColorKey; mapIconStyle: "model" | "photo" | "real" }>,
  ) => {
    const next = {
      iconKey: patch.iconKey ?? iconKey,
      iconColorMode: patch.iconColorMode ?? iconColorMode,
      iconColor: patch.iconColor ?? iconColor,
      mapIconStyle: patch.mapIconStyle ?? mapIconStyle,
    };
    if (patch.mapIconStyle) setMapIconStyle(patch.mapIconStyle);
    if (patch.iconKey) setIconKey(patch.iconKey);
    if (patch.iconColorMode) setIconColorMode(patch.iconColorMode);
    if (patch.iconColor) setIconColor(patch.iconColor);
    if (vehicle) {
      // Edição: grava de verdade no device do Traccar (attributes) assim que
      // o usuário muda o ícone — vale em qualquer navegador. Sem API_URL
      // (modo demo), cai no override local.
      const updated = await vehiclesService.updateIconConfig(vehicle.id, next).catch(() => undefined);
      if (!updated) setOverride(vehicle.id, { iconKey: next.iconKey });
      void queryClient.invalidateQueries({ queryKey: ["vehicles"] });
    }
    // Cadastro: o ícone só é gravado no Traccar quando o dispositivo é criado (ver handleSave).
  };

  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null);
  const [imagePreviewDataUrl, setImagePreviewDataUrl] = useState<string | undefined>(undefined);
  const [imageUploading, setImageUploading] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const onPickImage = async (rawFile?: File) => {
    if (!rawFile) return;
    // eslint-disable-next-line no-console -- instrumentação temporária,
    // remover depois de achada a causa. Só metadados do arquivo, nunca
    // conteúdo.
    console.log("[IMAGE] arquivo selecionado", {
      name: rawFile.name,
      size: rawFile.size,
      type: rawFile.type,
    });
    setImageError(null);
    const file = rawFile;
    if (isCreate) {
      // Ainda não existe deviceId antes do POST /api/devices — guarda o
      // arquivo e só envia depois que handleSave criar o dispositivo.
      setPendingImageFile(file);
      const reader = new FileReader();
      reader.onload = () => setImagePreviewDataUrl(String(reader.result));
      reader.readAsDataURL(file);
      return;
    }
    // eslint-disable-next-line no-console
    console.log("[IMAGE] iniciando upload", { vehicleId: vehicle!.id });
    setImageUploading(true);
    try {
      // A resposta do upload já é o registro completo e persistido (banco
      // + storage confirmados no servidor, ver
      // vehicle-metadata.service.ts#uploadImageForField) — gravar direto
      // no cache com setQueryData faz o preview refletir o estado real
      // gravado imediatamente, sem depender de um refetch em segundo
      // plano ainda não ter chegado (a causa raiz do "aparece e some").
      const updated = await vehicleMetadataService.uploadImage(vehicle!.id, file);
      // eslint-disable-next-line no-console
      console.log("[IMAGE] uploadImage() resolveu", {
        hasImageKey: !!updated?.imageKey,
        hasImageUrls: !!updated?.imageUrls,
        imageUrlsKeys: updated?.imageUrls ? Object.keys(updated.imageUrls) : null,
      });
      // Cancela qualquer GET /vehicle-metadata/:id ainda em voo para esta
      // mesma chave (ex.: o fetch inicial disparado quando o diálogo
      // abriu, se o usuário for rápido ao escolher o arquivo) — sem isso,
      // uma resposta mais antiga chegando DEPOIS do setQueryData abaixo
      // sobrescreve silenciosamente o dado fresco com o estado de antes
      // do upload (causa raiz real do "nada muda"). setQueryData não tem
      // proteção automática contra isso, ao contrário de um refetch normal.
      await queryClient.cancelQueries({ queryKey: ["vehicle-metadata", vehicle!.id] });
      queryClient.setQueryData(["vehicle-metadata", vehicle!.id], updated);
      // eslint-disable-next-line no-console
      console.log("[IMAGE] setQueryData executado", { queryKey: ["vehicle-metadata", vehicle!.id] });
      // A foto antiga é apagada do storage assim que a nova é gravada (ver
      // vehicle-metadata.service.ts#uploadImageForField) — sem invalidar
      // esta query também, o sidebar da frota (useVehicleImages) continua
      // servindo a imageUrl antiga em cache, agora um 404 real.
      await queryClient.invalidateQueries({ queryKey: ["vehicle-images"] });
      // eslint-disable-next-line no-console
      console.log("[IMAGE] upload concluído");
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log("[IMAGE] upload falhou", err);
      // Falha real (ex.: arquivo maior que STORAGE_MAX_FILE_SIZE_MB) —
      // nunca silenciosa: fica visível aqui embaixo do botão até o
      // próximo pick/upload bem-sucedido. Mensagem amigável, nunca o
      // texto técnico bruto do backend (ver describeImageUploadError).
      setImageError(describeImageUploadError(err));
    } finally {
      setImageUploading(false);
      // eslint-disable-next-line no-console
      console.log("[IMAGE] setImageUploading(false) executado");
    }
  };
  const imagePreview =
    imagePreviewDataUrl ??
    (metadata?.imageUrls
      ? resolveGpsDriveUrl(metadata.imageUrls["256"] ?? metadata.imageUrls.original)
      : undefined);
  // eslint-disable-next-line no-console -- instrumentação temporária: o
  // que o RENDER de fato calcula como imagePreview a cada mudança, pra
  // separar "o dado chegou no cache" de "o componente usou o dado".
  useEffect(() => {
    console.log("[IMAGE] imagePreview recalculado", {
      imagePreview,
      metadataImageKey: metadata?.imageKey ?? null,
      hasMetadataImageUrls: !!metadata?.imageUrls,
    });
  }, [imagePreview, metadata?.imageKey, metadata?.imageUrls]);

  const [pendingInstallImageFile, setPendingInstallImageFile] = useState<File | null>(null);
  const [installImagePreviewDataUrl, setInstallImagePreviewDataUrl] = useState<string | undefined>(undefined);
  const [installImageUploading, setInstallImageUploading] = useState(false);
  const [installImageError, setInstallImageError] = useState<string | null>(null);
  const onPickInstallImage = async (file?: File) => {
    if (!file) return;
    setInstallImageError(null);
    if (isCreate) {
      setPendingInstallImageFile(file);
      const reader = new FileReader();
      reader.onload = () => setInstallImagePreviewDataUrl(String(reader.result));
      reader.readAsDataURL(file);
      return;
    }
    setInstallImageUploading(true);
    try {
      await vehicleMetadataService.uploadInstallImage(vehicle!.id, file);
      await queryClient.invalidateQueries({ queryKey: ["vehicle-metadata", vehicle!.id] });
    } catch (err) {
      setInstallImageError(err instanceof Error ? err.message : "Erro ao enviar a imagem.");
    } finally {
      setInstallImageUploading(false);
    }
  };
  const installImagePreview =
    installImagePreviewDataUrl ??
    (metadata?.installImageUrls
      ? resolveGpsDriveUrl(metadata.installImageUrls["256"] ?? metadata.installImageUrls.original)
      : undefined);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const handleSave = async () => {
    const title = form.customName.trim() || name.trim();
    if (isCreate) {
      // Pedido 2026-09-01: placa NÃO é mais obrigatória para cadastrar —
      // só nome (título) e, com backend real configurado, IMEI (exigido
      // pelo próprio Traccar como uniqueId, ver create() abaixo).
      if (!title) return;
      if (API_URL && !imei.trim()) {
        setSaveError("Informe o IMEI do rastreador na aba Hardware e Simcard para cadastrar o dispositivo.");
        setTab("hardware");
        return;
      }
    }

    // Pedido 2026-09-16: protocolo deixou de ser obrigatório (era desde
    // 2026-09-07) — só valida se "Outro" foi escolhido sem digitar nada,
    // porque aí "__custom__" (sentinela, ver CUSTOM_PROTOCOL_VALUE) acabaria
    // gravado como se fosse um protocolo de verdade em vehicle_metadata.
    if (form.protocol === CUSTOM_PROTOCOL_VALUE && !customProtocolLabel.trim()) {
      setSaveError("Digite o nome do protocolo (ou escolha um da lista) na aba Hardware e Simcard.");
      setTab("hardware");
      return;
    }

    const invalid = validateCadastroForm(form);
    if (invalid) {
      setSaveError(invalid.message);
      setTab(invalid.tab);
      return;
    }

    setSaving(true);
    setSaveError(null);
    setImageError(null);
    // setSaveError() é assíncrono — a variável de estado `saveError` capturada
    // neste closure não reflete chamadas feitas mais abaixo dentro desta MESMA
    // execução, por isso rastreamos aqui em vez de reler `saveError` no final.
    let hasSaveError = false;

    // "Outro" (CUSTOM_PROTOCOL_VALUE): cadastra (ou reaproveita, se já
    // existir) o protocolo digitado no catálogo global — a partir daqui ele
    // já aparece em Administração > Comandos GT06 pra alguém adicionar os
    // comandos depois. `resolvedProtocol` é o que de fato vai pro payload;
    // `form.protocol` só é atualizado depois, se a chamada der certo, pra
    // não perder o texto digitado numa falha de rede.
    let resolvedProtocol = form.protocol;
    if (form.protocol === CUSTOM_PROTOCOL_VALUE) {
      try {
        const created = await quickCreateProtocol.mutateAsync(customProtocolLabel.trim());
        resolvedProtocol = created.value;
        patchForm({ protocol: created.value });
        setCustomProtocolLabel("");
      } catch (err) {
        setSaveError(err instanceof Error ? err.message : "Erro ao cadastrar o protocolo.");
        setSaving(false);
        setTab("hardware");
        return;
      }
    }

    let vehicleId: string;
    if (isCreate && API_URL) {
      // Cadastro real: cria o dispositivo no Traccar (GET /api/devices vai
      // passar a listá-lo).
      try {
        const created = await vehiclesService.create({
          name: title,
          uniqueId: form.trackerType === "tag" ? normalizeTagImei(imei) : imei.trim(),
          attributes: mergeIconAttributes(
            {
              plate: plate.trim().toUpperCase(),
            },
            { iconKey, iconColorMode, iconColor, mapIconStyle },
          ),
        });
        vehicleId = created.id;
        if (salesClientUserId != null) {
          try {
            // PUT troca a lista inteira — soma aos vínculos que já existem.
            const current = await vehiclePermissionsService.usersForVehicle(Number(vehicleId));
            const ids = new Set([...current.map((u) => u.id), salesClientUserId]);
            await vehiclePermissionsService.syncVehicleUsers(Number(vehicleId), [...ids]);
          } catch (err) {
            setSaveError(err instanceof Error ? `Veículo criado, mas não foi vinculado ao cliente: ${err.message}` : "Veículo criado, mas não foi vinculado ao cliente.");
            hasSaveError = true;
          }
        }
        void queryClient.invalidateQueries({ queryKey: ["vehicles"] });
      } catch (err) {
        if (err instanceof ApiError && err.message.includes("uniqueid")) {
          setSaveError(
            `Este IMEI (${imei.trim()}) já está cadastrado em outro veículo. Use um IMEI diferente ou edite o veículo existente.`,
          );
        } else {
          setSaveError(err instanceof Error ? err.message : "Erro ao cadastrar dispositivo.");
        }
        setSaving(false);
        return;
      }
    } else if (isCreate) {
      // Modo demo (sem VITE_API_URL configurada): mantém o cadastro local.
      vehicleId = `local-${Date.now()}`;
      addExtra({
        id: vehicleId,
        name: title,
        plate: plate.trim().toUpperCase(),
        status: "online",
        iconKey,
        iconColorMode,
        iconColor,
        speed: 0,
        lat: -30.03,
        lng: -51.2,
        lastConnection: new Date().toISOString(),
      });
    } else {
      vehicleId = vehicle!.id;
      // "Descrição / Nome" é o título exibido do veículo em todo o app
      // (mapa, listas, PWA) — mas esse nome de verdade é o device.name do
      // Traccar, nunca vehicle_metadata (ver comentário acima em `name`).
      // Sem isto, o campo salvava só em vehicle_metadata.customName (que
      // nada mais lê) e a mudança "sumia" em qualquer outra tela.
      if (API_URL && title !== vehicle!.name) {
        try {
          await vehiclesService.updateName(vehicleId, title);
          patchVehicleInCache(queryClient, vehicleId, { name: title });
        } catch (err) {
          setSaveError(err instanceof Error ? err.message : "Erro ao renomear o veículo.");
          hasSaveError = true;
        }
      }
      if (API_URL && salesClientUserId != null) {
        try {
          const current = await vehiclePermissionsService.usersForVehicle(Number(vehicleId));
          if (!current.some((u) => u.id === salesClientUserId)) {
            await vehiclePermissionsService.syncVehicleUsers(Number(vehicleId), [
              ...current.map((u) => u.id),
              salesClientUserId,
            ]);
            void queryClient.invalidateQueries({ queryKey: queryKeys.vehicleUsers(Number(vehicleId)) });
          }
        } catch (err) {
          setSaveError(err instanceof Error ? `Não foi possível vincular ao cliente: ${err.message}` : "Não foi possível vincular ao cliente.");
          hasSaveError = true;
        }
      }
      const trimmedPlate = plate.trim().toUpperCase();
      if (API_URL && trimmedPlate !== (vehicle!.plate ?? "")) {
        try {
          await vehiclesService.updateAttributes(vehicleId, { plate: trimmedPlate });
          patchVehicleInCache(queryClient, vehicleId, { plate: trimmedPlate });
        } catch (err) {
          setSaveError(err instanceof Error ? err.message : "Erro ao salvar a placa.");
          hasSaveError = true;
        }
      }
      const trimmedImei = form.trackerType === "tag" ? normalizeTagImei(imei) : imei.trim();
      if (API_URL && trimmedImei && trimmedImei !== (vehicle!.imei ?? "")) {
        try {
          await vehiclesService.updateUniqueId(vehicleId, trimmedImei);
          patchVehicleInCache(queryClient, vehicleId, { imei: trimmedImei });
        } catch (err) {
          if (err instanceof ApiError && err.message.includes("uniqueid")) {
            setSaveError(
              `Este IMEI (${trimmedImei}) já está cadastrado em outro veículo. Use um IMEI diferente.`,
            );
          } else {
            setSaveError(err instanceof Error ? err.message : "Erro ao salvar o IMEI.");
          }
          hasSaveError = true;
        }
      }
    }

    // Cadastro real (marca/modelo/ano/rastreador/instalação/imagem) — sempre
    // no GPS Drive Backend, nunca em localStorage (ver
    // src/lib/vehicle-cadastro-fields.ts).
    const payload = buildSavePayload({ ...form, customName: title, protocol: resolvedProtocol });
    const saved: string[] = [];
    const failed: string[] = [];
    try {
      if (isCreate) {
        await vehicleMetadataService.update(vehicleId, payload);
      } else {
        await updateMetadata.mutateAsync({ deviceId: vehicleId, patch: payload });
      }
      saved.push(...Object.keys(payload));
    } catch (err) {
      failed.push(...Object.keys(payload));
      setSaveError(err instanceof Error ? err.message : "Erro ao salvar o cadastro do veículo.");
      hasSaveError = true;
    }

    if (isCreate && pendingImageFile) {
      try {
        // Mesmo motivo do onPickImage: a resposta já é o registro
        // persistido de verdade — grava direto no cache da chave que o
        // form vai usar quando este veículo for reaberto para edição, sem
        // depender de um refetch de rede acontecer antes do reabrir (a
        // causa raiz do "aparece no preview e depois some ao reabrir").
        const updated = await vehicleMetadataService.uploadImage(vehicleId, pendingImageFile);
        // Mesmo motivo do cancelQueries em onPickImage — nesta chave
        // específica (vehicleId recém-criado) normalmente não há nada em
        // voo ainda, mas custa nada garantir o mesmo padrão nos dois
        // lugares que fazem setQueryData.
        await queryClient.cancelQueries({ queryKey: ["vehicle-metadata", vehicleId] });
        queryClient.setQueryData(["vehicle-metadata", vehicleId], updated);
        saved.push("vehicleImage");
        void queryClient.invalidateQueries({ queryKey: ["vehicle-images"] });
      } catch (err) {
        failed.push("vehicleImage");
        // Antes só aparecia no relatório de salvamento (texto pequeno,
        // fácil de não notar) — agora também fica visível junto do campo
        // de imagem, mesmo tratamento do modo edição. Mensagem amigável,
        // nunca o texto técnico bruto (ver describeImageUploadError).
        setImageError(describeImageUploadError(err));
      }
    }
    if (isCreate && pendingInstallImageFile) {
      try {
        await vehicleMetadataService.uploadInstallImage(vehicleId, pendingInstallImageFile);
        saved.push("installImage");
      } catch {
        failed.push("installImage");
      }
    }

    void queryClient.invalidateQueries({ queryKey: ["vehicle-metadata", vehicleId] });
    setSaving(false);
    // Pedido 2026-09-01 ("tira isso"): fecha direto, sem o popup "Resultado
    // do salvamento" que antes exigia um clique extra em "Ok" pra sair —
    // era o mecanismo de nunca-sucesso-silencioso (ver VehicleSaveReport),
    // mas na prática só atrapalhava. Falha real ainda aparece (saveError,
    // texto vermelho ao lado do botão), só não bloqueia mais o fechamento.
    if (failed.length > 0 && !hasSaveError) {
      setSaveError("Alguns campos não foram salvos — tente novamente.");
      hasSaveError = true;
    }
    onSaved?.(vehicleId);
    // 2026-10-01: edição também fecha ao salvar sem erro — antes só o
    // cadastro fechava, e na edição o popup ficava aberto sem nenhum sinal
    // de que tinha salvo (operador achava que não gravou). Com erro, fica
    // aberto com a mensagem em destaque (faixa vermelha abaixo do topo).
    if (!hasSaveError) onClose();
  };

  const tabProps: VehicleFormTabProps = {
    mode: isCreate ? "create" : "edit",
    vehicle,
    form,
    onChange: patchForm,
    name,
    onNameChange: setName,
    plate,
    onPlateChange: setPlate,
    imei,
    onImeiChange: setImei,
    customProtocolLabel,
    onCustomProtocolLabelChange: setCustomProtocolLabel,
    iconKey,
    iconColorMode,
    iconColor,
    mapIconStyle,
    onIconChange,
    imagePreview,
    onPickImage,
    imageUploading,
    imageError,
    users,
    installImagePreview,
    onPickInstallImage,
    installImageUploading,
    installImageError,
    onSalesClientChange: setSalesClientUserId,
  };

  return (
    <>
      {/* header */}
      <div className="flex items-center justify-between border-b border-border bg-background/40 px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-lg border border-border bg-background">
            <Truck className="h-5 w-5" strokeWidth={1.75} />
          </div>
          <div>
            <DialogTitle className="flex items-center gap-2 text-base">
              {isCreate ? "Cadastrar veículo" : `Editar dispositivo — ${vehicle!.name}`}
            </DialogTitle>
            <DialogDescription className="mt-0.5 font-mono text-[11px]">
              {isCreate ? "Novo dispositivo" : `${vehicle!.plate} · ID ${vehicle!.id}`}
            </DialogDescription>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={saving || metadataLoading || (isCreate && (!form.customName.trim() && !name.trim()))}
          >
            {saving ? (isCreate ? "Cadastrando…" : "Salvando…") : isCreate ? "Cadastrar" : "Salvar alterações"}
          </Button>
        </div>
      </div>

      {saveError && (
        <div
          role="alert"
          className="flex items-start gap-2 border-b border-red-200 bg-red-50 px-6 py-2.5 text-sm font-medium text-red-700 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-300"
        >
          <span aria-hidden>⚠️</span>
          <span>Não foi salvo: {saveError}</span>
        </div>
      )}

      <GpsDriveSessionBanner />

      {/* horizontal tabs */}
      <nav className="flex items-center gap-1 overflow-x-auto border-b border-border bg-background/40 px-4">
        {TABS.filter((t) => {
          if (t.key === "excluir") return !isCreate && canDeleteDevices;
          if (t.key === "alertas" || t.key === "logs") return !isCreate;
          return true;
        }).map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={cn(
              "relative whitespace-nowrap px-4 py-3 text-sm transition-colors",
              tab === t.key
                ? t.key === "excluir"
                  ? "text-rose-600 dark:text-rose-400"
                  : "text-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            <span className="inline-flex items-center gap-1.5">
              {t.emoji && <span aria-hidden>{t.emoji}</span>}
              {t.label}
            </span>
            {tab === t.key && (
              <span className="absolute inset-x-3 -bottom-px h-0.5 rounded-full bg-foreground" />
            )}
          </button>
        ))}
      </nav>

      <div className="flex min-h-0 flex-1">
        {/* content */}
        <div className="min-h-0 flex-1 overflow-y-auto p-6">
          {tab === "principal" && <TabPrincipal {...tabProps} />}
          {tab === "hardware" && <TabHardware {...tabProps} />}
          {tab === "alertas" && !isCreate && <TabAlertasEdit vehicle={vehicle!} />}
          {tab === "logs" && !isCreate && <TabLogs vehicle={vehicle!} />}
          {tab === "excluir" && !isCreate && canDeleteDevices && (
            <TabExcluir vehicle={vehicle!} onDeleted={onClose} />
          )}
        </div>
      </div>
    </>
  );
}

/* ---------- shared form pieces ---------- */
// Section/Field/TxtInput/InfoRow vivem em components/vehicle-form-fields.tsx
// — importadas no topo do arquivo, não redefinidas aqui.

function TxtSelect({
  children,
  ...props
}: React.ComponentProps<"select">) {
  return (
    <select
      {...props}
      className={cn(
        "flex h-9 w-full rounded-md border border-border bg-background px-3 text-sm outline-none transition-colors focus:border-foreground/40",
        props.className,
      )}
    >
      {children}
    </select>
  );
}

function Toggle({
  label,
  description,
  defaultChecked,
}: {
  label: string;
  description?: string;
  defaultChecked?: boolean;
}) {
  const [on, setOn] = useState(!!defaultChecked);
  return (
    <button
      type="button"
      onClick={() => setOn((v) => !v)}
      className="flex w-full items-center justify-between rounded-lg border border-border bg-background/60 px-3 py-2.5 text-left transition-colors hover:bg-accent/50"
    >
      <div className="min-w-0 pr-3">
        <div className="text-sm font-medium">{label}</div>
        {description && <div className="mt-0.5 text-[11px] text-muted-foreground">{description}</div>}
      </div>
      <span
        className={cn(
          "relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors",
          on ? "bg-foreground" : "bg-border",
        )}
      >
        <span
          className={cn(
            "inline-block h-4 w-4 transform rounded-full bg-background transition-transform",
            on ? "translate-x-[18px]" : "translate-x-0.5",
          )}
        />
      </span>
    </button>
  );
}

/* ---------- tabs ---------- */

// Props compartilhadas por TabPrincipal/TabHardware nos dois modos (cadastro
// e edição) — todo o estado de verdade vive em VehicleFormPanel; as abas só
// renderizam e disparam callbacks, sem hooks de dado próprios.
interface VehicleFormTabProps {
  mode: "create" | "edit";
  vehicle: Vehicle | null;
  form: CadastroFormState;
  onChange: (patch: Partial<CadastroFormState>) => void;
  name: string;
  onNameChange: (v: string) => void;
  plate: string;
  onPlateChange: (v: string) => void;
  imei: string;
  onImeiChange: (v: string) => void;
  customProtocolLabel: string;
  onCustomProtocolLabelChange: (v: string) => void;
  iconKey: VehicleModelKey;
  iconColorMode: "auto" | "fixed";
  iconColor: VehicleColorKey;
  mapIconStyle: "model" | "photo" | "real";
  onIconChange: (
    patch: Partial<{ iconKey: VehicleModelKey; iconColorMode: "auto" | "fixed"; iconColor: VehicleColorKey; mapIconStyle: "model" | "photo" | "real" }>,
  ) => void;
  imagePreview: string | undefined;
  onPickImage: (file?: File) => void;
  imageUploading: boolean;
  imageError: string | null;
  users: LinkableUser[] | undefined;
  installImagePreview: string | undefined;
  onPickInstallImage: (file?: File) => void;
  installImageUploading: boolean;
  installImageError: string | null;
  // Cadastro: cliente (Traccar) do pré-contrato escolhido em "Puxar dados
  // de Vendas" — o veículo criado é vinculado a ele no handleSave.
  onSalesClientChange: (traccarUserId: number | null) => void;
}

const norm = (s: string) =>
  s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

// "GM/CELTA" ou "Chevrolet Celta" → marca + modelo; sem marca conhecida,
// tudo vira modelo (o formulário de vendas só tem um campo "modelo").
const KNOWN_BRANDS = [
  "chevrolet", "gm", "volkswagen", "vw", "fiat", "ford", "renault", "toyota", "honda", "hyundai",
  "nissan", "peugeot", "citroen", "jeep", "mitsubishi", "kia", "yamaha", "suzuki", "bmw",
  "mercedes", "mercedes-benz", "audi", "chery", "caoa", "byd", "volvo", "scania", "iveco", "dafra", "shineray",
];
function splitBrandModel(raw: string): { brand: string; model: string } {
  const text = raw.trim();
  const slash = text.match(/^([^/]+)\/(.+)$/);
  if (slash) return { brand: slash[1].trim(), model: slash[2].trim() };
  const [first, ...rest] = text.split(/\s+/);
  if (rest.length && KNOWN_BRANDS.includes(norm(first))) return { brand: first, model: rest.join(" ") };
  return { brand: "", model: text };
}

function TabPrincipal(props: VehicleFormTabProps) {
  const {
    mode,
    vehicle,
    form,
    onChange,
    name,
    onNameChange,
    plate,
    onPlateChange,
    iconKey,
    iconColorMode,
    iconColor,
    mapIconStyle,
    onIconChange,
    imagePreview,
    onPickImage,
    imageUploading,
    imageError,
    users,
    onSalesClientChange,
  } = props;
  const isCreate = mode === "create";
  const fileRef = useRef<HTMLInputElement>(null);

  // Mesmo mecanismo de VehicleRow (sidebar da frota): se a foto real falhar
  // ao carregar, cai no placeholder "Sem imagem" em vez do ícone de imagem
  // quebrada do navegador. Reseta sempre que imagePreview muda (nova
  // seleção de arquivo, upload concluído, ou troca de veículo em edição) —
  // nunca dispara onError de novo sozinho, sem loop.
  const [photoFailed, setPhotoFailed] = useState(false);
  useEffect(() => setPhotoFailed(false), [imagePreview]);

  // Vínculo usuário↔veículo — Frontend → GPS Drive Backend → Traccar (ver
  // relatório da etapa). Mesma relação/backend que Usuários → Veículos em
  // routes/users.tsx (useDevicePermissions/useSyncUserDevicePermissions);
  // só o sentido da consulta muda. Multi-select real (reflete o vínculo
  // many-to-many do Traccar) — cada toggle já sincroniza na hora, sem
  // botão "Salvar" próprio (o form principal do veículo tem o dele, mas
  // esse vínculo não depende dele: cada toggle é um link/unlink isolado e
  // rápido no Traccar).
  const deviceId = !isCreate && vehicle ? Number(vehicle.id) : undefined;
  const { data: linkedTraccarUsers, isLoading: loadingLinkedUsers } = useVehicleUsers(deviceId);
  const syncVehicleUsers = useSyncVehicleUsers();

  // Plano (Administração > Planos) — listar é de toda a equipe
  // (admin/funcionário, GET /admin/plans em plans.controller.ts, liberado
  // 2026-10-14); cliente/motorista não busca nem vê o campo.
  const nameOnlyEditor = useIsNameOnlyVehicleEditor();
  const canManagePlans = !nameOnlyEditor;
  const { data: plans } = usePlans(canManagePlans);
  const [userQuery, setUserQuery] = useState("");

  const applySalesPick = (pick: SalesVehiclePick | null) => {
    onSalesClientChange(pick?.lead.user?.traccarUserId ?? null);
    if (!pick) return;
    const { lead, vehicle: v } = pick;
    const { brand, model } = splitBrandModel(v.model);
    const plateValue = v.plate.trim().toUpperCase();
    const title = [model.toUpperCase(), plateValue].filter(Boolean).join(" - ");
    const planName = norm(lead.data.plan);
    const plan = planName
      ? (plans ?? []).find((p) => norm(p.name) === planName) ??
        (plans ?? []).find((p) => norm(p.name).includes(planName) || planName.includes(norm(p.name)))
      : undefined;
    const yearMatch = v.year.match(/\d{4}/);
    // Só os dados do veículo/cliente — nunca IMEI, chip, rastreador ou
    // instalação; campo vazio no pré-contrato não apaga o que já existe.
    onChange({
      ...(title ? { customName: title } : {}),
      ...(model ? { model: model.toUpperCase() } : {}),
      ...(brand ? { brand: brand.toUpperCase() } : {}),
      ...(yearMatch ? { year: yearMatch[0] } : {}),
      ...(v.color.trim() ? { color: v.color.trim() } : {}),
      ...(plan ? { planId: plan.id } : {}),
    });
    if (title) onNameChange(title);
    if (plateValue) onPlateChange(plateValue);
  };
  // "Puxar dados da Agenda" (2026-10-14): veículo digitado à mão num
  // agendamento. Mesmas regras do de Vendas — só dados do veículo, campo
  // vazio não apaga o que já existe, cliente com usuário é vinculado ao salvar.
  const applyAgendaPick = (pick: AgendaManualVehicle | null) => {
    onSalesClientChange(pick?.clientUserId ?? null);
    if (!pick) return;
    const v = pick.vehicle;
    const split = v.brand.trim() ? { brand: v.brand.trim(), model: v.model.trim() } : splitBrandModel(v.model);
    const plateValue = v.plate.trim().toUpperCase();
    const title = [split.model.toUpperCase(), plateValue].filter(Boolean).join(" - ");
    const yearMatch = v.year.match(/\d{4}/);
    onChange({
      ...(title ? { customName: title } : {}),
      ...(split.model ? { model: split.model.toUpperCase() } : {}),
      ...(split.brand ? { brand: split.brand.toUpperCase() } : {}),
      ...(yearMatch ? { year: yearMatch[0] } : {}),
      ...(v.color.trim() ? { color: v.color.trim() } : {}),
    });
    if (title) onNameChange(title);
    if (plateValue) onPlateChange(plateValue);
  };
  const linkedUserIds = new Set((linkedTraccarUsers ?? []).map((u) => u.id));

  const filteredUsers = (users ?? []).filter((u) => {
    const q = userQuery.trim().toLowerCase();
    if (!q) return true;
    return (
      u.name?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q) ||
      String(u.id).toLowerCase().includes(q)
    );
  });

  const toggleLinkedUser = (traccarUserId: number) => {
    if (!deviceId) return;
    const next = new Set(linkedUserIds);
    if (next.has(traccarUserId)) next.delete(traccarUserId);
    else next.add(traccarUserId);
    syncVehicleUsers.mutate(
      { deviceId, traccarUserIds: [...next] },
      {
        onError: (err) => {
          toast.error("Não foi possível atualizar o vínculo", {
            description: err instanceof GpsDriveApiError ? err.message : undefined,
          });
        },
      },
    );
  };

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
      {/* ---------------- LEFT ---------------- */}
      <div>
        <SalesVehiclePicker onPick={applySalesPick} currentPlate={isCreate ? undefined : plate} />
        <AgendaVehiclePicker onPick={applyAgendaPick} currentPlate={isCreate ? undefined : plate} />
        <Section
          title="🚗 Principal"
          description="Imagem e dados principais do veículo — gravados no GPS Drive Backend."
        >
          <div className="grid grid-cols-1 gap-5 md:grid-cols-[180px_1fr]">
            {/* image */}
            <div className="space-y-2">
              <div className="relative flex aspect-square w-full items-center justify-center overflow-hidden rounded-lg border border-border bg-background/60">
                {imagePreview && !photoFailed ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={imagePreview}
                    alt={form.customName || name}
                    className="h-full w-full object-cover"
                    onError={() => setPhotoFailed(true)}
                  />
                ) : (
                  <div className="flex flex-col items-center gap-1 text-muted-foreground">
                    <Truck className="h-8 w-8" strokeWidth={1.5} />
                    <span className="text-[11px]">Sem imagem</span>
                  </div>
                )}
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => onPickImage(e.target.files?.[0])}
              />
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="w-full"
                onClick={() => fileRef.current?.click()}
                disabled={imageUploading}
              >
                {imageUploading ? "Enviando…" : "Alterar imagem"}
              </Button>
              {imageError && <p className="text-[11px] text-red-600">{imageError}</p>}
            </div>

            {/* main fields */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Field
                label={isCreate ? "Descrição / Nome *" : "Descrição / Nome"}
                span={2}
                hint="A descrição é o título do veículo."
              >
                <TxtInput
                  value={form.customName}
                  onChange={(e) => {
                    onChange({ customName: e.target.value });
                    if (isCreate) onNameChange(e.target.value);
                  }}
                  placeholder="Ex: Caminhão da entrega"
                />
              </Field>
              <Field label="Modelo do veículo">
                <TxtInput
                  value={form.model}
                  onChange={(e) => onChange({ model: e.target.value })}
                  placeholder="Ex: Hilux SRX"
                />
              </Field>
              <Field label="Marca">
                <TxtInput
                  value={form.brand}
                  onChange={(e) => onChange({ brand: e.target.value })}
                  placeholder="Ex: Toyota"
                />
              </Field>
              <Field label="Ano">
                <TxtInput
                  type="number"
                  value={form.year}
                  onChange={(e) => onChange({ year: e.target.value })}
                  placeholder="Ex: 2023"
                />
              </Field>
              <Field label="Placa">
                <TxtInput
                  value={plate}
                  onChange={(e) => onPlateChange(e.target.value)}
                  placeholder="ABC-1D23"
                  className="font-mono uppercase"
                />
              </Field>
            </div>
          </div>
        </Section>

        <Section title="Mais dados do veículo" description="Informações complementares.">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <Field label="Cor do veículo">
              <TxtInput value={form.color} onChange={(e) => onChange({ color: e.target.value })} placeholder="Ex: Branco" />
            </Field>
            <Field label="Valor FIPE (R$)">
              <TxtInput
                type="number"
                min="0"
                step="0.01"
                value={form.fipeValue}
                onChange={(e) => onChange({ fipeValue: e.target.value })}
                placeholder="0,00"
              />
            </Field>
            <Field label="Chassis">
              <TxtInput
                value={form.chassis}
                onChange={(e) => onChange({ chassis: e.target.value.toUpperCase() })}
                className="font-mono"
                placeholder="9BW..."
                maxLength={17}
              />
            </Field>
            {/* Plano (Administração > Planos) — campo manual de cadastro,
                não deduzido de nenhuma Recorrência Asaas (ver comentário em
                UpdateVehicleMetadataInput). Só aparece pra quem tem a
                mesma permissão que já lista Planos hoje (Conta &
                Integrações) — o backend rejeitaria a escrita de qualquer
                forma, mas esconder evita mostrar um seletor vazio/quebrado
                pra quem nunca teria como escolher algo nele. */}
            {canManagePlans && (
              <Field label="Plano">
                <Select
                  value={form.planId || "none"}
                  onValueChange={(v) => onChange({ planId: v === "none" ? "" : v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sem plano" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sem plano</SelectItem>
                    {(plans ?? []).map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name} · {planCurrencyFormatter.format(p.value)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            )}
          </div>
          <div className="mt-4">
            <Field label="Observações">
              <textarea
                value={form.notes}
                onChange={(e) => onChange({ notes: e.target.value })}
                rows={3}
                className="flex w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none transition-colors placeholder:text-muted-foreground focus:border-foreground/40"
                placeholder="Observações gerais sobre o veículo"
              />
            </Field>
          </div>
        </Section>
      </div>

      {/* ---------------- RIGHT ---------------- */}
      <div className="space-y-6">
        {/* Vínculo real via GPS Drive Backend → Traccar (ver relatório da
            etapa) — mesma relação de permissão da tela Usuários → Veículos,
            só o sentido da consulta muda. Só existe no fluxo de edição
            (precisa de um deviceId real do Traccar já criado). */}
        {!isCreate && deviceId && (
          <Section
            title="👤 Vincular usuário"
            description="Vincule os usuários que terão acesso a este veículo."
          >
            {/* Resumo rápido de quem já está vinculado — a lista abaixo é um
                seletor com busca (útil pra vincular/desvincular), mas não
                deixa claro à primeira vista quem já tem acesso; este resumo
                cobre isso sem precisar rolar a lista inteira. */}
            {!loadingLinkedUsers && (
              <p className="mb-3 text-xs text-muted-foreground">
                {linkedTraccarUsers && linkedTraccarUsers.length > 0 ? (
                  <>
                    <span className="font-medium text-foreground">Vinculado a: </span>
                    {linkedTraccarUsers.map((u) => u.name).join(", ")}
                  </>
                ) : (
                  "Nenhum usuário vinculado ainda."
                )}
              </p>
            )}
            <PermissionLinkPicker
              items={filteredUsers.slice(0, 30)}
              getId={(u) => Number(u.id)}
              isSelected={(id) => linkedUserIds.has(id)}
              onToggle={toggleLinkedUser}
              query={userQuery}
              onQueryChange={setUserQuery}
              searchPlaceholder="Buscar por nome, e-mail ou ID..."
              emptyText="Nenhum usuário encontrado."
              loading={loadingLinkedUsers}
              loadingText="Carregando usuários vinculados…"
              maxHeightClassName="max-h-60"
              renderRow={(u) => (
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="truncate text-sm font-medium">{u.name}</span>
                    {u.status === "nao_provisionado" && (
                      <span className="shrink-0 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">
                        Disponível
                      </span>
                    )}
                  </div>
                  <div className="truncate text-[11px] text-muted-foreground">{u.email}</div>
                </div>
              )}
            />
          </Section>
        )}

        <Section title="🧭 Ícone no mapa" description="Escolha o tipo e a cor do ícone que aparece no mapa.">
          <VehicleIconPicker
            value={{ iconKey, iconColorMode, iconColor, mapIconStyle }}
            onChange={onIconChange}
            photoUrl={imagePreview}
          />
        </Section>

      </div>
    </div>
  );
}

function TabHardware(props: VehicleFormTabProps) {
  const {
    mode,
    vehicle,
    form,
    onChange,
    imei,
    onImeiChange,
    customProtocolLabel,
    onCustomProtocolLabelChange,
    installImagePreview,
    onPickInstallImage,
    installImageUploading,
    installImageError,
  } = props;
  const isCreate = mode === "create";
  const fileRef = useRef<HTMLInputElement>(null);
  const { data: trackerProtocols } = useTrackerProtocols();

  return (
    <div
      className={cn(
        "grid grid-cols-1 gap-6",
        !isCreate && "lg:grid-cols-[minmax(0,1fr)_360px]",
      )}
    >
      {/* ---------------- LEFT ---------------- */}
      <div>
        <Section title="📶 Operadora" description="Dados do simcard instalado no rastreador.">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field label="Fornecedor">
              <TxtInput
                value={form.trackerSupplier}
                onChange={(e) => onChange({ trackerSupplier: e.target.value })}
                placeholder="Ex: Arqia, Transmite, Links Field"
              />
            </Field>
            <Field label="Operadora">
              <TxtInput
                value={form.simCarrier}
                onChange={(e) => onChange({ simCarrier: e.target.value })}
                placeholder="Ex: Vivo, Claro, TIM"
              />
            </Field>
            <Field label="ICCID">
              <TxtInput
                value={form.simIccid}
                onChange={(e) => onChange({ simIccid: e.target.value })}
                className="font-mono"
                placeholder="8955..."
              />
            </Field>
            <Field label="APN">
              <TxtInput
                value={form.simApn}
                onChange={(e) => onChange({ simApn: e.target.value })}
                placeholder="Ex: virtueyes.com.br"
              />
            </Field>
            <Field label="Número SIM" span={2}>
              <TxtInput
                value={form.simNumber}
                onChange={(e) => onChange({ simNumber: e.target.value })}
                className="font-mono"
                placeholder="55 11 9..."
              />
            </Field>
          </div>
        </Section>

        <Section title="📟 Modelo do Rastreador" description="Fabricante, modelo e custo do equipamento.">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <Field
              label="Tipo de rastreador"
              hint="Câmera: o veículo é rastreado pela própria câmera (GPS embutido) — só 1 IMEI é necessário."
            >
              <Select
                value={form.trackerType || "rastreador"}
                onValueChange={(v) => {
                  // Ao trocar pra câmera, se já tiver um IMEI de rastreador
                  // digitado e nenhum IMEI de câmera ainda, reaproveita o
                  // valor em vez de fazer o usuário digitar de novo.
                  if (v === "camera" && !form.cameraImei && imei) {
                    onChange({ trackerType: v, cameraImei: imei });
                  } else {
                    onChange({ trackerType: v });
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="rastreador">Rastreador</SelectItem>
                  <SelectItem value="tag">Tag</SelectItem>
                  <SelectItem value="camera">Câmera</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Fabricante do rastreador">
              <TxtInput
                value={form.trackerBrand}
                onChange={(e) => onChange({ trackerBrand: e.target.value })}
                placeholder="Ex: Suntech, Queclink"
              />
            </Field>
            <Field label="Modelo do rastreador">
              <TxtInput
                value={form.trackerModel}
                onChange={(e) => onChange({ trackerModel: e.target.value })}
                placeholder="Ex: ST4315U"
              />
            </Field>
            <Field label="Valor do equipamento (R$)">
              <TxtInput
                type="number"
                min="0"
                step="0.01"
                value={form.trackerPrice}
                onChange={(e) => onChange({ trackerPrice: e.target.value })}
                placeholder="0,00"
              />
            </Field>
          </div>
          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            {form.trackerType !== "camera" && (
              <Field
                label={API_URL ? "IMEI *" : "IMEI"}
                hint={
                  form.trackerType === "tag"
                    ? "Regra do tag: sempre começa com 00000 — digite só o restante do número que o aparelho informa."
                    : API_URL
                      ? "Identificador único do rastreador — usado pelo sistema para casar as posições recebidas com este veículo. Cuidado ao alterar: precisa bater exatamente com o que o aparelho envia (inclusive zeros à esquerda)."
                      : undefined
                }
              >
                {form.trackerType === "tag" ? (
                  <div className="flex items-center gap-2">
                    <span className="flex h-9 shrink-0 items-center rounded-md border border-border bg-muted px-3 font-mono text-sm text-muted-foreground">
                      {TAG_IMEI_PREFIX}
                    </span>
                    <TxtInput
                      value={(() => {
                        const full = normalizeTagImei(imei);
                        return full.startsWith(TAG_IMEI_PREFIX) ? full.slice(TAG_IMEI_PREFIX.length) : full;
                      })()}
                      onChange={(e) => onImeiChange(normalizeTagImei(e.target.value))}
                      placeholder="0000000000"
                      className="font-mono"
                    />
                  </div>
                ) : (
                  <TxtInput
                    value={imei}
                    onChange={(e) => onImeiChange(e.target.value)}
                    placeholder="000000000000000"
                    className="font-mono"
                  />
                )}
              </Field>
            )}
            <Field
              label="Protocolo do rastreador"
              hint='Opcional. Decide quais comandos do catálogo (Administração > Comandos GT06) aparecem no popup de envio deste veículo. Não achou o protocolo? Escolha "Outro" e digite o nome — ele já fica disponível em Administração para cadastrar os comandos depois.'
            >
              <Select
                value={form.protocol || undefined}
                onValueChange={(v) => {
                  onChange({ protocol: v });
                  if (v !== CUSTOM_PROTOCOL_VALUE) onCustomProtocolLabelChange("");
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o protocolo" />
                </SelectTrigger>
                <SelectContent>
                  {trackerProtocols?.map((p) => (
                    <SelectItem key={p.id} value={p.value}>
                      {p.label}
                    </SelectItem>
                  ))}
                  <SelectItem value={CUSTOM_PROTOCOL_VALUE}>Outro…</SelectItem>
                </SelectContent>
              </Select>
              {form.protocol === CUSTOM_PROTOCOL_VALUE && (
                <TxtInput
                  value={customProtocolLabel}
                  onChange={(e) => onCustomProtocolLabelChange(e.target.value)}
                  placeholder="Nome do protocolo (ex: Meitrack)"
                  className="mt-2"
                />
              )}
            </Field>
          </div>
          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field
              label={form.trackerType === "camera" ? (API_URL ? "IMEI da câmera *" : "IMEI da câmera") : "IMEI da câmera"}
              hint={
                form.trackerType === "camera"
                  ? "Este veículo é rastreado pela própria câmera — o mesmo IMEI identifica o rastreador (Traccar) e a câmera (tela Câmera da empresa)."
                  : "Opcional — se preenchido, este veículo aparece na tela Câmera da empresa (menu visível só quando o Super Master ativa o módulo Câmera)."
              }
            >
              <TxtInput
                value={form.cameraImei}
                onChange={(e) => {
                  onChange({ cameraImei: e.target.value });
                  // Câmera = mesmo IMEI serve pro Traccar (uniqueId) e pra
                  // tela Câmera — um campo só, sincroniza os dois.
                  if (form.trackerType === "camera") onImeiChange(e.target.value);
                }}
                placeholder="Ex: 860879071102483"
                className="font-mono"
              />
            </Field>
          </div>

        </Section>

        <Section title="🛠️ Local da Instalação" description="Registro da instalação do rastreador no veículo.">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-[180px_1fr]">
            {/* install image */}
            <div className="space-y-2">
              <div className="relative flex aspect-square w-full items-center justify-center overflow-hidden rounded-lg border border-border bg-background/60">
                {installImagePreview ? (
                  <img src={installImagePreview} alt="Instalação" className="h-full w-full object-cover" />
                ) : (
                  <div className="flex flex-col items-center gap-1 text-muted-foreground">
                    <MapPin className="h-8 w-8" strokeWidth={1.5} />
                    <span className="text-[11px]">Sem imagem</span>
                  </div>
                )}
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => onPickInstallImage(e.target.files?.[0])}
              />
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="w-full"
                onClick={() => fileRef.current?.click()}
                disabled={installImageUploading}
              >
                {installImageUploading ? "Enviando…" : "Alterar imagem"}
              </Button>
              {installImageError && <p className="text-[11px] text-red-600">{installImageError}</p>}
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Field label="Empresa da instalação" span={2}>
                <TxtInput
                  value={form.installCompany}
                  onChange={(e) => onChange({ installCompany: e.target.value })}
                  placeholder="Ex: Instala Rastreadores LTDA"
                />
              </Field>
              <Field label="Local da instalação" span={2}>
                <TxtInput
                  value={form.installLocation}
                  onChange={(e) => onChange({ installLocation: e.target.value })}
                  placeholder="Ex: sob o painel, lado do motorista"
                />
              </Field>
              <Field label="Data de instalação">
                <TxtInput
                  type="date"
                  value={form.installedAt}
                  onChange={(e) => onChange({ installedAt: e.target.value })}
                />
              </Field>
              <Field label="Instalador">
                <TxtInput
                  value={form.installTechnician}
                  onChange={(e) => onChange({ installTechnician: e.target.value })}
                  placeholder="Nome do técnico responsável"
                />
              </Field>
              <Field label="Garantia" span={2}>
                <TxtInput
                  value={form.installWarranty}
                  onChange={(e) => onChange({ installWarranty: e.target.value })}
                  placeholder="Ex: 12 meses"
                />
              </Field>
              <Field label="Observações da instalação" span={2}>
                <textarea
                  value={form.installNotes}
                  onChange={(e) => onChange({ installNotes: e.target.value })}
                  rows={3}
                  className="flex w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none transition-colors placeholder:text-muted-foreground focus:border-foreground/40"
                  placeholder="Observações sobre a instalação"
                />
              </Field>
            </div>
          </div>
        </Section>
      </div>

      {/* ---------------- RIGHT ---------------- */}
      {!isCreate && (
        <div className="space-y-6">
          <Section title="🚗 Informações do veículo" description="Dados atuais do veículo (somente leitura).">
            <div className="space-y-3 text-sm">
              <InfoRow label="Nome" value={vehicle!.name} />
              <InfoRow label="Placa" value={vehicle!.plate} mono />
              <InfoRow label="ID" value={vehicle!.id} mono />
              <InfoRow label="Última comunicação" value={timeWithAgo(vehicle!.lastConnection)} />
              <InfoRow label="Velocidade" value={`${vehicle!.speed} km/h`} />
              <InfoRow label="Odômetro" value={vehicle!.odometer ? `${vehicle!.odometer} km` : "—"} />
            </div>
          </Section>
        </div>
      )}
    </div>
  );
}

function InlineToggle({ defaultChecked }: { defaultChecked?: boolean }) {
  const [on, setOn] = useState(!!defaultChecked);
  return (
    <button
      type="button"
      onClick={() => setOn((v) => !v)}
      className={cn(
        "relative inline-flex h-5 w-9 items-center rounded-full transition-colors",
        on ? "bg-foreground" : "bg-border",
      )}
    >
      <span
        className={cn(
          "inline-block h-4 w-4 transform rounded-full bg-background transition-transform",
          on ? "translate-x-[18px]" : "translate-x-0.5",
        )}
      />
    </button>
  );
}

function TabCliente() {
  return (
    <>
      <Section title="Cliente vinculado" description="Empresa ou pessoa responsável por este dispositivo.">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <Field label="Razão social" span={2}>
            <TxtInput defaultValue="Transportes Sul LTDA" />
          </Field>
          <Field label="CNPJ">
            <TxtInput defaultValue="12.345.678/0001-90" className="font-mono" />
          </Field>
          <Field label="Nome fantasia">
            <TxtInput defaultValue="Sul Cargas" />
          </Field>
          <Field label="Inscrição estadual">
            <TxtInput defaultValue="096/1234567" className="font-mono" />
          </Field>
          <Field label="Segmento">
            <TxtSelect defaultValue="logistica">
              <option value="logistica">Logística</option>
              <option value="passageiros">Transporte de passageiros</option>
              <option value="delivery">Delivery</option>
              <option value="frota-propria">Frota própria</option>
            </TxtSelect>
          </Field>
        </div>
      </Section>

      <Section title="Contato">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <Field label="Responsável">
            <TxtInput defaultValue="Fernando Braga" />
          </Field>
          <Field label="E-mail">
            <TxtInput type="email" defaultValue="contato@sulcargas.com.br" />
          </Field>
          <Field label="Telefone">
            <TxtInput defaultValue="+55 51 99999-0000" />
          </Field>
          <Field label="Endereço" span={2}>
            <TxtInput defaultValue="Av. Ipiranga, 6681 — Porto Alegre / RS" />
          </Field>
          <Field label="CEP">
            <TxtInput defaultValue="90619-900" className="font-mono" />
          </Field>
        </div>
      </Section>

      <Section title="Plano contratado">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <Field label="Plano">
            <TxtSelect defaultValue="premium">
              <option value="basic">Básico</option>
              <option value="pro">Profissional</option>
              <option value="premium">Premium</option>
              <option value="enterprise">Enterprise</option>
            </TxtSelect>
          </Field>
          <Field label="Ciclo de faturamento">
            <TxtSelect defaultValue="mensal">
              <option value="mensal">Mensal</option>
              <option value="anual">Anual</option>
            </TxtSelect>
          </Field>
          <Field label="Vencimento">
            <TxtInput type="date" defaultValue="2026-08-15" />
          </Field>
        </div>
      </Section>
    </>
  );
}

function TabVeiculos({ vehicle }: { vehicle: Vehicle }) {
  return (
    <>
      <Section title="Veículos associados" description="Dispositivos e veículos do mesmo grupo/cliente.">
        <div className="overflow-hidden rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-background/60 text-[11px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-2 text-left font-medium">Veículo</th>
                <th className="px-4 py-2 text-left font-medium">Placa</th>
                <th className="px-4 py-2 text-left font-medium">Motorista</th>
                <th className="px-4 py-2 text-left font-medium">Status</th>
                <th className="px-4 py-2 text-right font-medium">Vinculado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {[
                { n: vehicle.name, p: vehicle.plate, d: vehicle.driver ?? "—", s: vehicle.status, on: true },
                { n: "Frota 07 - Saveiro", p: "IVQ-7G89", d: "Diego Martins", s: "online" as VehicleStatus, on: true },
                { n: "Frota 11 - Partner", p: "IVU-2K23", d: "Eduardo Lima", s: "online" as VehicleStatus, on: false },
                { n: "Frota 15 - Master", p: "IVY-6O67", d: "Thiago Correa", s: "idle" as VehicleStatus, on: true },
              ].map((r) => (
                <tr key={r.p}>
                  <td className="px-4 py-2.5 font-medium">{r.n}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">{r.p}</td>
                  <td className="px-4 py-2.5 text-muted-foreground">{r.d}</td>
                  <td className="px-4 py-2.5">
                    <StatusPill status={r.s} />
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <InlineToggle defaultChecked={r.on} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section title="Grupo de frota">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <Field label="Grupo principal">
            <TxtSelect defaultValue="rs-sul">
              <option value="rs-sul">Frota RS — Sul</option>
              <option value="rs-metro">Frota RS — Metropolitana</option>
              <option value="sc">Frota SC</option>
            </TxtSelect>
          </Field>
          <Field label="Subgrupo">
            <TxtInput defaultValue="Coleta e entrega" />
          </Field>
          <Field label="Cor de identificação">
            <TxtSelect defaultValue="cinza">
              <option>Cinza</option>
              <option>Branco</option>
              <option>Verde</option>
              <option>Vermelho</option>
            </TxtSelect>
          </Field>
        </div>
      </Section>
    </>
  );
}

function TabApi({ vehicle }: { vehicle: Vehicle }) {
  const key = `wp_live_${vehicle.id.padEnd(6, "0")}_kR8sY2xN1mQ7pL4aV9dT`;
  return (
    <>
      <Section title="Credenciais de API" description="Use estas credenciais para integrar via REST.">
        <div className="grid grid-cols-1 gap-4">
          <Field label="Endpoint base">
            <TxtInput readOnly defaultValue="https://api.wprastreamento.com.br/v1" className="font-mono text-xs" />
          </Field>
          <Field label="API Key" hint="Mantenha em segredo. Regenerar invalida integrações existentes.">
            <div className="flex gap-2">
              <TxtInput readOnly defaultValue={key} className="font-mono text-xs" />
              <Button variant="outline" size="sm">Copiar</Button>
              <Button variant="outline" size="sm">Regenerar</Button>
            </div>
          </Field>
          <Field label="Webhook URL (posições em tempo real)">
            <TxtInput placeholder="https://sua-empresa.com/webhooks/rastreamento" />
          </Field>
          <Field label="Assinatura HMAC (segredo)">
            <TxtInput readOnly defaultValue="whsec_x8Kf2mN7pQ4rL1vT9sY3aD6bH0jU5gC" className="font-mono text-xs" />
          </Field>
        </div>
      </Section>

      <Section title="Permissões da API">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Toggle label="Leitura de posições" defaultChecked />
          <Toggle label="Leitura de histórico" defaultChecked />
          <Toggle label="Leitura de alertas" defaultChecked />
          <Toggle label="Envio de comandos" />
          <Toggle label="Gerenciar cercas eletrônicas" />
          <Toggle label="Gerenciar usuários" />
        </div>
      </Section>

      <Section title="Endpoints disponíveis">
        <div className="overflow-hidden rounded-lg border border-border font-mono text-xs">
          {[
            ["GET", "/vehicles", "Lista veículos"],
            ["GET", "/vehicles/:id", "Detalhes do veículo"],
            ["GET", "/history?vehicleId=:id", "Histórico de posições"],
            ["GET", "/alerts", "Alertas ativos"],
            ["POST", "/commands", "Envia comando ao dispositivo"],
          ].map(([m, path, desc]) => (
            <div key={path} className="flex items-center gap-3 border-b border-border px-4 py-2 last:border-b-0">
              <span
                className={cn(
                  "inline-flex h-5 w-14 items-center justify-center rounded text-[10px] font-semibold",
                  m === "GET" ? "bg-emerald-500/15 text-emerald-500" : "bg-yellow-500/15 text-yellow-500",
                )}
              >
                {m}
              </span>
              <span className="text-foreground">{path}</span>
              <span className="ml-auto text-muted-foreground">{desc}</span>
            </div>
          ))}
        </div>
      </Section>
    </>
  );
}

function TabDispositivos({ vehicle }: { vehicle: Vehicle }) {
  const sessions = [
    { app: "Painel Web", browser: "Chrome 128 · macOS", ip: "189.45.12.88", loc: "Porto Alegre / RS", last: "agora", active: true },
    { app: "App Mobile", browser: "iOS 18 · iPhone 15", ip: "177.98.201.44", loc: "Viamão / RS", last: "5 min atrás", active: true },
    { app: "Rastreador", browser: `${vehicle.name}`, ip: "Modem 4G · Vivo", loc: `${vehicle.lat.toFixed(3)}, ${vehicle.lng.toFixed(3)}`, last: timeWithAgo(vehicle.lastConnection), active: vehicle.status !== "offline" },
    { app: "Painel Web", browser: "Firefox 130 · Windows", ip: "200.132.4.17", loc: "Canoas / RS", last: "2 h atrás", active: false },
  ];
  return (
    <>
      <Section title="Sessões e dispositivos conectados" description="Aparelhos com acesso ativo a esta conta de rastreamento.">
        <div className="overflow-hidden rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-background/60 text-[11px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-2 text-left font-medium">Origem</th>
                <th className="px-4 py-2 text-left font-medium">Dispositivo</th>
                <th className="px-4 py-2 text-left font-medium">IP / Rede</th>
                <th className="px-4 py-2 text-left font-medium">Localização</th>
                <th className="px-4 py-2 text-left font-medium">Última atividade</th>
                <th className="px-4 py-2 text-right font-medium">Ação</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {sessions.map((s, i) => (
                <tr key={i}>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <span
                        className={cn(
                          "h-1.5 w-1.5 rounded-full",
                          s.active ? "bg-emerald-500" : "bg-muted-foreground",
                        )}
                      />
                      <span className="font-medium">{s.app}</span>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-muted-foreground">{s.browser}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">{s.ip}</td>
                  <td className="px-4 py-2.5 text-muted-foreground">{s.loc}</td>
                  <td className="px-4 py-2.5 text-muted-foreground">{s.last}</td>
                  <td className="px-4 py-2.5 text-right">
                    <Button variant="outline" size="sm" className="h-7 text-xs">
                      Encerrar
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section title="Segurança de sessão">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Toggle label="Autenticação em duas etapas" description="Exigir código adicional no login." defaultChecked />
          <Toggle label="Notificar novos acessos" description="Enviar e-mail ao detectar dispositivo novo." defaultChecked />
          <Toggle label="Bloquear acesso fora do horário comercial" />
          <Toggle label="Encerrar sessões inativas após 30 min" defaultChecked />
        </div>
      </Section>
    </>
  );
}

function VehicleTypeCard({
  label,
  sub,
  image,
  active,
  tone,
  onClick,
}: {
  label: string;
  sub: string;
  image: string;
  active: boolean;
  tone: "blue" | "red";
  onClick: () => void;
}) {
  const glow =
    tone === "blue"
      ? "from-sky-500/25 via-blue-500/10 to-transparent"
      : "from-rose-500/25 via-red-500/10 to-transparent";
  const ring =
    tone === "blue"
      ? "ring-sky-400/70 shadow-[0_0_0_1px_rgba(56,189,248,0.6),0_18px_40px_-12px_rgba(56,189,248,0.55)]"
      : "ring-rose-400/70 shadow-[0_0_0_1px_rgba(251,113,133,0.6),0_18px_40px_-12px_rgba(244,63,94,0.55)]";
  const dot =
    tone === "blue"
      ? "bg-gradient-to-b from-sky-400 to-blue-600"
      : "bg-gradient-to-b from-rose-400 to-red-600";
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`group relative overflow-hidden rounded-2xl border bg-gradient-to-b from-card to-background/60 p-4 text-left transition-all hover:-translate-y-0.5 active:translate-y-0 ${
        active ? `ring-2 ${ring}` : "border-border/70 hover:border-foreground/30"
      }`}
    >
      <div
        className={`pointer-events-none absolute inset-0 bg-gradient-radial ${glow} opacity-80`}
        style={{
          background: `radial-gradient(120% 80% at 50% 0%, var(--tw-gradient-stops))`,
        }}
      />
      <div className="relative flex items-center gap-4">
        <div className="relative flex h-24 w-28 shrink-0 items-center justify-center rounded-xl bg-gradient-to-b from-white/5 to-black/30 ring-1 ring-white/5">
          <div className="absolute inset-x-4 bottom-2 h-2 rounded-full bg-black/50 blur-md" />
          <img
            src={image}
            alt={label}
            width={112}
            height={96}
            loading="lazy"
            className="relative h-24 w-auto object-contain drop-shadow-[0_10px_18px_rgba(0,0,0,0.55)] transition-transform duration-300 group-hover:scale-105 group-hover:-rotate-1"
          />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <div className={`h-2.5 w-2.5 rounded-full ${dot} shadow-inner`} />
            <div className="text-base font-semibold">{label}</div>
          </div>
          <div className="mt-1 text-xs text-muted-foreground">{sub}</div>
          <div
            className={`mt-3 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium ${
              active
                ? tone === "blue"
                  ? "bg-sky-500/15 text-sky-300 ring-1 ring-sky-400/40"
                  : "bg-rose-500/15 text-rose-300 ring-1 ring-rose-400/40"
                : "bg-muted/40 text-muted-foreground ring-1 ring-border/60"
            }`}
          >
            <span
              className={`h-1.5 w-1.5 rounded-full ${active ? dot : "bg-muted-foreground/60"}`}
            />
            {active ? "Selecionado" : "Selecionar"}
          </div>
        </div>
      </div>
    </button>
  );
}

/* ---------- Alertas tab ---------- */

const ALERTS_PAGE_SIZE = 10;

function AlertsPager({
  page,
  pageCount,
  total,
  noun,
  onPage,
}: {
  page: number;
  pageCount: number;
  total: number;
  noun: string;
  onPage: (page: number) => void;
}) {
  const from = (page - 1) * ALERTS_PAGE_SIZE + 1;
  const to = Math.min(page * ALERTS_PAGE_SIZE, total);
  return (
    <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
      <span>
        {from}–{to} de {total} {noun}
        {total === 1 ? "" : "s"}
      </span>
      {pageCount > 1 && (
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => onPage(page - 1)}>
            ← Anterior
          </Button>
          <span className="font-medium text-foreground">
            {page} / {pageCount}
          </span>
          <Button
            size="sm"
            variant="outline"
            disabled={page >= pageCount}
            onClick={() => onPage(page + 1)}
          >
            Próxima →
          </Button>
        </div>
      )}
    </div>
  );
}

export function TabAlertasEdit({ vehicle }: { vehicle: Vehicle }) {
  const deviceId = Number(vehicle.id);
  const [createOpen, setCreateOpen] = useState(false);

  const { data: configured = [], isLoading: configuredLoading } = useAlertRules(deviceId);
  // Pedido 2026-10-09: filtro + "quebra" de 10 em 10 no histórico —
  // paginado e filtrado no servidor pra alcançar o histórico inteiro.
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [periodFilter, setPeriodFilter] = useState<"all" | "24h" | "7d" | "30d">("all");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [eventsPage, setEventsPage] = useState(1);
  const [rulesPage, setRulesPage] = useState(1);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);
  useEffect(() => setEventsPage(1), [typeFilter, periodFilter, debouncedSearch]);

  // Arredondado ao minuto pra queryKey não mudar a cada render.
  const since = useMemo(() => {
    const hours = periodFilter === "24h" ? 24 : periodFilter === "7d" ? 24 * 7 : periodFilter === "30d" ? 24 * 30 : 0;
    if (!hours) return undefined;
    const minute = Math.floor(Date.now() / 60_000) * 60_000;
    return new Date(minute - hours * 3_600_000).toISOString();
  }, [periodFilter]);

  const filtersActive = typeFilter !== "all" || !!since || !!debouncedSearch;
  const eventsQuery = useAlertEventsPage({
    deviceId,
    page: eventsPage,
    pageSize: VEHICLE_ALERT_EVENTS_PAGE_SIZE,
    ...(typeFilter !== "all" ? { type: typeFilter } : {}),
    ...(since ? { since } : {}),
    ...(debouncedSearch ? { q: debouncedSearch } : {}),
  });
  const pagedEvents = eventsQuery.data?.items ?? [];
  const eventsTotal = eventsQuery.data?.total ?? 0;
  const recentLoading = eventsQuery.isLoading;
  const eventTypes = useMemo(
    () => [...(eventsQuery.data?.types ?? [])].sort((x, y) => labelFor(x).localeCompare(labelFor(y))),
    [eventsQuery.data?.types],
  );
  const eventsPageCount = Math.max(1, Math.ceil(eventsTotal / VEHICLE_ALERT_EVENTS_PAGE_SIZE));
  const eventsPageSafe = Math.min(eventsPage, eventsPageCount);

  const rulesPageCount = Math.max(1, Math.ceil(configured.length / ALERTS_PAGE_SIZE));
  const rulesPageSafe = Math.min(rulesPage, rulesPageCount);
  const pagedRules = configured.slice(
    (rulesPageSafe - 1) * ALERTS_PAGE_SIZE,
    rulesPageSafe * ALERTS_PAGE_SIZE,
  );
  const createRule = useCreateAlertRule();
  const updateRule = useUpdateAlertRule(deviceId);
  const deleteRule = useDeleteAlertRule(deviceId);

  const handleCreate = (
    draft: Omit<Parameters<typeof createRule.mutate>[0], "deviceId">,
  ) => {
    createRule.mutate(
      { ...draft, deviceId },
      {
        onSuccess: () => {
          setCreateOpen(false);
          toast.success("Alerta criado", { description: labelFor(draft.type) });
        },
        onError: (err) =>
          toast.error("Não foi possível criar o alerta", { description: err.message }),
      },
    );
  };

  const toggleAlert = (id: string, enabled: boolean) =>
    updateRule.mutate(
      { id, patch: { enabled: !enabled } },
      { onError: (err) => toast.error("Não foi possível atualizar o alerta", { description: err.message }) },
    );
  const removeAlert = (id: string) =>
    deleteRule.mutate(id, {
      onError: (err) => toast.error("Não foi possível excluir o alerta", { description: err.message }),
    });

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
      <SimulateAlertPanel deviceId={deviceId} />
      {/* -------- LEFT: vehicle info -------- */}
      <div>
        <Section title="🚗 Informações do veículo" description="Dados atuais do dispositivo.">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <AlertInfoRow icon={Truck} label="Nome" value={vehicle.name} />
            <AlertInfoRow icon={Hash} label="Placa" value={vehicle.plate} mono />
            <AlertInfoRow icon={User} label="Motorista" value={vehicle.driver ?? "—"} />
            <AlertInfoRow
              icon={Activity}
              label="Status"
              value={
                vehicle.status === "online"
                  ? "Online"
                  : vehicle.status === "idle"
                    ? "Parado"
                    : "Offline"
              }
            />
            <AlertInfoRow icon={Gauge} label="Velocidade" value={`${vehicle.speed} km/h`} />
            <AlertInfoRow
              icon={Clock}
              label="Última comunicação"
              value={timeWithAgo(vehicle.lastConnection)}
            />
            <AlertInfoRow
              icon={MapPin}
              label="Coordenadas"
              value={`${vehicle.lat.toFixed(5)}, ${vehicle.lng.toFixed(5)}`}
              mono
              span={2}
            />
          </div>
        </Section>
      </div>

      {/* -------- RIGHT: alerts -------- */}
      <div className="space-y-6">
        <Section
          title="🔔 Últimos alertas do veículo"
          description="Alertas das regras + eventos do rastreador (ignição, alarmes, velocidade, cercas)."
        >
          <div className="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
              aria-label="Filtrar por tipo"
            >
              <option value="all">Todos os tipos</option>
              {eventTypes.map((t) => (
                <option key={t} value={t}>
                  {labelFor(t)}
                </option>
              ))}
            </select>
            <select
              value={periodFilter}
              onChange={(e) => setPeriodFilter(e.target.value as typeof periodFilter)}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
              aria-label="Filtrar por período"
            >
              <option value="all">Qualquer data</option>
              <option value="24h">Últimas 24 horas</option>
              <option value="7d">Últimos 7 dias</option>
              <option value="30d">Últimos 30 dias</option>
            </select>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar na mensagem…"
              className="h-9"
            />
          </div>
          {recentLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-[52px] animate-pulse rounded-lg bg-muted/60" />
              ))}
            </div>
          ) : eventsTotal === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              {filtersActive
                ? "Nenhum alerta encontrado com esses filtros."
                : "Nenhum alerta disparado ainda para este veículo."}
            </p>
          ) : (
            <>
              <ul className="space-y-2">
                {pagedEvents.map((a) => (
                  <li
                    key={a.id}
                    className="flex items-start gap-3 rounded-lg border border-border bg-background/60 px-3 py-2.5"
                  >
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted text-base">
                      <span aria-hidden>
                        {ALERT_TYPE_OPTIONS.find((o) => o.key === a.type)?.emoji ?? "🚨"}
                      </span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium">{a.message}</span>
                        <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] uppercase text-muted-foreground">
                          {a.source === "tracker" ? "Rastreador" : "Regra de alerta"}
                        </span>
                      </div>
                      <div className="mt-0.5 text-[11px] text-muted-foreground">
                        {labelFor(a.type)} · {new Date(a.firedAt).toLocaleString("pt-BR")}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
              {!since && eventsQuery.data?.trackerSince && (
                <p className="mt-2 text-[11px] text-muted-foreground">
                  Eventos do rastreador desde{" "}
                  {new Date(eventsQuery.data.trackerSince).toLocaleDateString("pt-BR")} (últimos 90 dias).
                </p>
              )}
              <AlertsPager
                page={eventsPageSafe}
                pageCount={eventsPageCount}
                total={eventsTotal}
                noun="alerta"
                onPage={setEventsPage}
              />
            </>
          )}
        </Section>

        <section className="mb-8">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold tracking-tight">⚙️ Alertas configurados</h3>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Regras que disparam notificações automáticas.
              </p>
            </div>
            <Button size="sm" onClick={() => setCreateOpen(true)} className="gap-1.5">
              <Plus className="h-4 w-4" />
              Criar alerta
            </Button>
          </div>
          <div className="rounded-xl border border-border bg-background/40 p-5">
            {configuredLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 2 }).map((_, i) => (
                  <div key={i} className="h-[60px] animate-pulse rounded-lg bg-muted/60" />
                ))}
              </div>
            ) : configured.length === 0 ? (
              <div className="flex flex-col items-center gap-2 py-6 text-center">
                <Bell className="h-6 w-6 text-muted-foreground" strokeWidth={1.5} />
                <p className="text-sm text-muted-foreground">Nenhum alerta configurado.</p>
                <p className="text-[11px] text-muted-foreground">
                  Clique em <span className="font-medium">Criar alerta</span> para começar.
                </p>
              </div>
            ) : (
              <ul className="space-y-2">
                {pagedRules.map((c) => {
                  const opt = ALERT_TYPE_OPTIONS.find((o) => o.key === c.type) ?? {
                    key: c.type,
                    label: c.type,
                    emoji: "🔔",
                  };
                  return (
                    <li
                      key={c.id}
                      className="flex items-start gap-3 rounded-lg border border-border bg-background/60 px-3 py-2.5"
                    >
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted text-base">
                        <span aria-hidden>{opt.emoji}</span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-medium">{opt.label}</span>
                          {!c.enabled && (
                            <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] uppercase text-muted-foreground">
                              Desativado
                            </span>
                          )}
                        </div>
                        <div className="mt-1 flex flex-wrap gap-1.5">
                          {c.config?.maxSpeed != null && (
                            <ChannelChip label={`💨 Máx ${c.config.maxSpeed} km/h`} />
                          )}
                          {c.config?.stopMinutes != null && (
                            <ChannelChip
                              label={`⏱️ ${c.config.stopMinutes >= 60 && c.config.stopMinutes % 60 === 0 ? `${c.config.stopMinutes / 60}h` : `${c.config.stopMinutes}min`} parado`}
                            />
                          )}
                          {c.config?.noSignalMinutes != null && (
                            <ChannelChip
                              label={`📡 ${c.config.noSignalMinutes >= 60 && c.config.noSignalMinutes % 60 === 0 ? `${c.config.noSignalMinutes / 60}h` : `${c.config.noSignalMinutes}min`} sem sinal`}
                            />
                          )}
                          {c.config?.offHours && (
                            <ChannelChip
                              label={`🕒 ${c.config.offHours.weekdaysStart}–${c.config.offHours.weekdaysEnd}${c.config.offHours.weekendEnabled ? ` · fds ${c.config.offHours.weekendStart}–${c.config.offHours.weekendEnd}` : ""}`}
                            />
                          )}
                          {c.channelApp && <ChannelChip label="📱 No app" />}
                          {c.channelPush && <ChannelChip label="📲 Push no celular" />}
                          <ChannelChip label={`🔁 Ignora ${c.dedupeMinutes}min`} />
                        </div>
                        <div className="mt-1 text-[11px] text-muted-foreground">
                          Criado em {new Date(c.createdAt).toLocaleString("pt-BR")}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <button
                          type="button"
                          onClick={() => toggleAlert(c.id, c.enabled)}
                          className="text-[11px] font-medium text-muted-foreground hover:text-foreground"
                        >
                          {c.enabled ? "Desativar" : "Ativar"}
                        </button>
                        <button
                          type="button"
                          onClick={() => removeAlert(c.id)}
                          className="text-[11px] font-medium text-rose-600 hover:text-rose-700"
                        >
                          Excluir
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
            {!configuredLoading && configured.length > 0 && (
              <AlertsPager
                page={rulesPageSafe}
                pageCount={rulesPageCount}
                total={configured.length}
                noun="regra"
                onPage={setRulesPage}
              />
            )}
          </div>
        </section>
      </div>

      <CreateAlertDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSubmit={handleCreate}
      />
    </div>
  );
}

// Pedido 2026-09-08: "dentro do popup de editar veículo, deve ter aba de
// logs, com tudo isso que acontece — que foi enviado, que foi recebido,
// qual erro deu, o que o veículo fez". Reaproveita o mesmo painel do
// expandir-linha em /vehicles (ver components/vehicle-logs-panel.tsx), com
// janela maior (7 dias em vez de 72h — aqui é consulta ativa de diagnóstico,
// não um resumo rápido) e cabeçalho no padrão Section usado no resto do
// formulário.
function TabLogs({ vehicle }: { vehicle: Vehicle }) {
  return (
    <Section
      title="🧾 Logs do veículo"
      description="Eventos reais do rastreador (Traccar) — comandos enviados/respondidos, ignição, movimento, alarmes."
    >
      <VehicleLogsPanel vehicle={vehicle} hours={24 * 7} />
    </Section>
  );
}

// Pedido 2026-09-11: "Excluir dispositivo" não chama mais o DELETE do
// Traccar direto do frontend — passa pelo GPS Drive Backend
// (useMoveVehicleToTrash), que grava um retrato completo (device Traccar +
// vehicle_metadata) ANTES de excluir de verdade, pra dar pra recuperar
// depois pela Lixeira (tela Veículos, ícone ao lado de Importar/Exportar).
// O histórico de posições/comandos continua se perdendo pra sempre (o
// Traccar não tem undelete) — só o CADASTRO (nome/IMEI/linha do
// chip/placa/marca/modelo/instalação/plano) é recuperável.
// Pedido 2026-09-27: excluir exige o MOTIVO, e o destino do rastreador
// (volta pro estoque / teste / descarte) vira uma movimentação real no
// Estoque da Agenda (POST /api/v1/stock/movements) — mesma rota do botão
// "Movimentar equipamento" de lá. A movimentação só é gravada DEPOIS da
// exclusão dar certo: ao contrário, uma exclusão que falhasse deixaria o
// mesmo IMEI "instalado" na frota e "disponível" no estoque ao mesmo tempo.
const DELETE_STOCK_OPTIONS: Array<{ value: MovementAction; label: string; hint: string }> = [
  {
    value: "voltar_estoque",
    label: "Rastreador volta para o estoque",
    hint: "Retirado do veículo e em bom estado — fica disponível para reinstalar",
  },
  {
    value: "testar",
    label: "Rastreador vai para teste",
    hint: "Precisa ser testado antes de reinstalar",
  },
  {
    value: "descartar",
    label: "Descartar rastreador",
    hint: "Defeito, perdido, roubado ou ficou com o cliente — dá baixa no estoque",
  },
];

function TabExcluir({ vehicle, onDeleted }: { vehicle: Vehicle; onDeleted: () => void }) {
  const moveToTrash = useMoveVehicleToTrash();
  const moveTracker = useMoveTracker();
  const { data: metadata } = useVehicleMetadata(vehicle.id);
  const [acao, setAcao] = useState<MovementAction | null>(null);
  const [motivo, setMotivo] = useState("");
  const [error, setError] = useState<string | null>(null);
  const imei = vehicle.imei?.trim() ?? "";
  const busy = moveToTrash.isPending || moveTracker.isPending;

  const handleDelete = async () => {
    if (!acao) {
      setError("Escolha o que acontece com o rastreador");
      return;
    }
    if (motivo.trim().length < 3) {
      setError("Informe o motivo da exclusão");
      return;
    }
    setError(null);
    if (
      !window.confirm(
        `Tem certeza que deseja EXCLUIR o dispositivo ${vehicle.name} (${vehicle.plate})?\n\nO rastreador some da frota e o histórico de posições/comandos é apagado, mas o cadastro (IMEI, SIM, placa, marca/modelo etc.) fica salvo na Lixeira (tela Veículos) — dá pra restaurar depois.`,
      )
    ) {
      return;
    }
    try {
      await moveToTrash.mutateAsync(Number(vehicle.id));
    } catch (err) {
      toast.error("Não foi possível excluir o dispositivo", {
        description: err instanceof Error ? err.message : undefined,
      });
      return;
    }
    if (imei) {
      try {
        await moveTracker.mutateAsync({
          imei,
          acao,
          motivo: `Exclusão de ${vehicle.name}${vehicle.plate ? ` (${vehicle.plate})` : ""}: ${motivo.trim()}`,
          modelo: metadata?.trackerModel?.trim() || "Sem modelo",
          fornecedor: metadata?.trackerSupplier || null,
          garantia: metadata?.installWarranty || null,
          chipNumero: metadata?.simNumber || vehicle.phone || null,
          chipIccid: metadata?.simIccid || null,
          chipOperadora: metadata?.simCarrier || null,
          localizacao: acao === "descartar" ? null : "Estoque",
        });
      } catch (err) {
        toast.error("Dispositivo excluído, mas o estoque não foi atualizado", {
          description: `Ajuste manualmente em Agenda > Estoque. ${err instanceof Error ? err.message : ""}`,
        });
      }
    }
    onDeleted();
  };

  return (
    <div className="mx-auto max-w-xl">
      <Section
        title="⚠️ Excluir dispositivo"
        description="Remove este rastreador da frota — o cadastro fica recuperável pela Lixeira."
      >
        <div className="rounded-lg border border-rose-500/40 bg-rose-500/5 p-4 text-sm text-rose-700 dark:text-rose-300">
          <p className="flex items-center gap-2 font-semibold">{vehicle.name} <LicensePlate plate={vehicle.plate} size="xs" /></p>
          <p className="mt-1 text-[13px] leading-relaxed opacity-90">
            Ao excluir, o dispositivo some da lista de frota, do mapa e dos relatórios. Histórico
            de posições, comandos enviados e alertas configurados são apagados junto e não podem
            ser recuperados — mas o CADASTRO (IMEI, SIM, placa, marca/modelo etc.) fica salvo na
            Lixeira (tela Veículos) e pode ser restaurado depois.
          </p>
        </div>

        <div className="mt-4 space-y-2">
          <div className="text-sm font-medium text-foreground">
            O que acontece com o rastreador?
          </div>
          <RadioGroup
            value={acao ?? ""}
            onValueChange={(v) => {
              setAcao(v as MovementAction);
              setError(null);
            }}
            className="gap-2"
          >
            {DELETE_STOCK_OPTIONS.map((o) => (
              <label
                key={o.value}
                className={cn(
                  "flex cursor-pointer items-start gap-3 rounded-lg border p-3",
                  acao === o.value ? "border-primary bg-primary/5" : "border-border",
                )}
              >
                <RadioGroupItem value={o.value} className="mt-0.5" />
                <span>
                  <span className="block text-sm font-medium text-foreground">{o.label}</span>
                  <span className="block text-xs text-muted-foreground">{o.hint}</span>
                </span>
              </label>
            ))}
          </RadioGroup>
          {!imei && (
            <p className="text-xs text-amber-600 dark:text-amber-400">
              Este dispositivo não tem IMEI cadastrado — o estoque não será ajustado.
            </p>
          )}
        </div>

        <div className="mt-4 space-y-1.5">
          <div className="text-sm font-medium text-foreground">Motivo da exclusão</div>
          <Textarea
            value={motivo}
            onChange={(e) => {
              setMotivo(e.target.value);
              setError(null);
            }}
            placeholder="Ex.: cliente cancelou, veículo vendido, equipamento com defeito…"
            rows={3}
          />
        </div>

        {error && <p className="mt-2 text-xs text-rose-500">{error}</p>}

        <Button
          type="button"
          variant="destructive"
          className="mt-4"
          onClick={handleDelete}
          disabled={busy}
        >
          {busy ? "Excluindo…" : "Excluir dispositivo"}
        </Button>
      </Section>
    </div>
  );
}

function labelFor(type: AlertRuleType | "alarm") {
  if (type === "alarm") return "Alarme do rastreador";
  return ALERT_TYPE_OPTIONS.find((o) => o.key === type)?.label ?? type;
}

function AlertInfoRow({
  icon: Icon,
  label,
  value,
  mono,
  span,
}: {
  icon: typeof Truck;
  label: string;
  value: string;
  mono?: boolean;
  span?: 1 | 2;
}) {
  return (
    <div
      className={cn(
        "flex items-start gap-3 rounded-lg border border-border bg-background/60 px-3 py-2.5",
        span === 2 && "md:col-span-2",
      )}
    >
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted">
        <Icon className="h-4 w-4" strokeWidth={1.75} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
          {label}
        </div>
        <div className={cn("mt-0.5 truncate text-sm", mono && "font-mono")}>{value}</div>
      </div>
    </div>
  );
}

function ChannelChip({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-muted/60 px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
      {label}
    </span>
  );
}

function CreateAlertDialog({
  open,
  onOpenChange,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onSubmit: (draft: {
    type: AlertRuleType;
    config?: AlertRuleConfig;
    channelApp: boolean;
    channelPush: boolean;
    dedupeMinutes: number;
  }) => void;
}) {
  const [type, setType] = useState<AlertRuleType>("ignition_on");
  const [channelApp, setChannelApp] = useState(true);
  const [channelPush, setChannelPush] = useState(true);
  const [dedupe, setDedupe] = useState(5);
  const [maxSpeed, setMaxSpeed] = useState(80);
  const [stopValue, setStopValue] = useState(15);
  const [stopUnit, setStopUnit] = useState<"min" | "h">("min");
  const [noSignalValue, setNoSignalValue] = useState(10);
  const [noSignalUnit, setNoSignalUnit] = useState<"min" | "h">("min");
  const [weekdaysStart, setWeekdaysStart] = useState("08:00");
  const [weekdaysEnd, setWeekdaysEnd] = useState("18:00");
  const [weekendEnabled, setWeekendEnabled] = useState(false);
  const [weekendStart, setWeekendStart] = useState("09:00");
  const [weekendEnd, setWeekendEnd] = useState("13:00");

  useEffect(() => {
    if (open) {
      setType("ignition_on");
      setChannelApp(true);
      setChannelPush(true);
      setDedupe(5);
      setMaxSpeed(80);
      setStopValue(15);
      setStopUnit("min");
      setNoSignalValue(10);
      setNoSignalUnit("min");
      setWeekdaysStart("08:00");
      setWeekdaysEnd("18:00");
      setWeekendEnabled(false);
      setWeekendStart("09:00");
      setWeekendEnd("13:00");
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] w-[560px] max-w-[95vw] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span aria-hidden>🔔</span> Criar alerta
          </DialogTitle>
          <DialogDescription>
            Escolha o tipo de alerta e como você quer ser avisado.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div>
            <div className="mb-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              Tipo de alerta
            </div>
            <div className="grid grid-cols-2 gap-2">
              {ALERT_TYPE_OPTIONS.map((o) => {
                const active = type === o.key;
                return (
                  <button
                    key={o.key}
                    type="button"
                    onClick={() => setType(o.key)}
                    className={cn(
                      "flex items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm transition-colors",
                      active
                        ? "border-foreground/60 bg-accent/50"
                        : "border-border bg-background/60 hover:bg-accent/40",
                    )}
                  >
                    <span aria-hidden className="text-base">
                      {o.emoji}
                    </span>
                    <span className="truncate">{o.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {type === "overspeed" && (
            <div className="rounded-lg border border-border bg-background/60 p-3">
              <div className="mb-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                💨 Velocidade máxima
              </div>
              <div className="flex items-center gap-2">
                <TxtInput
                  type="number"
                  min={1}
                  value={maxSpeed}
                  onChange={(e) => setMaxSpeed(Math.max(1, Number(e.target.value) || 0))}
                  className="w-28"
                />
                <span className="text-sm text-muted-foreground">km/h</span>
              </div>
              <p className="mt-1 text-[11px] text-muted-foreground">
                Dispara quando a velocidade ultrapassar este valor.
              </p>
            </div>
          )}

          {type === "long_stop" && (
            <div className="rounded-lg border border-border bg-background/60 p-3">
              <div className="mb-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                ⏱️ Tempo parado
              </div>
              <div className="flex items-center gap-2">
                <TxtInput
                  type="number"
                  min={1}
                  value={stopValue}
                  onChange={(e) => setStopValue(Math.max(1, Number(e.target.value) || 0))}
                  className="w-28"
                />
                <select
                  value={stopUnit}
                  onChange={(e) => setStopUnit(e.target.value as "min" | "h")}
                  className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                >
                  <option value="min">minutos</option>
                  <option value="h">horas</option>
                </select>
              </div>
              <p className="mt-1 text-[11px] text-muted-foreground">
                Dispara quando o veículo ficar parado por este período.
              </p>
            </div>
          )}

          {type === "no_signal" && (
            <div className="rounded-lg border border-border bg-background/60 p-3">
              <div className="mb-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                📡 Tempo sem sinal
              </div>
              <div className="flex items-center gap-2">
                <TxtInput
                  type="number"
                  min={1}
                  value={noSignalValue}
                  onChange={(e) => setNoSignalValue(Math.max(1, Number(e.target.value) || 0))}
                  className="w-28"
                />
                <select
                  value={noSignalUnit}
                  onChange={(e) => setNoSignalUnit(e.target.value as "min" | "h")}
                  className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                >
                  <option value="min">minutos</option>
                  <option value="h">horas</option>
                </select>
              </div>
              <p className="mt-1 text-[11px] text-muted-foreground">
                Dispara quando o dispositivo ficar sem comunicação por este período.
              </p>
            </div>
          )}

          {type === "off_hours" && (
            <div className="rounded-lg border border-border bg-background/60 p-3 space-y-3">
              <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                🕒 Horário de funcionamento
              </div>
              <div>
                <div className="mb-1 text-xs font-medium">Dias úteis (seg–sex)</div>
                <div className="flex items-center gap-2">
                  <TxtInput
                    type="time"
                    value={weekdaysStart}
                    onChange={(e) => setWeekdaysStart(e.target.value)}
                    className="w-32"
                  />
                  <span className="text-sm text-muted-foreground">até</span>
                  <TxtInput
                    type="time"
                    value={weekdaysEnd}
                    onChange={(e) => setWeekdaysEnd(e.target.value)}
                    className="w-32"
                  />
                </div>
              </div>
              <div>
                <div className="mb-1 flex items-center justify-between">
                  <span className="text-xs font-medium">Finais de semana</span>
                  <button
                    type="button"
                    onClick={() => setWeekendEnabled((v) => !v)}
                    className="text-[11px] font-medium text-muted-foreground hover:text-foreground"
                  >
                    {weekendEnabled ? "Desativar" : "Ativar"}
                  </button>
                </div>
                {weekendEnabled ? (
                  <div className="flex items-center gap-2">
                    <TxtInput
                      type="time"
                      value={weekendStart}
                      onChange={(e) => setWeekendStart(e.target.value)}
                      className="w-32"
                    />
                    <span className="text-sm text-muted-foreground">até</span>
                    <TxtInput
                      type="time"
                      value={weekendEnd}
                      onChange={(e) => setWeekendEnd(e.target.value)}
                      className="w-32"
                    />
                  </div>
                ) : (
                  <p className="text-[11px] text-muted-foreground">
                    Sem operação nos finais de semana.
                  </p>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground">
                Alerta dispara quando houver condução fora destes horários.
              </p>
            </div>
          )}



          <div>
            <div className="mb-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              Como avisar
            </div>
            <div className="space-y-2">
              <ChannelToggle
                emoji="📱"
                title="Notificação no app"
                description="Aparece no sino de notificações do GPS Drive."
                checked={channelApp}
                onChange={setChannelApp}
              />
              <ChannelToggle
                emoji="📲"
                title="Notificação push no celular"
                description="Chega mesmo com o site fechado (site instalado na tela de início)."
                checked={channelPush}
                onChange={(v) => {
                  setChannelPush(v);
                  if (!v) return;
                  ensurePushSubscription().catch((err) => {
                    setChannelPush(false);
                    toast.error("Não foi possível ativar o push", { description: err.message });
                  });
                }}
              />
            </div>
          </div>

          <div>
            <div className="mb-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              Ignorar repetidos por
            </div>
            <div className="flex items-center gap-2">
              <TxtInput
                type="number"
                min={0}
                value={dedupe}
                onChange={(e) => setDedupe(Math.max(0, Number(e.target.value) || 0))}
                className="w-28"
              />
              <span className="text-sm text-muted-foreground">minutos</span>
            </div>
            <p className="mt-1 text-[11px] text-muted-foreground">
              Evita disparar o mesmo alerta várias vezes seguidas.
            </p>
          </div>

          <div className="rounded-lg border border-border bg-muted/30 px-3 py-2 text-[11px] text-muted-foreground">
            <span className="font-medium text-foreground">Auditoria:</span> criação, alterações e
            disparos deste alerta serão registrados no log da conta.
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={() => {
              const config: AlertRuleConfig = {};
              if (type === "overspeed") config.maxSpeed = maxSpeed;
              if (type === "long_stop")
                config.stopMinutes = stopUnit === "h" ? stopValue * 60 : stopValue;
              if (type === "no_signal")
                config.noSignalMinutes = noSignalUnit === "h" ? noSignalValue * 60 : noSignalValue;
              if (type === "off_hours")
                config.offHours = {
                  weekdaysStart,
                  weekdaysEnd,
                  weekendEnabled,
                  weekendStart,
                  weekendEnd,
                };
              onSubmit({
                type,
                channelApp,
                channelPush,
                dedupeMinutes: dedupe,
                config: Object.keys(config).length ? config : undefined,
              });
            }}
          >
            Criar alerta
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ChannelToggle({
  emoji,
  title,
  description,
  checked,
  onChange,
}: {
  emoji: string;
  title: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className="flex w-full items-center gap-3 rounded-lg border border-border bg-background/60 px-3 py-2.5 text-left transition-colors hover:bg-accent/40"
    >
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted text-base">
        <span aria-hidden>{emoji}</span>
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium">{title}</div>
        <div className="text-[11px] text-muted-foreground">{description}</div>
      </div>
      <span
        className={cn(
          "relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors",
          checked ? "bg-foreground" : "bg-border",
        )}
      >
        <span
          className={cn(
            "inline-block h-4 w-4 transform rounded-full bg-background transition-transform",
            checked ? "translate-x-[18px]" : "translate-x-0.5",
          )}
        />
      </span>
    </button>
  );
}

